import { Organization } from "@/contexts/DealContext";
import Fuse, { IFuseOptions } from "fuse.js";

/**
 * Retourne l'organisation la plus proche du nom fourni.
 * @param name Le nom à matcher
 * @param organizations La liste des organisations disponibles
 * @returns L'organisation correspondant ou undefined si aucune correspondance satisfaisante
 */

export function normalizeRegistration(raw: string): string {
  if (!raw) return "";

  // Uppercase & trim
  let reg = raw.toUpperCase().trim();

  // Retirer mentions parasites fréquentes
  reg = reg.replace(/NUM[ÉE]RO|NUMBER|N°|ID|IDENTIFIANT|REGISTRATION|REGISTRE|EST|IS|:|=|\s+/gi, "");

  // Retirer mentions de contexte (RCS, TVA, VAT, MWST, CN, etc.)
  reg = reg.replace(/\b(RCS|R\.C\.S\.?|TVA|VAT|MWST|CN)\b/g, "");

  // Supprimer tous les séparateurs (points, tirets, slashs…)
  reg = reg.replace(/[^A-Z0-9]/g, "");

  // --------------
  // CHECK NUMÉRIQUE
  // --------------
  const numericOnly = reg.replace(/[^0-9]/g, "");

  // SIRET = 14 chiffres
  if (/^\d{14}$/.test(numericOnly)) {
    return numericOnly;
  }

  // SIREN = 9 chiffres
  if (/^\d{9}$/.test(numericOnly)) {
    return numericOnly;
  }

  // CHE suisse (on conserve la forme alphanumérique pour éviter collisions)
  if (/^CHE\d{9}$/.test(reg)) {
    return reg;
  }

  // TVA intracommunautaire
  if (/^[A-Z]{2}[0-9A-Z]{6,12}$/.test(reg)) {
    return reg;
  }

  // Cas alphanumériques exotiques (ex: 9N55YRQC69CN)
  if (/^[A-Z0-9]{6,}$/.test(reg)) {
    return reg;
  }

  // Fallback : cœur numérique si existant
  const numericMatches = raw.match(/\d+/g);
  if (numericMatches) {
    return numericMatches.reduce((a, b) => (b.length > a.length ? b : a), "");
  }

  return reg;
}

export const BANK_SYNONYMS: Record<string, string[]> = {
  // --- Crédit Agricole ---
  "Crédit Agricole": ["CA"],
  "Crédit Agricole CIB": ["CACIB", "CA CIB", "CIB", "Corporate and Investment Bank"],

  // --- BNP ---
  "BNP Paribas": ["BNP", "BNPP", "BNPPARIBAS"],

  // --- Société Générale ---
  "Société Générale": ["SG", "SocGen"],

  // --- HSBC ---
  "HSBC": ["Hongkong and Shanghai Banking Corporation"],

  // --- Citibank ---
  "Citibank": ["Citi"],

  // --- JP Morgan ---
  "JPMorgan": ["JP Morgan", "JPMC", "JPM"],

  // --- Bank of America ---
  "Bank of America": ["BofA", "BoA", "Merrill Lynch"],

  // --- Goldman Sachs (pas dans ta liste mais fréquent) ---
  "Goldman Sachs": ["GS"],

  // --- Morgan Stanley (idem) ---
  "Morgan Stanley": ["MS"],

  // --- Crédit Suisse ---
  "Crédit Suisse": ["CS"],

  // --- Deutsche Bank (ajout car fréquent) ---
  "Deutsche Bank": ["DB"],

  // --- UBS (ajout car fréquent) ---
  "UBS": ["Union Bank of Switzerland"],

  // --- ICBC ---
  "ICBC": ["Industrial and Commercial Bank of China"],

  // --- Standard Chartered ---
  "Standard Chartered": ["StanChart"],

  // --- ING ---
  "ING": ["ING Bank"],

  // --- DNB ---
  "DNB": ["Den Norske Bank"],

  // --- ABN AMRO ---
  "ABN AMRO": ["ABN"],

  // --- BRED Banque Populaire ---
  "BRED Banque Populaire": ["BRED"],

  // --- Banque Populaire (groupe) ---
  "Banque Populaire": ["BP"],

  // --- Caisse d’Epargne ---
  "Caisse d'Epargne": ["CE"],

  // --- CASDEN Banque Populaire ---
  "CASDEN Banque Populaire": ["CASDEN"],

  // --- BPI France ---
  "BPIfrance": ["BPI"],

  // --- Swedbank ---
  "Swedbank": ["SWED"],

  // --- Mizuho ---
  "Mizuho Bank": ["MH"],

  // --- Sumitomo Mitsui ---
  "Sumitomo Mitsui Trust Bank": ["SMTB"],
  "SMBC Bank": ["SMBC"],

  // --- Nordea ---
  "Nordea Bank": ["Nordea"],

  // --- DBs Bank ---
  "DBS Bank": ["DBS"],

  // --- CaixaBank ---
  "CaixaBank": ["Caixa"],

  // --- Bank of Tokyo Mitsubishi UFJ ---
  "Bank of Tokyo Mitsubishi UFJ": ["BTMU", "MUFG"],

  // --- Alpha Bank ---
  "Alpha Bank": ["Alpha"],

  // --- Hamburg Commercial Bank ---
  "Hamburg Commercial Bank": ["HCOB"],

  // --- Joh. Berenberg, Gossler & Co. KG ---
  "Berenberg": ["Joh. Berenberg", "Berenberg Bank"],
};

export const GENERIC_SYNONYMS: Record<string, string[]> = {
  // Structures bancaires
  "CIB": ["Corporate and Investment Bank", "Corporate & Investment Banking"],
  "Corporate and Investment Bank": ["CIB", "Corporate & Investment Banking"],
  "Corporate & Investment Banking": ["CIB", "Corporate and Investment Bank"],
  "IB": ["Investment Bank", "Investment Banking"],
  "Investment Bank": ["IB", "Investment Banking"],
  "Investment Banking": ["IB", "Investment Bank"],
  "SA": ["S.A.", "Sociedad Anónima", "Société Anonyme"],
  "S.A.": ["SA", "Sociedad Anónima", "Société Anonyme"],
  "Société Anonyme": ["SA", "S.A.", "Sociedad Anónima"],
  "Sociedad Anónima": ["SA", "S.A.", "Société Anonyme"],
  "N.V.": ["NV", "Naamloze Vennootschap"],
  "NV": ["N.V.", "Naamloze Vennootschap"],
  "Naamloze Vennootschap": ["N.V.", "NV"],
  "N.A.": ["NA", "National Association"],
  "NA": ["N.A.", "National Association"],
  "National Association": ["N.A.", "NA"],
  "PLC": ["Public Limited Company"],
  "P.L.C.": ["Public Limited Company"],
  "Public Limited Company": ["PLC", "P.L.C."],
  "LLC": ["Limited Liability Company"],
  "L.L.C.": ["Limited Liability Company"],
  "Limited Liability Company": ["LLC", "L.L.C."],
  "Ltd": ["Limited"],
  "Ltd.": ["Limited"],
  "Limited": ["Ltd", "Ltd."],
  "Inc": ["Incorporated"],
  "Inc.": ["Incorporated"],
  "Incorporated": ["Inc", "Inc."],
  "Co": ["Company", "Compagnie"],
  "Co.": ["Company", "Compagnie"],
  "Company": ["Co", "Co.", "Compagnie"],
  "Compagnie": ["Co", "Co.", "Company"],
  "KG": ["Kommanditgesellschaft"],
  "K.G.": ["Kommanditgesellschaft"],
  "Kommanditgesellschaft": ["KG", "K.G."],
  "KGaA": ["Kommanditgesellschaft auf Aktien"],

  // Groupes bancaires
  "AG": ["Aktiengesellschaft"], // Allemagne, Suisse
  "Aktiengesellschaft": ["AG"], // Allemagne, Suisse
  "AB": ["Aktiebolag"],         // Suède
  "Aktiebolag": ["AB"],         // Suède
  "ASA": ["Allmennaksjeselskap"], // Norvège
  "Allmennaksjeselskap": ["ASA"], // Norvège

  // Réglementaire
  "ICAV": ["Irish Collective Asset-management Vehicle"],
  "Irish Collective Asset-management Vehicle": ["ICAV"],
  "UCITS": ["Undertakings for Collective Investment in Transferable Securities"],
  "Undertakings for Collective Investment in Transferable Securities": ["UCITS"],
  "ETF": ["Exchange Traded Fund"],
  "Exchange Traded Fund": ["ETF"],

  // Abréviations géographiques fréquentes
  "UK": ["United Kingdom"],
  "GB": ["Great Britain", "United Kingdom"],
  "United Kingdom": ["UK", "GB"],
  "Great Britain": ["GB"],
  "US": ["United States", "USA"],
  "USA": ["US", "United States"],
  "United States": ["US", "USA"],
  "EU": ["European Union", "Europe"],
  "Europe": ["EU", "European Union"],
  "European Union": ["EU", "Europe"],
  "FR": ["France"],
  "DE": ["Germany", "Deutschland"],
  "CN": ["China"],
  "China": ["CN"],
  "JP": ["Japan"],
  "Japan": ["JP"],

  // Abréviations générales utilisées dans les noms
  "Intl": ["International"],
  "Int'l": ["International"],
  "International": ["Intl", "Int'l"],
  "Corp": ["Corporation", "Corporate"],
  "Corp.": ["Corporation", "Corporate"],
  "Corporation": ["Corp", "Corp."],
  "Corporate": ["Corp", "Corp."],
  "Comm.": ["Commercial"],
  "Comm": ["Commercial"],
  "Commercial": ["Comm", "Comm."],
  "Dev.": ["Development"],
};

function normalizeForSynonyms(input: string): string {
  if (!input) return "";
  // 1) enlever accents (NFD + strip combining)
  // 2) uppercase
  // 3) remplacer tout ce qui n'est pas A-Z0-9 par un espace
  // 4) trim et normaliser les espaces
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

export function expandWithSynonyms(
  name: string,
  BANK_SYNONYMS: Record<string, string[]> = {},
  GENERIC_SYNONYMS: Record<string, string[]> = {}
): string[] {
  const normalized = normalizeForSynonyms(name);
  const tokens = normalized.length ? normalized.split(" ") : [];
  const tokenSet = new Set(tokens);

  const expanded = new Set<string>();
  expanded.add(name);

  // --- 1) Synonymes spécifiques aux banques ---
  for (const [canonical, syns] of Object.entries(BANK_SYNONYMS)) {
    const canonicalNorm = normalizeForSynonyms(canonical);
    if (!canonicalNorm) continue;

    const matchesCanonical =
      normalized.includes(canonicalNorm) ||
      canonicalNorm.split(" ").every((t) => tokenSet.has(t));

    if (matchesCanonical) {
      syns.forEach((s) => {
        expanded.add(s); // synonyme seul

        // Replace dans le nom si ce n’est pas déjà la même chose
        if (s !== canonical) {
          const replaced = name.replace(new RegExp(`\\b${canonical}\\b`, "i"), s);
          expanded.add(replaced);
        }
      });
    }
  }

  // --- 2) Synonymes génériques ---
  for (const [abbr, values] of Object.entries(GENERIC_SYNONYMS)) {
    const abbrNorm = normalizeForSynonyms(abbr);
    const matchedToken = tokens.includes(abbrNorm);
    const matchedValue = values.some((v) => normalized.includes(normalizeForSynonyms(v)));

    if (matchedToken || matchedValue) {
      values.forEach((v) => {
        expanded.add(v); // uniquement synonyme seul
        // Remplacement dans le nom uniquement si le sigle existe
        if (tokens.includes(abbrNorm)) {
          const replaced = name.replace(new RegExp(`\\b${abbr}\\b`, "i"), v);
          expanded.add(replaced);
        }
      });
    }
  }

  return Array.from(expanded);
}



// Enrichit une organisation avec ses synonymes
function enrichOrganization(org: Organization): Organization & { synonyms: string[] } {
  const allNames = [org.name, org.shortName].filter(Boolean) as string[];
  const synonyms = allNames.flatMap((name) =>
    expandWithSynonyms(name, BANK_SYNONYMS, GENERIC_SYNONYMS)
  );
  return { ...org, synonyms };
}

export function matchOrganization(
  name: string,
  organizations: Organization[],
  registration?: string
): Organization[] {
  if (registration && normalizeRegistration(registration).length > 0) {
    const normReg = normalizeRegistration(registration);

    const matchedOrg = organizations.filter((org) => {
      if (!org.registration || normalizeRegistration(org.registration).length === 0) return false;
      const orgNorm = normalizeRegistration(org.registration);

      // match exact
      if (orgNorm === normReg) return true;

      // match partiel
      if (orgNorm.includes(normReg) || normReg.includes(orgNorm)) return true;

      return false;
    });

    if (matchedOrg.length > 0) {
      return matchedOrg;
    }
  }
  const enrichedOrgs = organizations.map(enrichOrganization);

  const options: IFuseOptions<Organization & { synonyms: string[] }> = {
    includeScore: true,
    threshold: 0.3,
    keys: [
      { name: "name", weight: 0.6 },
      { name: "shortName", weight: 0.3 },
      { name: "registration", weight: 0.1 },
      { name: "synonyms", weight: 0.2 }, // synonymes pris en compte
    ],
  };

  const fuse = new Fuse(enrichedOrgs, options);

  // Étend la recherche avec les synonymes
  const queryVariants = expandWithSynonyms(name);
  console.log("queryVariants", queryVariants);
  // On concatène tous les résultats (et on déduplique)
  const results = queryVariants.flatMap((variant) => fuse.search(variant));

  // Dédupliquer par ID (ou registration)
  const seen = new Set<string>();
  const unique = results
    .sort((a, b) => a.score! - b.score!) // tri par score Fuse
    .filter((r) => {
      const key = r.item.registration || r.item.name;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  return unique.map((r) => r.item);
}

export const preprodOrganizationsMock: Organization[] = [
  {
    id: "ckyd26xy5329274vztplgzq1zx",
    createdAt: "2022-01-13T14:20:24.797Z",
    updatedAt: "2022-01-13T14:20:24.798Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "ABN AMRO Bank N.V. Oslo branch",
    shortName: "ABN AMRO NO",
    registration: "995550164",
    private: false,
    addressId: "ckyd26xy5329284vztfavnwiii",
    address: {
      id: "ckyd26xy5329284vztfavnwiii",
      createdAt: "2022-01-13T14:20:24.797Z",
      updatedAt: "2022-01-13T14:20:24.797Z",
      deletedAt: null,
      line1: "Olav V Gate 5",
      line2: null,
      line3: null,
      cityId: "ckyd26xy5329294vzt8n0kjc1i",
      city: {
        id: "ckyd26xy5329294vzt8n0kjc1i",
        createdAt: "2022-01-13T14:20:24.797Z",
        updatedAt: "2022-01-13T14:20:24.797Z",
        name: "OSLO",
        countryId: "ckvi6x9qg08174qyes76887zu",
        postalCode: "N-0161",
        country: {
          name: "Norway",
        },
      },
    },
  },
  {
    id: "ckyd26xy3329154vztfoso61im",
    createdAt: "2022-01-13T14:20:24.795Z",
    updatedAt: "2022-01-13T14:20:24.796Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "ABN AMRO Bank N.V. ",
    shortName: "ABN AMRO NV",
    registration: "34334259",
    private: false,
    addressId: "ckyd26xy3329164vztr0pm6c9u",
    address: {
      id: "ckyd26xy3329164vztr0pm6c9u",
      createdAt: "2022-01-13T14:20:24.795Z",
      updatedAt: "2022-01-13T14:20:24.795Z",
      deletedAt: null,
      line1: "Gustav Mahlerlaan 10",
      line2: null,
      line3: null,
      cityId: "ckyd26xy3329174vztpkh8kpv4",
      city: {
        id: "ckyd26xy3329174vztpkh8kpv4",
        createdAt: "2022-01-13T14:20:24.795Z",
        updatedAt: "2022-01-13T14:20:24.795Z",
        name: "AMSTERDAM",
        countryId: "ckvi6x9pp08034qye0xciozlj",
        postalCode: "1082 PP",
        country: {
          name: "Netherlands",
        },
      },
    },
  },
  {
    id: "ckyd26xyv330664vztc5x69b3f",
    createdAt: "2022-01-13T14:20:24.823Z",
    updatedAt: "2022-01-13T14:20:24.824Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Alpha Bank",
    shortName: "Alpha Bank",
    registration: "9N55YRQC69CN",
    private: false,
    addressId: "ckyd26xyv330674vzt7pbi9olf",
    address: {
      id: "ckyd26xyv330674vzt7pbi9olf",
      createdAt: "2022-01-13T14:20:24.823Z",
      updatedAt: "2022-01-13T14:20:24.823Z",
      deletedAt: null,
      line1: "40 Stadiou St",
      line2: null,
      line3: null,
      cityId: "ckyd26xyv330684vztig3o5qfg",
      city: {
        id: "ckyd26xyv330684vztig3o5qfg",
        createdAt: "2022-01-13T14:20:24.823Z",
        updatedAt: "2022-01-13T14:20:24.823Z",
        name: "ATHENS",
        countryId: "ckvi6x5zs01824qyerfpm7o7u",
        postalCode: "102 52",
        country: {
          name: "Greece",
        },
      },
    },
  },
  {
    id: "clf6p5f8m22064591wsacrqlbf",
    createdAt: "2023-03-13T10:43:41.542Z",
    updatedAt: "2023-03-13T10:43:41.543Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "ALTFINPARTNERS",
    shortName: "ALTFINPARTNERS",
    registration: "//",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "ckyd26xyx330754vztrcv2lzhx",
    createdAt: "2022-01-13T14:20:24.825Z",
    updatedAt: "2022-01-13T14:20:24.826Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Australia & New Zealand Bank",
    shortName: "ANZ",
    registration: "35976",
    private: false,
    addressId: "ckyd26xyx330764vztqjb0z80m",
    address: {
      id: "ckyd26xyx330764vztqjb0z80m",
      createdAt: "2022-01-13T14:20:24.825Z",
      updatedAt: "2022-01-13T14:20:24.825Z",
      deletedAt: null,
      line1: "ANZ Centre Melbourne",
      line2: " Level 9 833 collins Street",
      line3: " Docklands",
      cityId: "ckyd26xyx330774vztol7lyd4j",
      city: {
        id: "ckyd26xyx330774vztol7lyd4j",
        createdAt: "2022-01-13T14:20:24.825Z",
        updatedAt: "2022-01-13T14:20:24.825Z",
        name: "VICTORIA",
        countryId: "ckvi6x5z800304qyee8i7b5rm",
        postalCode: "3008",
        country: {
          name: "Australia",
        },
      },
    },
  },
  {
    id: "bank1",
    createdAt: "2023-05-09T08:52:17.109Z",
    updatedAt: "2025-06-30T14:33:31.174Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 1",
    shortName: "Bank-1",
    registration: "Reg-Bank-1",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank2",
    createdAt: "2023-05-09T08:52:17.148Z",
    updatedAt: "2025-06-30T14:33:31.234Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 2",
    shortName: "Bank-2",
    registration: "Reg-Bank-2",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank3",
    createdAt: "2023-05-09T08:52:17.219Z",
    updatedAt: "2025-06-30T14:33:31.294Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 3",
    shortName: "Bank-3",
    registration: "Reg-Bank-3",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank4",
    createdAt: "2023-05-09T08:52:17.290Z",
    updatedAt: "2025-06-30T14:33:31.358Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 4",
    shortName: "Bank-4",
    registration: "Reg-Bank-4",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank5",
    createdAt: "2023-05-09T08:52:17.320Z",
    updatedAt: "2025-06-30T14:33:31.422Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 5",
    shortName: "Bank-5",
    registration: "Reg-Bank-5",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank6",
    createdAt: "2023-05-09T08:52:17.358Z",
    updatedAt: "2025-06-30T14:33:31.478Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 6",
    shortName: "Bank-6",
    registration: "Reg-Bank-6",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank7",
    createdAt: "2023-05-09T08:52:17.429Z",
    updatedAt: "2025-06-30T14:33:31.538Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 7",
    shortName: "Bank-7",
    registration: "Reg-Bank-7",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank8",
    createdAt: "2023-05-09T08:52:17.462Z",
    updatedAt: "2025-06-30T14:33:31.602Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 8",
    shortName: "Bank-8",
    registration: "Reg-Bank-8",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank9",
    createdAt: "2023-05-09T08:52:17.489Z",
    updatedAt: "2025-06-30T14:33:31.666Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 9",
    shortName: "Bank-9",
    registration: "Reg-Bank-9",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "ckyd26xyb329644vztl08yt7s4",
    createdAt: "2022-01-13T14:20:24.803Z",
    updatedAt: "2022-01-13T14:20:24.804Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank Itau BBA SA",
    shortName: "Bank Itau",
    registration: "60.872.504/0001-23",
    private: false,
    addressId: "ckyd26xyb329654vztlv3o2ex7",
    address: {
      id: "ckyd26xyb329654vztlv3o2ex7",
      createdAt: "2022-01-13T14:20:24.803Z",
      updatedAt: "2022-01-13T14:20:24.804Z",
      deletedAt: null,
      line1: "Av. Brigadeiro Faria Lima 3.500 -1 - 2 - 3 (Parte)",
      line2: null,
      line3: null,
      cityId: "ckyd26xyc329664vztke8d41le",
      city: {
        id: "ckyd26xyc329664vztke8d41le",
        createdAt: "2022-01-13T14:20:24.804Z",
        updatedAt: "2022-01-13T14:20:24.804Z",
        name: "SAO PAULO",
        countryId: "ckvi6x5zf00674qye45w83t7d",
        postalCode: "4 E 5 Andares",
        country: {
          name: "Brazil",
        },
      },
    },
  },
  {
    id: "ckyd26xyf329864vztelv7fy72",
    createdAt: "2022-01-13T14:20:24.807Z",
    updatedAt: "2022-01-13T14:20:24.808Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank of China",
    shortName: "Bank of China",
    registration: "FC002851",
    private: false,
    addressId: "ckyd26xyf329874vztx41upnfx",
    address: {
      id: "ckyd26xyf329874vztx41upnfx",
      createdAt: "2022-01-13T14:20:24.807Z",
      updatedAt: "2022-01-13T14:20:24.807Z",
      deletedAt: null,
      line1: "1 Lothbury",
      line2: null,
      line3: null,
      cityId: "ckyd26xyf329884vztepcz8ly3",
      city: {
        id: "ckyd26xyf329884vztepcz8ly3",
        createdAt: "2022-01-13T14:20:24.807Z",
        updatedAt: "2022-01-13T14:20:24.807Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC2R 7DB",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "cm5n19zw4471268y5ucffsy59",
    createdAt: "2025-01-07T22:19:49.876Z",
    updatedAt: "2025-01-07T22:19:49.877Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque de Savoie",
    shortName: "Banque de Savoie",
    registration: "745 520 411",
    private: false,
    addressId: "cm5n19zw4471368y5sqkfixk2",
    address: {
      id: "cm5n19zw4471368y5sqkfixk2",
      createdAt: "2025-01-07T22:19:49.876Z",
      updatedAt: "2025-01-07T22:19:49.876Z",
      deletedAt: null,
      line1: "6 Boulevard du Théâtre",
      line2: null,
      line3: null,
      cityId: "cm5n19zw4471468y5zwjd4tky",
      city: {
        id: "cm5n19zw4471468y5zwjd4tky",
        createdAt: "2025-01-07T22:19:49.876Z",
        updatedAt: "2025-01-07T22:19:49.876Z",
        name: "Chambéry",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "73000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xyc329694vzt5hiedypq",
    createdAt: "2022-01-13T14:20:24.804Z",
    updatedAt: "2022-01-13T14:20:24.805Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Joh. Berenberg, Gossler & Co. KG",
    shortName: "Berenberg",
    registration: "42659",
    private: false,
    addressId: "ckyd26xyc329704vztn5ekhgia",
    address: {
      id: "ckyd26xyc329704vztn5ekhgia",
      createdAt: "2022-01-13T14:20:24.804Z",
      updatedAt: "2022-01-13T14:20:24.804Z",
      deletedAt: null,
      line1: "Neuer Jungfernstieg",
      line2: " Neustadt 20",
      line3: null,
      cityId: "ckyd26xyc329714vztb01gmjp5",
      city: {
        id: "ckyd26xyc329714vztb01gmjp5",
        createdAt: "2022-01-13T14:20:24.804Z",
        updatedAt: "2022-01-13T14:20:24.804Z",
        name: "HAMBURG",
        countryId: "ckvi6x5zl01224qyebcy10zfp",
        postalCode: "20354",
        country: {
          name: "Germany",
        },
      },
    },
  },
  {
    id: "ckyd26xxg327764vztfzcxiv89",
    createdAt: "2022-01-13T14:20:24.772Z",
    updatedAt: "2022-01-13T14:20:24.773Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "BNP PARIBAS SA",
    shortName: "BNPP FR",
    registration: "662042449",
    private: false,
    addressId: "ckyd26xxg327774vzt7kela3i0",
    address: {
      id: "ckyd26xxg327774vzt7kela3i0",
      createdAt: "2022-01-13T14:20:24.772Z",
      updatedAt: "2022-01-13T14:20:24.772Z",
      deletedAt: null,
      line1: "16 Boulevard des Italiens",
      line2: null,
      line3: null,
      cityId: "ckyd26xxg327784vzt4djz17c0",
      city: {
        id: "ckyd26xxg327784vzt4djz17c0",
        createdAt: "2022-01-13T14:20:24.772Z",
        updatedAt: "2022-01-13T14:20:24.772Z",
        name: "PARIS",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75009",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xxf327674vztzm4r54rb",
    createdAt: "2022-01-13T14:20:24.771Z",
    updatedAt: "2022-01-13T14:20:24.772Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "BNP PARIBAS (Suisse)",
    shortName: "BNPP SWITZ.",
    registration: "CHE-102.922.193",
    private: false,
    addressId: "ckyd26xxf327684vztkdn26qbm",
    address: {
      id: "ckyd26xxf327684vztkdn26qbm",
      createdAt: "2022-01-13T14:20:24.771Z",
      updatedAt: "2022-01-13T14:20:24.771Z",
      deletedAt: null,
      line1: "2 place de Hollande",
      line2: null,
      line3: null,
      cityId: "ckyd26xxf327694vztbyedug0o",
      city: {
        id: "ckyd26xxf327694vztbyedug0o",
        createdAt: "2022-01-13T14:20:24.771Z",
        updatedAt: "2022-01-13T14:20:24.771Z",
        name: "GENEVE",
        countryId: "ckvi6x5zh00864qyecukyagie",
        postalCode: "1204",
        country: {
          name: "Switzerland",
        },
      },
    },
  },
  {
    id: "ckyd26xyn330304vztqtsq7oyc",
    createdAt: "2022-01-13T14:20:24.815Z",
    updatedAt: "2022-01-13T14:20:24.816Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "BANK OF AMERICA EUROPE DESIGNATED ACTIVITY COMPANY",
    shortName: "BoA ML",
    registration: "FC026157",
    private: false,
    addressId: "ckyd26xyn330314vztd9mbz6dv",
    address: {
      id: "ckyd26xyn330314vztd9mbz6dv",
      createdAt: "2022-01-13T14:20:24.815Z",
      updatedAt: "2022-01-13T14:20:24.815Z",
      deletedAt: null,
      line1: "2 King Edward Street",
      line2: null,
      line3: null,
      cityId: "ckyd26xyn330324vztupa6sqsh",
      city: {
        id: "ckyd26xyn330324vztupa6sqsh",
        createdAt: "2022-01-13T14:20:24.815Z",
        updatedAt: "2022-01-13T14:20:24.815Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC1A 1HQ",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "cm5n19zw7472768y5kqn4rvr5",
    createdAt: "2025-01-07T22:19:49.879Z",
    updatedAt: "2025-01-07T22:19:49.880Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Aquitaine Centre Atlantique",
    shortName: "BPACA",
    registration: "347 607 939",
    private: false,
    addressId: "cm5n19zw7472868y5pzyngrcf",
    address: {
      id: "cm5n19zw7472868y5pzyngrcf",
      createdAt: "2025-01-07T22:19:49.879Z",
      updatedAt: "2025-01-07T22:19:49.879Z",
      deletedAt: null,
      line1: "10 Quai des Queyries",
      line2: null,
      line3: null,
      cityId: "cm5n19zw7472968y5uo9onvoj",
      city: {
        id: "cm5n19zw7472968y5uo9onvoj",
        createdAt: "2025-01-07T22:19:49.879Z",
        updatedAt: "2025-01-07T22:19:49.879Z",
        name: "Bordeaux",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "33072",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zw6472168y51swdj1kk",
    createdAt: "2025-01-07T22:19:49.878Z",
    updatedAt: "2025-01-07T22:19:49.879Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Alsace Lorraine Champagne",
    shortName: "BPALC",
    registration: "356 801 571",
    private: false,
    addressId: "cm5n19zw6472268y5np0u4ume",
    address: {
      id: "cm5n19zw6472268y5np0u4ume",
      createdAt: "2025-01-07T22:19:49.878Z",
      updatedAt: "2025-01-07T22:19:49.878Z",
      deletedAt: null,
      line1: "3 Rue François de Curel",
      line2: null,
      line3: null,
      cityId: "cm5n19zw6472368y53irnnz16",
      city: {
        id: "cm5n19zw6472368y53irnnz16",
        createdAt: "2025-01-07T22:19:49.878Z",
        updatedAt: "2025-01-07T22:19:49.878Z",
        name: "Metz",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "57000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zw8473268y5wr0macgd",
    createdAt: "2025-01-07T22:19:49.880Z",
    updatedAt: "2025-01-07T22:19:49.881Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Auvergne Rhône Alpes",
    shortName: "BPAURA",
    registration: "356 000 109",
    private: false,
    addressId: "cm5n19zw8473368y5g7iw07hd",
    address: {
      id: "cm5n19zw8473368y5g7iw07hd",
      createdAt: "2025-01-07T22:19:49.880Z",
      updatedAt: "2025-01-07T22:19:49.880Z",
      deletedAt: null,
      line1: "4 Boulevard Eugène Deruelle",
      line2: null,
      line3: null,
      cityId: "cm5n19zw8473468y5i28bl18o",
      city: {
        id: "cm5n19zw8473468y5i28bl18o",
        createdAt: "2025-01-07T22:19:49.880Z",
        updatedAt: "2025-01-07T22:19:49.880Z",
        name: "Lyon",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "69003",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zw8473768y5khqr10x8",
    createdAt: "2025-01-07T22:19:49.880Z",
    updatedAt: "2025-01-07T22:19:49.881Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Bourgogne Franche-Comté",
    shortName: "BPBFC",
    registration: "542 820 352",
    private: false,
    addressId: "cm5n19zw8473868y5lhn5ni4w",
    address: {
      id: "cm5n19zw8473868y5lhn5ni4w",
      createdAt: "2025-01-07T22:19:49.880Z",
      updatedAt: "2025-01-07T22:19:49.880Z",
      deletedAt: null,
      line1: "14 Boulevard de la Trémouille",
      line2: null,
      line3: null,
      cityId: "cm5n19zw9473968y5e19grxc3",
      city: {
        id: "cm5n19zw9473968y5e19grxc3",
        createdAt: "2025-01-07T22:19:49.881Z",
        updatedAt: "2025-01-07T22:19:49.881Z",
        name: "Dijon",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "21000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwb475368y5kgvwhh6u",
    createdAt: "2025-01-07T22:19:49.883Z",
    updatedAt: "2025-01-07T22:19:49.884Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Grand Ouest",
    shortName: "BPGO",
    registration: "857 500 227",
    private: false,
    addressId: "cm5n19zwb475468y5wgp60x7y",
    address: {
      id: "cm5n19zwb475468y5wgp60x7y",
      createdAt: "2025-01-07T22:19:49.883Z",
      updatedAt: "2025-01-07T22:19:49.883Z",
      deletedAt: null,
      line1: "15 Boulevard de la Boutière",
      line2: null,
      line3: null,
      cityId: "cm5n19zwb475568y5twkj9g1r",
      city: {
        id: "cm5n19zwb475568y5twkj9g1r",
        createdAt: "2025-01-07T22:19:49.883Z",
        updatedAt: "2025-01-07T22:19:49.883Z",
        name: "Saint-Grégoire",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "35768",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xxg327724vztbk1tpgbi",
    createdAt: "2022-01-13T14:20:24.772Z",
    updatedAt: "2022-01-13T14:20:24.772Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "BPIfrance Assurance Export",
    shortName: "BPI",
    registration: "815276308",
    private: false,
    addressId: "ckyd26xxg327734vztzmpysbrq",
    address: {
      id: "ckyd26xxg327734vztzmpysbrq",
      createdAt: "2022-01-13T14:20:24.772Z",
      updatedAt: "2022-01-13T14:20:24.772Z",
      deletedAt: null,
      line1: "27-31 avenue du Général Leclerc",
      line2: null,
      line3: null,
      cityId: "ckyd26xxg327744vztv5wfla9y",
      city: {
        id: "ckyd26xxg327744vztv5wfla9y",
        createdAt: "2022-01-13T14:20:24.772Z",
        updatedAt: "2022-01-13T14:34:28.846Z",
        name: "MAISONS-ALFORT CEDEX",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "94700",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwc475868y5c266kf19",
    createdAt: "2025-01-07T22:19:49.884Z",
    updatedAt: "2025-01-07T22:19:49.885Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Méditerranée",
    shortName: "BPMED",
    registration: "058 801 481",
    private: false,
    addressId: "cm5n19zwc475968y5blzyt6le",
    address: {
      id: "cm5n19zwc475968y5blzyt6le",
      createdAt: "2025-01-07T22:19:49.884Z",
      updatedAt: "2025-01-07T22:19:49.884Z",
      deletedAt: null,
      line1: "457 Promenade des Anglais",
      line2: null,
      line3: null,
      cityId: "cm5n19zwc476068y5b3rnv9ec",
      city: {
        id: "cm5n19zwc476068y5b3rnv9ec",
        createdAt: "2025-01-07T22:19:49.884Z",
        updatedAt: "2025-01-07T22:19:49.884Z",
        name: "Nice",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "06200",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zw9474368y5cwcnx4bv",
    createdAt: "2025-01-07T22:19:49.881Z",
    updatedAt: "2025-01-07T22:19:49.883Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire du Nord",
    shortName: "BPN",
    registration: "457 506 566",
    private: false,
    addressId: "cm5n19zw9474468y5ck1bsos8",
    address: {
      id: "cm5n19zw9474468y5ck1bsos8",
      createdAt: "2025-01-07T22:19:49.881Z",
      updatedAt: "2025-01-07T22:19:49.881Z",
      deletedAt: null,
      line1: "847 Avenue de la République",
      line2: null,
      line3: null,
      cityId: "cm5n19zwa474568y51r7z4okh",
      city: {
        id: "cm5n19zwa474568y51r7z4okh",
        createdAt: "2025-01-07T22:19:49.882Z",
        updatedAt: "2025-01-07T22:19:49.882Z",
        name: "Marcq-en-Barœul",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "59700",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwd476468y5f6vjkzou",
    createdAt: "2025-01-07T22:19:49.885Z",
    updatedAt: "2025-01-07T22:19:49.886Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Occitane",
    shortName: "BPOC",
    registration: "560 801 300",
    private: false,
    addressId: "cm5n19zwd476568y5on5s85pg",
    address: {
      id: "cm5n19zwd476568y5on5s85pg",
      createdAt: "2025-01-07T22:19:49.885Z",
      updatedAt: "2025-01-07T22:19:49.885Z",
      deletedAt: null,
      line1: "33 Avenue Georges Pompidou",
      line2: null,
      line3: null,
      cityId: "cm5n19zwd476668y5htf8oe0f",
      city: {
        id: "cm5n19zwd476668y5htf8oe0f",
        createdAt: "2025-01-07T22:19:49.885Z",
        updatedAt: "2025-01-07T22:19:49.885Z",
        name: "Balma",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "31130",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwe476868y5rbrrnpzp",
    createdAt: "2025-01-07T22:19:49.886Z",
    updatedAt: "2025-01-07T22:19:49.887Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Rives de Paris",
    shortName: "BPRI",
    registration: "552 002 313",
    private: false,
    addressId: "cm5n19zwe476968y570638500",
    address: {
      id: "cm5n19zwe476968y570638500",
      createdAt: "2025-01-07T22:19:49.886Z",
      updatedAt: "2025-01-07T22:19:49.886Z",
      deletedAt: null,
      line1: "80 Boulevard Auguste Blanqui",
      line2: null,
      line3: null,
      cityId: "cm5n19zwe477068y5wioesi1u",
      city: {
        id: "cm5n19zwe477068y5wioesi1u",
        createdAt: "2025-01-07T22:19:49.886Z",
        updatedAt: "2025-01-07T22:19:49.886Z",
        name: "Paris",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75013",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwa474868y5elie6kfk",
    createdAt: "2025-01-07T22:19:49.882Z",
    updatedAt: "2025-01-07T22:19:49.883Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire du Sud",
    shortName: "BPS",
    registration: "554 200 808",
    private: false,
    addressId: "cm5n19zwa474968y55i5b2owz",
    address: {
      id: "cm5n19zwa474968y55i5b2owz",
      createdAt: "2025-01-07T22:19:49.882Z",
      updatedAt: "2025-01-07T22:19:49.882Z",
      deletedAt: null,
      line1: "38 Boulevard Georges Clémenceau",
      line2: null,
      line3: null,
      cityId: "cm5n19zwa475068y52o7sxr83",
      city: {
        id: "cm5n19zwa475068y52o7sxr83",
        createdAt: "2025-01-07T22:19:49.882Z",
        updatedAt: "2025-01-07T22:19:49.882Z",
        name: "Perpignan",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "66000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwe477368y5pv3gahmr",
    createdAt: "2025-01-07T22:19:49.886Z",
    updatedAt: "2025-01-07T22:19:49.887Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Val de France",
    shortName: "BPVF",
    registration: "549 800 373",
    private: false,
    addressId: "cm5n19zwe477468y5kwpbmrgr",
    address: {
      id: "cm5n19zwe477468y5kwpbmrgr",
      createdAt: "2025-01-07T22:19:49.886Z",
      updatedAt: "2025-01-07T22:19:49.886Z",
      deletedAt: null,
      line1: "9 Avenue Newton",
      line2: null,
      line3: null,
      cityId: "cm5n19zwe477568y51rjx3n9r",
      city: {
        id: "cm5n19zwe477568y51rjx3n9r",
        createdAt: "2025-01-07T22:19:49.886Z",
        updatedAt: "2025-01-07T22:19:49.886Z",
        name: "Montigny-le-Bretonneux",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "78180",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwf477868y5gqc5adjg",
    createdAt: "2025-01-07T22:19:49.887Z",
    updatedAt: "2025-01-07T22:19:49.888Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "BRED Banque Populaire",
    shortName: "BRED",
    registration: "552 091 795",
    private: false,
    addressId: "cm5n19zwf477968y54rtsi4r8",
    address: {
      id: "cm5n19zwf477968y54rtsi4r8",
      createdAt: "2025-01-07T22:19:49.887Z",
      updatedAt: "2025-01-07T22:19:49.887Z",
      deletedAt: null,
      line1: "18 Quai de la Rapée",
      line2: null,
      line3: null,
      cityId: "cm5n19zwf478068y5f3lm080q",
      city: {
        id: "cm5n19zwf478068y5f3lm080q",
        createdAt: "2025-01-07T22:19:49.887Z",
        updatedAt: "2025-01-07T22:19:49.887Z",
        name: "Paris",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75012",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xya329544vzte9oqj38q",
    createdAt: "2022-01-13T14:20:24.802Z",
    updatedAt: "2022-01-13T14:20:24.803Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "The Bank of Tokyo Mitsubishi UFJ, Ltd",
    shortName: "BTMU NYC",
    registration: "13-5611741",
    private: false,
    addressId: "ckyd26xya329554vztxkwtap4m",
    address: {
      id: "ckyd26xya329554vztxkwtap4m",
      createdAt: "2022-01-13T14:20:24.802Z",
      updatedAt: "2022-01-13T14:20:24.802Z",
      deletedAt: null,
      line1: "1251 Avenue of the Americas",
      line2: null,
      line3: null,
      cityId: "ckyd26xya329564vzt6xwxe252",
      city: {
        id: "ckyd26xya329564vzt6xwxe252",
        createdAt: "2022-01-13T14:20:24.802Z",
        updatedAt: "2022-01-13T14:20:24.802Z",
        name: "NEW YORK",
        countryId: "ckvi6xam911794qye0pfhrme8",
        postalCode: "NY 10020-1104",
        country: {
          name: "United States",
        },
      },
    },
  },
  {
    id: "ckyd26xya329594vzttofb8aye",
    createdAt: "2022-01-13T14:20:24.802Z",
    updatedAt: "2022-01-13T14:20:24.804Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "The Bank of Tokyo Mitsubishi UFJ, Ltd",
    shortName: "BTMU UK",
    registration: "FC007499",
    private: false,
    addressId: "ckyd26xyb329604vzt6mskqptg",
    address: {
      id: "ckyd26xyb329604vzt6mskqptg",
      createdAt: "2022-01-13T14:20:24.803Z",
      updatedAt: "2022-01-13T14:20:24.803Z",
      deletedAt: null,
      line1: "24 Lombard Street",
      line2: null,
      line3: null,
      cityId: "ckyd26xyb329614vztp2i4bhuw",
      city: {
        id: "ckyd26xyb329614vztp2i4bhuw",
        createdAt: "2022-01-13T14:20:24.803Z",
        updatedAt: "2022-01-13T14:20:24.803Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC3V 9AJ",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xxn328214vztopxxkse4",
    createdAt: "2022-01-13T14:20:24.779Z",
    updatedAt: "2022-01-13T14:20:24.780Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Crédit Agricole CIB",
    shortName: "CACIB",
    registration: "304187701",
    private: false,
    addressId: "ckyd26xxn328224vztzxzmudww",
    address: {
      id: "ckyd26xxn328224vztzxzmudww",
      createdAt: "2022-01-13T14:20:24.779Z",
      updatedAt: "2022-01-13T14:20:24.779Z",
      deletedAt: null,
      line1: "12 place des Etats Unis -CS 70052",
      line2: null,
      line3: null,
      cityId: "ckyd26xxn328234vztmubixiwa",
      city: {
        id: "ckyd26xxn328234vztmubixiwa",
        createdAt: "2022-01-13T14:20:24.779Z",
        updatedAt: "2022-01-13T14:20:24.779Z",
        name: "MONTROUGE CEDEX",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "92547",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xxq328314vztw84dy1m4",
    createdAt: "2022-01-13T14:20:24.781Z",
    updatedAt: "2022-01-13T14:20:24.783Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "CaixaBank SA",
    shortName: "CaixaBank",
    registration: "A08663619",
    private: false,
    addressId: "ckyd26xxq328324vzt9eg4pcxk",
    address: {
      id: "ckyd26xxq328324vzt9eg4pcxk",
      createdAt: "2022-01-13T14:20:24.782Z",
      updatedAt: "2022-01-13T14:20:24.782Z",
      deletedAt: null,
      line1: "2-4 calle Pintor Sorolla",
      line2: null,
      line3: null,
      cityId: "ckyd26xxq328334vzt5lgi209j",
      city: {
        id: "ckyd26xxq328334vzt5lgi209j",
        createdAt: "2022-01-13T14:20:24.782Z",
        updatedAt: "2022-01-13T14:20:24.782Z",
        name: "VALENCE",
        countryId: "ckvi6x5zn01424qyetum74vm9",
        postalCode: "46002",
        country: {
          name: "Spain",
        },
      },
    },
  },
  {
    id: "cm5n19zws485268y5sx5h64fr",
    createdAt: "2025-01-07T22:19:49.900Z",
    updatedAt: "2025-01-07T22:19:49.901Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CASDEN Banque Populaire",
    shortName: "CASDEN",
    registration: "784 275 778",
    private: false,
    addressId: "cm5n19zws485368y5y5bguwpi",
    address: {
      id: "cm5n19zws485368y5y5bguwpi",
      createdAt: "2025-01-07T22:19:49.900Z",
      updatedAt: "2025-01-07T22:19:49.900Z",
      deletedAt: null,
      line1: "1bis rue Jean WIENER",
      line2: null,
      line3: null,
      cityId: "cm5n19zws485468y5fcbdfl40",
      city: {
        id: "cm5n19zws485468y5fcbdfl40",
        createdAt: "2025-01-07T22:19:49.900Z",
        updatedAt: "2025-01-07T22:19:49.900Z",
        name: "Champs-sur-Marne",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "77420",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xyt330564vzteqymj5ti",
    createdAt: "2022-01-13T14:20:24.821Z",
    updatedAt: "2022-01-13T14:20:24.822Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Commonwealth Bank of Australia, Ltd",
    shortName: "CBA UK",
    registration: "FC016009",
    private: false,
    addressId: "ckyd26xyt330574vztms0z80xp",
    address: {
      id: "ckyd26xyt330574vztms0z80xp",
      createdAt: "2022-01-13T14:20:24.821Z",
      updatedAt: "2022-01-13T14:20:24.821Z",
      deletedAt: null,
      line1: "1 New Ludgate 60 Ludgate Hill",
      line2: null,
      line3: null,
      cityId: "ckyd26xyt330584vztpm69ijjr",
      city: {
        id: "ckyd26xyt330584vztpm69ijjr",
        createdAt: "2022-01-13T14:20:24.821Z",
        updatedAt: "2022-01-13T14:20:24.821Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC4M 7AW",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "cm5n19zwt486368y5mf7oyecp",
    createdAt: "2025-01-07T22:19:49.901Z",
    updatedAt: "2025-01-07T22:19:49.902Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Crédit Coopératif",
    shortName: "CCOOP",
    registration: "349 974 931",
    private: false,
    addressId: "cm5n19zwt486468y5yf31cptp",
    address: {
      id: "cm5n19zwt486468y5yf31cptp",
      createdAt: "2025-01-07T22:19:49.901Z",
      updatedAt: "2025-01-07T22:19:49.901Z",
      deletedAt: null,
      line1: "12 Boulevard de Pesaro",
      line2: " CS 10002",
      line3: null,
      cityId: "cm5n19zwt486568y59pbc9vhy",
      city: {
        id: "cm5n19zwt486568y59pbc9vhy",
        createdAt: "2025-01-07T22:19:49.901Z",
        updatedAt: "2025-01-07T22:19:49.901Z",
        name: "Nanterre",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "92000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xz2331004vztys77v04k",
    createdAt: "2022-01-13T14:20:24.830Z",
    updatedAt: "2022-01-13T14:20:24.832Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "China Development Bank",
    shortName: "CDB",
    registration: "",
    private: false,
    addressId: "ckyd26xz2331014vzthclrr6rj",
    address: {
      id: "ckyd26xz2331014vzthclrr6rj",
      createdAt: "2022-01-13T14:20:24.831Z",
      updatedAt: "2022-01-13T14:20:24.831Z",
      deletedAt: null,
      line1: "No.18 Fuxingmennei Street",
      line2: null,
      line3: null,
      cityId: "ckyd26xz3331024vzti3mg8thm",
      city: {
        id: "ckyd26xz3331024vzti3mg8thm",
        createdAt: "2022-01-13T14:20:24.831Z",
        updatedAt: "2022-01-13T14:20:24.831Z",
        name: "BEIJING",
        countryId: "ckvi6x5zh00894qyef5a1a0du",
        postalCode: "Xicheng District",
        country: {
          name: "China",
        },
      },
    },
  },
  {
    id: "cm5n19zwg478368y5v0ajrsgy",
    createdAt: "2025-01-07T22:19:49.888Z",
    updatedAt: "2025-01-07T22:19:49.889Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Aquitaine Poitou-Charentes",
    shortName: "CEAPC",
    registration: "353 821 028",
    private: false,
    addressId: "cm5n19zwg478468y5sbcd2i6z",
    address: {
      id: "cm5n19zwg478468y5sbcd2i6z",
      createdAt: "2025-01-07T22:19:49.888Z",
      updatedAt: "2025-01-07T22:19:49.888Z",
      deletedAt: null,
      line1: "1 parvis Corto Maltese – CS 31271",
      line2: null,
      line3: null,
      cityId: "cm5n19zwg478568y5vgky9xxv",
      city: {
        id: "cm5n19zwg478568y5vgky9xxv",
        createdAt: "2025-01-07T22:19:49.888Z",
        updatedAt: "2025-01-07T22:19:49.888Z",
        name: "Bordeaux cedex",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "33076",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwi479268y59gg4ht7u",
    createdAt: "2025-01-07T22:19:49.890Z",
    updatedAt: "2025-01-07T22:19:49.891Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne de Bourgogne Franche-Comté",
    shortName: "CEBFC",
    registration: "352 483 341",
    private: false,
    addressId: "cm5n19zwi479368y5g7usvttr",
    address: {
      id: "cm5n19zwi479368y5g7usvttr",
      createdAt: "2025-01-07T22:19:49.890Z",
      updatedAt: "2025-01-07T22:19:49.890Z",
      deletedAt: null,
      line1: "18 Avenue Françoise Giroud",
      line2: null,
      line3: null,
      cityId: "cm5n19zwi479468y52tu7xqyt",
      city: {
        id: "cm5n19zwi479468y52tu7xqyt",
        createdAt: "2025-01-07T22:19:49.890Z",
        updatedAt: "2025-01-07T22:19:49.890Z",
        name: "Dijon",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "21000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwj479768y5fnh05vc1",
    createdAt: "2025-01-07T22:19:49.891Z",
    updatedAt: "2025-01-07T22:19:49.892Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Bretagne Pays de Loire",
    shortName: "CEBPL",
    registration: "392 640 090",
    private: false,
    addressId: "cm5n19zwj479868y5uroc9e25",
    address: {
      id: "cm5n19zwj479868y5uroc9e25",
      createdAt: "2025-01-07T22:19:49.891Z",
      updatedAt: "2025-01-07T22:19:49.891Z",
      deletedAt: null,
      line1: "2",
      line2: " Place Graslin – CS 10305",
      line3: null,
      cityId: "cm5n19zwj479968y5gxapzzrn",
      city: {
        id: "cm5n19zwj479968y5gxapzzrn",
        createdAt: "2025-01-07T22:19:49.891Z",
        updatedAt: "2025-01-07T22:19:49.891Z",
        name: "NANTES Cedex 1.",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "44003",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwk480268y5hukvdgt7",
    createdAt: "2025-01-07T22:19:49.892Z",
    updatedAt: "2025-01-07T22:19:49.893Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Côte d’Azur",
    shortName: "CECAZ",
    registration: "384 402 871",
    private: false,
    addressId: "cm5n19zwk480368y5hw6d86vr",
    address: {
      id: "cm5n19zwk480368y5hw6d86vr",
      createdAt: "2025-01-07T22:19:49.892Z",
      updatedAt: "2025-01-07T22:19:49.892Z",
      deletedAt: null,
      line1: "455 Promenade des Anglais",
      line2: null,
      line3: null,
      cityId: "cm5n19zwk480468y5f1iwdy1s",
      city: {
        id: "cm5n19zwk480468y5f1iwdy1s",
        createdAt: "2025-01-07T22:19:49.892Z",
        updatedAt: "2025-01-07T22:19:49.892Z",
        name: "Nice",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "06200",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwl480668y5usuad3fq",
    createdAt: "2025-01-07T22:19:49.893Z",
    updatedAt: "2025-01-07T22:19:49.894Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Grand Est Europe",
    shortName: "CEGEE",
    registration: "775 618 622",
    private: false,
    addressId: "cm5n19zwl480768y51zorqeys",
    address: {
      id: "cm5n19zwl480768y51zorqeys",
      createdAt: "2025-01-07T22:19:49.893Z",
      updatedAt: "2025-01-07T22:19:49.893Z",
      deletedAt: null,
      line1: "1 Avenue du Rhin",
      line2: null,
      line3: null,
      cityId: "cm5n19zwl480868y5mmn415rl",
      city: {
        id: "cm5n19zwl480868y5mmn415rl",
        createdAt: "2025-01-07T22:19:49.893Z",
        updatedAt: "2025-01-07T22:19:49.893Z",
        name: "Strasbourg",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "67000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwl481268y57tq5mvrh",
    createdAt: "2025-01-07T22:19:49.893Z",
    updatedAt: "2025-01-07T22:19:49.894Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Hauts de France",
    shortName: "CEHDF",
    registration: "383 000 692",
    private: false,
    addressId: "cm5n19zwm481368y5kst9ns3o",
    address: {
      id: "cm5n19zwm481368y5kst9ns3o",
      createdAt: "2025-01-07T22:19:49.894Z",
      updatedAt: "2025-01-07T22:19:49.894Z",
      deletedAt: null,
      line1: "612 rue de la Chaude Rivière",
      line2: null,
      line3: null,
      cityId: "cm5n19zwm481468y5nv9r4897",
      city: {
        id: "cm5n19zwm481468y5nv9r4897",
        createdAt: "2025-01-07T22:19:49.894Z",
        updatedAt: "2025-01-07T22:19:49.894Z",
        name: "Lille",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "59800",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwm481868y5vf302pvo",
    createdAt: "2025-01-07T22:19:49.894Z",
    updatedAt: "2025-01-07T22:19:49.895Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Ile-de-France",
    shortName: "CEIDF",
    registration: "382 900 942",
    private: false,
    addressId: "cm5n19zwm481968y56b3odfro",
    address: {
      id: "cm5n19zwm481968y56b3odfro",
      createdAt: "2025-01-07T22:19:49.894Z",
      updatedAt: "2025-01-07T22:19:49.894Z",
      deletedAt: null,
      line1: "19 Rue du Louvre",
      line2: null,
      line3: null,
      cityId: "cm5n19zwm482068y57agjx94w",
      city: {
        id: "cm5n19zwm482068y57agjx94w",
        createdAt: "2025-01-07T22:19:49.894Z",
        updatedAt: "2025-01-07T22:19:49.894Z",
        name: "Paris",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75001",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwo483468y5bo8w8a6l",
    createdAt: "2025-01-07T22:19:49.896Z",
    updatedAt: "2025-01-07T22:19:49.897Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Loire-Centre",
    shortName: "CELC",
    registration: "383 952 470",
    private: false,
    addressId: "cm5n19zwo483568y54gli38pk",
    address: {
      id: "cm5n19zwo483568y54gli38pk",
      createdAt: "2025-01-07T22:19:49.896Z",
      updatedAt: "2025-01-07T22:19:49.896Z",
      deletedAt: null,
      line1: "7 Rue d'Escures",
      line2: null,
      line3: null,
      cityId: "cm5n19zwo483668y53nh1x1gz",
      city: {
        id: "cm5n19zwo483668y53nh1x1gz",
        createdAt: "2025-01-07T22:19:49.896Z",
        updatedAt: "2025-01-07T22:19:49.896Z",
        name: "Orléans",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "45000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwo483068y5dv1go3zf",
    createdAt: "2025-01-07T22:19:49.896Z",
    updatedAt: "2025-01-07T22:19:49.897Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Loire Drôme Ardèche",
    shortName: "CELDA",
    registration: "383 686 839",
    private: false,
    addressId: "cm5n19zwo483168y5wolqo3tw",
    address: {
      id: "cm5n19zwo483168y5wolqo3tw",
      createdAt: "2025-01-07T22:19:49.896Z",
      updatedAt: "2025-01-07T22:19:49.896Z",
      deletedAt: null,
      line1: "17 Rue Pierre et Dominique Ponchardier",
      line2: null,
      line3: null,
      cityId: "cm5n19zwo483268y5rg6yagrn",
      city: {
        id: "cm5n19zwo483268y5rg6yagrn",
        createdAt: "2025-01-07T22:19:49.896Z",
        updatedAt: "2025-01-07T22:19:49.896Z",
        name: "Saint-Étienne",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "42100",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwn482468y50ho6nkdd",
    createdAt: "2025-01-07T22:19:49.895Z",
    updatedAt: "2025-01-07T22:19:49.896Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Languedoc-Roussillon",
    shortName: "CELR",
    registration: "383 451 267",
    private: false,
    addressId: "cm5n19zwn482568y54gi8nmhm",
    address: {
      id: "cm5n19zwn482568y54gi8nmhm",
      createdAt: "2025-01-07T22:19:49.895Z",
      updatedAt: "2025-01-07T22:19:49.895Z",
      deletedAt: null,
      line1: "254 Rue Michel Teule",
      line2: null,
      line3: null,
      cityId: "cm5n19zwn482668y5ti8rp0ug",
      city: {
        id: "cm5n19zwn482668y5ti8rp0ug",
        createdAt: "2025-01-07T22:19:49.895Z",
        updatedAt: "2025-01-07T22:19:49.895Z",
        name: "Montpellier",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "34000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwp483968y5e6bkh249",
    createdAt: "2025-01-07T22:19:49.897Z",
    updatedAt: "2025-01-07T22:19:49.898Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne de Midi-Pyrénées",
    shortName: "CEMP",
    registration: "383 354 594",
    private: false,
    addressId: "cm5n19zwp484068y5wwmbqh2n",
    address: {
      id: "cm5n19zwp484068y5wwmbqh2n",
      createdAt: "2025-01-07T22:19:49.897Z",
      updatedAt: "2025-01-07T22:19:49.897Z",
      deletedAt: null,
      line1: "10 Avenue James Clerk Maxwell",
      line2: null,
      line3: null,
      cityId: "cm5n19zwp484168y5iacu04te",
      city: {
        id: "cm5n19zwp484168y5iacu04te",
        createdAt: "2025-01-07T22:19:49.897Z",
        updatedAt: "2025-01-07T22:19:49.897Z",
        name: "Toulouse",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "31100",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwq484368y5dkc2unth",
    createdAt: "2025-01-07T22:19:49.898Z",
    updatedAt: "2025-01-07T22:19:49.899Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Normandie",
    shortName: "CEN",
    registration: "384 353 413",
    private: false,
    addressId: "cm5n19zwq484468y5tsdqejsq",
    address: {
      id: "cm5n19zwq484468y5tsdqejsq",
      createdAt: "2025-01-07T22:19:49.898Z",
      updatedAt: "2025-01-07T22:19:49.898Z",
      deletedAt: null,
      line1: "151 Rue d'Uelzen",
      line2: null,
      line3: null,
      cityId: "cm5n19zwq484568y5c43gvb1q",
      city: {
        id: "cm5n19zwq484568y5c43gvb1q",
        createdAt: "2025-01-07T22:19:49.898Z",
        updatedAt: "2025-01-07T22:19:49.898Z",
        name: "Bois-Guillaume",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "76230",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zws485768y5krt3bqbd",
    createdAt: "2025-01-07T22:19:49.900Z",
    updatedAt: "2025-01-07T22:19:49.901Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CE CEPAC",
    shortName: "CEPAC",
    registration: "775 559 404",
    private: false,
    addressId: "cm5n19zws485868y5e0d85548",
    address: {
      id: "cm5n19zws485868y5e0d85548",
      createdAt: "2025-01-07T22:19:49.900Z",
      updatedAt: "2025-01-07T22:19:49.900Z",
      deletedAt: null,
      line1: "Place Estrangin Pastré",
      line2: null,
      line3: null,
      cityId: "cm5n19zwt485968y5r3coj524",
      city: {
        id: "cm5n19zwt485968y5r3coj524",
        createdAt: "2025-01-07T22:19:49.900Z",
        updatedAt: "2025-01-07T22:19:49.901Z",
        name: "Marseille",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "13006",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwh478768y539o5cbhs",
    createdAt: "2025-01-07T22:19:49.889Z",
    updatedAt: "2025-01-07T22:19:49.890Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Auvergne Limousin",
    shortName: "CEPAL",
    registration: "382 742 013",
    private: false,
    addressId: "cm5n19zwh478868y59xtoy574",
    address: {
      id: "cm5n19zwh478868y59xtoy574",
      createdAt: "2025-01-07T22:19:49.889Z",
      updatedAt: "2025-01-07T22:19:49.889Z",
      deletedAt: null,
      line1: "63 Boulevard François Mitterrand",
      line2: null,
      line3: null,
      cityId: "cm5n19zwh478968y5m4y3efi3",
      city: {
        id: "cm5n19zwh478968y5m4y3efi3",
        createdAt: "2025-01-07T22:19:49.889Z",
        updatedAt: "2025-01-07T22:19:49.889Z",
        name: "Clermont-Ferrand",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "63000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xxj327974vztgoicn345",
    createdAt: "2022-01-13T14:20:24.775Z",
    updatedAt: "2022-01-13T14:20:24.776Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Caisse d'Epargne et de Prévoyance Ile de France",
    shortName: "CEPIDF",
    registration: "382900942",
    private: false,
    addressId: "ckyd26xxj327984vzttpgpht3n",
    address: {
      id: "ckyd26xxj327984vzttpgpht3n",
      createdAt: "2022-01-13T14:20:24.775Z",
      updatedAt: "2022-01-13T14:20:24.775Z",
      deletedAt: null,
      line1: "19 Rue du Louvre",
      line2: null,
      line3: null,
      cityId: "ckyd26xxj327994vzt6kcdah3x",
      city: {
        id: "ckyd26xxj327994vzt6kcdah3x",
        createdAt: "2022-01-13T14:20:24.775Z",
        updatedAt: "2022-01-13T14:20:24.775Z",
        name: "PARIS",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75001",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xxj328014vztuunlrk4a",
    createdAt: "2022-01-13T14:20:24.775Z",
    updatedAt: "2022-01-13T14:20:24.776Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Caisse d'Epargne et de Prévoyance Bretagne - Pays de Loire",
    shortName: "CEPL",
    registration: "392640090",
    private: false,
    addressId: "ckyd26xxk328024vztxx3bmjl2",
    address: {
      id: "ckyd26xxk328024vztxx3bmjl2",
      createdAt: "2022-01-13T14:20:24.776Z",
      updatedAt: "2022-01-13T14:20:24.776Z",
      deletedAt: null,
      line1: "2 place Graslin CS 10305",
      line2: null,
      line3: null,
      cityId: "ckyd26xxk328034vzte5g8ee02",
      city: {
        id: "ckyd26xxk328034vzte5g8ee02",
        createdAt: "2022-01-13T14:20:24.776Z",
        updatedAt: "2022-01-13T14:20:24.776Z",
        name: "NANTES CEDEX 1",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "44003",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xxi327924vzt0lb5n2uq",
    createdAt: "2022-01-13T14:20:24.774Z",
    updatedAt: "2022-01-13T14:20:24.775Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Caisse d'Epargne et de Prévoyance Languedoc et Roussillon",
    shortName: "CEPLR",
    registration: "383451267",
    private: false,
    addressId: "ckyd26xxi327934vztvvjdl5zk",
    address: {
      id: "ckyd26xxi327934vztvvjdl5zk",
      createdAt: "2022-01-13T14:20:24.774Z",
      updatedAt: "2022-01-13T14:20:24.774Z",
      deletedAt: null,
      line1: "254 rue Michel Teule",
      line2: null,
      line3: null,
      cityId: "ckyd26xxi327944vztljwlpo3k",
      city: {
        id: "ckyd26xxi327944vztljwlpo3k",
        createdAt: "2022-01-13T14:20:24.774Z",
        updatedAt: "2022-01-13T14:20:24.774Z",
        name: "MONTPELLIER",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "34000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5n19zwr484768y5ewtujce4",
    createdAt: "2025-01-07T22:19:49.899Z",
    updatedAt: "2025-01-07T22:19:49.900Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d’Epargne Rhône Alpes",
    shortName: "CERA",
    registration: "384 006 029",
    private: false,
    addressId: "cm5n19zwr484868y59nvkurwc",
    address: {
      id: "cm5n19zwr484868y59nvkurwc",
      createdAt: "2025-01-07T22:19:49.899Z",
      updatedAt: "2025-01-07T22:19:49.899Z",
      deletedAt: null,
      line1: "116 Cours Lafayette",
      line2: null,
      line3: null,
      cityId: "cm5n19zwr484968y5pui6adrk",
      city: {
        id: "cm5n19zwr484968y5pui6adrk",
        createdAt: "2025-01-07T22:19:49.899Z",
        updatedAt: "2025-01-07T22:19:49.899Z",
        name: "Lyon",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "69003",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xyz330864vztma0de8kw",
    createdAt: "2022-01-13T14:20:24.827Z",
    updatedAt: "2022-01-13T14:20:24.829Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "China EXIM",
    shortName: "CEXIM",
    registration: "914403001017814000",
    private: false,
    addressId: "ckyd26xyz330874vztgc401lx2",
    address: {
      id: "ckyd26xyz330874vztgc401lx2",
      createdAt: "2022-01-13T14:20:24.827Z",
      updatedAt: "2022-01-13T14:20:24.827Z",
      deletedAt: null,
      line1: "No.30",
      line2: " FuXingMenNei Street",
      line3: " XiCheng District",
      cityId: "ckyd26xz0330884vztrpf50tzr",
      city: {
        id: "ckyd26xz0330884vztrpf50tzr",
        createdAt: "2022-01-13T14:20:24.828Z",
        updatedAt: "2022-01-13T14:20:24.828Z",
        name: "BEIJING",
        countryId: "ckvi6x5zh00894qyef5a1a0du",
        postalCode: "100031",
        country: {
          name: "China",
        },
      },
    },
  },
  {
    id: "ckyd26xxh327814vzt59vyry4k",
    createdAt: "2022-01-13T14:20:24.773Z",
    updatedAt: "2022-01-13T14:20:24.774Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Crédit Industriel et Commercial",
    shortName: "CIC FR",
    registration: "542016381",
    private: false,
    addressId: "ckyd26xxh327824vzt0oityrja",
    address: {
      id: "ckyd26xxh327824vzt0oityrja",
      createdAt: "2022-01-13T14:20:24.773Z",
      updatedAt: "2022-01-13T14:20:24.773Z",
      deletedAt: null,
      line1: "6 avenue de Provence",
      line2: null,
      line3: null,
      cityId: "ckyd26xxh327834vzt9l32lxv3",
      city: {
        id: "ckyd26xxh327834vzt9l32lxv3",
        createdAt: "2022-01-13T14:20:24.773Z",
        updatedAt: "2022-01-13T14:20:24.773Z",
        name: "PARIS",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75009",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xyi330064vzts6pta6gn",
    createdAt: "2022-01-13T14:20:24.810Z",
    updatedAt: "2022-01-13T14:20:24.811Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Citibank, N.a",
    shortName: "Citi NYC",
    registration: "13-5266470",
    private: false,
    addressId: "ckyd26xyi330074vzt1rhov2ak",
    address: {
      id: "ckyd26xyi330074vzt1rhov2ak",
      createdAt: "2022-01-13T14:20:24.810Z",
      updatedAt: "2022-01-13T14:20:24.810Z",
      deletedAt: null,
      line1: "388 Greenwich Street",
      line2: null,
      line3: null,
      cityId: "ckyd26xyi330084vztedaazhne",
      city: {
        id: "ckyd26xyi330084vztedaazhne",
        createdAt: "2022-01-13T14:20:24.810Z",
        updatedAt: "2022-01-13T14:20:24.810Z",
        name: "NEW YORK",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "NY 10013",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xyh330024vzt27sa4jl5",
    createdAt: "2022-01-13T14:20:24.809Z",
    updatedAt: "2022-01-13T14:20:24.810Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "CITIBANK EUROPE PLC, UK BRANCH",
    shortName: "Citi UK",
    registration: "FC032763",
    private: false,
    addressId: "ckyd26xyh330034vzt1z529sqv",
    address: {
      id: "ckyd26xyh330034vzt1z529sqv",
      createdAt: "2022-01-13T14:20:24.809Z",
      updatedAt: "2022-01-13T14:20:24.809Z",
      deletedAt: null,
      line1: "Citigroup Centre 33 Canada Square Canary Wharf",
      line2: null,
      line3: null,
      cityId: "ckyd26xyh330044vztg4arpzfn",
      city: {
        id: "ckyd26xyh330044vztg4arpzfn",
        createdAt: "2022-01-13T14:20:24.809Z",
        updatedAt: "2022-01-13T14:20:24.809Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "E14 5LB",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xyg329974vzt6962jj9i",
    createdAt: "2022-01-13T14:20:24.808Z",
    updatedAt: "2022-01-13T14:20:24.809Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Crédit Suisse",
    shortName: "Crédit Suisse SWITZ.",
    registration: "CHE-106.831.974",
    private: false,
    addressId: "ckyd26xyg329984vzt3fq7gaib",
    address: {
      id: "ckyd26xyg329984vzt3fq7gaib",
      createdAt: "2022-01-13T14:20:24.808Z",
      updatedAt: "2022-01-13T14:20:24.808Z",
      deletedAt: null,
      line1: "Paradeplatz 8",
      line2: null,
      line3: null,
      cityId: "ckyd26xyg329994vztuk7s19z5",
      city: {
        id: "ckyd26xyg329994vztuk7s19z5",
        createdAt: "2022-01-13T14:20:24.808Z",
        updatedAt: "2022-01-13T14:20:24.808Z",
        name: "ZURICH",
        countryId: "ckvi6x5zh00864qyecukyagie",
        postalCode: "8001",
        country: {
          name: "Switzerland",
        },
      },
    },
  },
  {
    id: "ckyd26xyk330204vztvh3o62q9",
    createdAt: "2022-01-13T14:20:24.812Z",
    updatedAt: "2022-01-13T14:20:24.814Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Danske Bank",
    shortName: "Danske bank",
    registration: "61126228",
    private: false,
    addressId: "ckyd26xyk330214vztcfc8ad7c",
    address: {
      id: "ckyd26xyk330214vztcfc8ad7c",
      createdAt: "2022-01-13T14:20:24.812Z",
      updatedAt: "2022-01-13T14:20:24.812Z",
      deletedAt: null,
      line1: "Holmens Kanal 2-12",
      line2: null,
      line3: null,
      cityId: "ckyd26xyk330224vztjaktfepy",
      city: {
        id: "ckyd26xyk330224vztjaktfepy",
        createdAt: "2022-01-13T14:20:24.813Z",
        updatedAt: "2022-01-13T14:20:24.813Z",
        name: "COPENHAGUE",
        countryId: "ckvi6x5zm01284qye3c1qpfhd",
        postalCode: "1092",
        country: {
          name: "Denmark",
        },
      },
    },
  },
  {
    id: "ckyd26xyd329774vzt8sjqfum2",
    createdAt: "2022-01-13T14:20:24.805Z",
    updatedAt: "2022-01-13T14:20:24.806Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "DBS Bank",
    shortName: "DBS",
    registration: "196800306E",
    private: false,
    addressId: "ckyd26xye329784vztvzl7la3n",
    address: {
      id: "ckyd26xye329784vztvzl7la3n",
      createdAt: "2022-01-13T14:20:24.806Z",
      updatedAt: "2022-01-13T14:20:24.806Z",
      deletedAt: null,
      line1: "12 Marina Blvd",
      line2: null,
      line3: null,
      cityId: "ckyd26xye329794vztn95q9za7",
      city: {
        id: "ckyd26xye329794vztn95q9za7",
        createdAt: "2022-01-13T14:20:24.806Z",
        updatedAt: "2022-01-13T14:20:24.806Z",
        name: "SINGAPOUR",
        countryId: "ckvi6xa9z10444qyej2rbcwh3",
        postalCode: "018982",
        country: {
          name: "Singapore",
        },
      },
    },
  },
  {
    id: "ckyd26xxv328594vztqazlygng",
    createdAt: "2022-01-13T14:20:24.787Z",
    updatedAt: "2022-01-13T14:20:24.788Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "DNB Bank ASA",
    shortName: "DNB Bank",
    registration: "984851006",
    private: false,
    addressId: "ckyd26xxv328604vztkzkvbs7s",
    address: {
      id: "ckyd26xxv328604vztkzkvbs7s",
      createdAt: "2022-01-13T14:20:24.787Z",
      updatedAt: "2022-01-13T14:20:24.787Z",
      deletedAt: null,
      line1: "Dronning Eufemias gate 30",
      line2: null,
      line3: null,
      cityId: "ckyd26xxv328614vzt9ft1yuhi",
      city: {
        id: "ckyd26xxv328614vzt9ft1yuhi",
        createdAt: "2022-01-13T14:20:24.787Z",
        updatedAt: "2022-01-13T14:20:24.787Z",
        name: "OSLO",
        countryId: "ckvi6x9qg08174qyes76887zu",
        postalCode: "0191",
        country: {
          name: "Norway",
        },
      },
    },
  },
  {
    id: "ckyd26xxx328804vztegczsm5v",
    createdAt: "2022-01-13T14:20:24.789Z",
    updatedAt: "2022-01-13T14:20:24.790Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "DNB Bank ASA, New York Branch",
    shortName: "DNB Bank NYC",
    registration: "98-0408276",
    private: false,
    addressId: "ckyd26xxx328814vztzfzm5oz4",
    address: {
      id: "ckyd26xxx328814vztzfzm5oz4",
      createdAt: "2022-01-13T14:20:24.789Z",
      updatedAt: "2022-01-13T14:20:24.790Z",
      deletedAt: null,
      line1: "30 Hudson Yards",
      line2: null,
      line3: null,
      cityId: "ckyd26xxy328824vztmn1scxon",
      city: {
        id: "ckyd26xxy328824vztmn1scxon",
        createdAt: "2022-01-13T14:20:24.790Z",
        updatedAt: "2022-01-13T14:20:24.790Z",
        name: "NEW YORK",
        countryId: "ckvi6xam911794qye0pfhrme8",
        postalCode: "NEW YORK",
        country: {
          name: "United States",
        },
      },
    },
  },
  {
    id: "ckyd26xxw328704vztbdta0hkj",
    createdAt: "2022-01-13T14:20:24.788Z",
    updatedAt: "2022-01-13T14:20:24.789Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "DNB Bank ASA, UK Branch",
    shortName: "DNB Bank UK",
    registration: "FC025119",
    private: false,
    addressId: "ckyd26xxw328714vztobsyqqt7",
    address: {
      id: "ckyd26xxw328714vztobsyqqt7",
      createdAt: "2022-01-13T14:20:24.788Z",
      updatedAt: "2022-01-13T14:20:24.788Z",
      deletedAt: null,
      line1: "8th Floor",
      line2: " The Walbrook Building 25 Walbrook",
      line3: null,
      cityId: "ckyd26xxw328724vzttfxybijs",
      city: {
        id: "ckyd26xxw328724vzttfxybijs",
        createdAt: "2022-01-13T14:20:24.788Z",
        updatedAt: "2022-01-13T14:20:24.788Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC4N 8AF",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xym330254vzt2ihaausx",
    createdAt: "2022-01-13T14:20:24.814Z",
    updatedAt: "2022-01-13T14:20:24.815Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Danish Ship Finance",
    shortName: "DSF",
    registration: "27492649",
    private: false,
    addressId: "ckyd26xym330264vzt161eqike",
    address: {
      id: "ckyd26xym330264vzt161eqike",
      createdAt: "2022-01-13T14:20:24.814Z",
      updatedAt: "2022-01-13T14:20:24.814Z",
      deletedAt: null,
      line1: "Sankt Annæ Pl. 3",
      line2: null,
      line3: null,
      cityId: "ckyd26xym330274vzt9r9j3hic",
      city: {
        id: "ckyd26xym330274vzt9r9j3hic",
        createdAt: "2022-01-13T14:20:24.814Z",
        updatedAt: "2022-01-13T14:20:24.814Z",
        name: "COPENHAGUE",
        countryId: "ckvi6x5zm01284qye3c1qpfhd",
        postalCode: "1250",
        country: {
          name: "Denmark",
        },
      },
    },
  },
  {
    id: "ckylc6yk71046194vxwaayu8oxa",
    createdAt: "2022-01-19T09:22:31.159Z",
    updatedAt: "2022-01-19T09:22:31.161Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Entrust Global ICAV - Blue Ocean Fund",
    shortName: "Entrust Blue Ocean Fund",
    registration: "LEI549300MFBL5PICTTOM90",
    private: false,
    addressId: "ckylc6yk71046204vxwh5gmuo1q",
    address: {
      id: "ckylc6yk71046204vxwh5gmuo1q",
      createdAt: "2022-01-19T09:22:31.159Z",
      updatedAt: "2022-01-19T09:22:31.159Z",
      deletedAt: null,
      line1: "32 Molesworth Street",
      line2: " Dublin 2",
      line3: null,
      cityId: "ckylc6yk71046214vxw8jy5ff64",
      city: {
        id: "ckylc6yk71046214vxw8jy5ff64",
        createdAt: "2022-01-19T09:22:31.159Z",
        updatedAt: "2022-01-19T09:22:31.159Z",
        name: "Dublin",
        countryId: "ckvi6x7fn02734qye72wvq4rs",
        postalCode: "IE-D IE D02 Y512",
        country: {
          name: "Ireland",
        },
      },
    },
  },
  {
    id: "ckyd26xyo330364vzttxwe3pq2",
    createdAt: "2022-01-13T14:20:24.816Z",
    updatedAt: "2022-01-13T14:20:24.818Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Hamburg Commercial Bank",
    shortName: "HCB",
    registration: "87366",
    private: false,
    addressId: "ckyd26xyo330374vztdlcvl1r6",
    address: {
      id: "ckyd26xyo330374vztdlcvl1r6",
      createdAt: "2022-01-13T14:20:24.816Z",
      updatedAt: "2022-01-13T14:20:24.816Z",
      deletedAt: null,
      line1: "Gerhart-Hauptmann-Platz 50",
      line2: null,
      line3: null,
      cityId: "ckyd26xyo330384vzt2j9ohxcc",
      city: {
        id: "ckyd26xyo330384vzt2j9ohxcc",
        createdAt: "2022-01-13T14:20:24.816Z",
        updatedAt: "2022-01-13T14:20:24.816Z",
        name: "HAMBURG",
        countryId: "ckvi6x5zl01224qyebcy10zfp",
        postalCode: "20095",
        country: {
          name: "Germany",
        },
      },
    },
  },
  {
    id: "ckyd26xyp330414vztt71ofybc",
    createdAt: "2022-01-13T14:20:24.817Z",
    updatedAt: "2022-01-13T14:20:24.819Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Helaba (Landesbank Hessen-Thüringen Girozentrale)",
    shortName: "Helaba",
    registration: "29821",
    private: false,
    addressId: "ckyd26xyq330424vztdzjf5zdq",
    address: {
      id: "ckyd26xyq330424vztdzjf5zdq",
      createdAt: "2022-01-13T14:20:24.818Z",
      updatedAt: "2022-01-13T14:20:24.818Z",
      deletedAt: null,
      line1: "Neue Mainzer Strasse 52-58",
      line2: null,
      line3: null,
      cityId: "ckyd26xyq330434vztf2bhnham",
      city: {
        id: "ckyd26xyq330434vztf2bhnham",
        createdAt: "2022-01-13T14:20:24.818Z",
        updatedAt: "2022-01-13T14:20:24.818Z",
        name: "FRANKFURT AM MAIN",
        countryId: "ckvi6x5zl01224qyebcy10zfp",
        postalCode: "60311",
        country: {
          name: "Germany",
        },
      },
    },
  },
  {
    id: "ckyd26xy8329434vztg2py6jbh",
    createdAt: "2022-01-13T14:20:24.800Z",
    updatedAt: "2022-01-13T14:20:24.801Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "HSBC BANK PLC",
    shortName: "HSBC London",
    registration: "00014259",
    private: false,
    addressId: "ckyd26xy8329444vzt8ogz0oa0",
    address: {
      id: "ckyd26xy8329444vzt8ogz0oa0",
      createdAt: "2022-01-13T14:20:24.800Z",
      updatedAt: "2022-01-13T14:20:24.800Z",
      deletedAt: null,
      line1: "8 Canada Square",
      line2: null,
      line3: null,
      cityId: "ckyd26xy8329454vzt56as4a01",
      city: {
        id: "ckyd26xy8329454vzt56as4a01",
        createdAt: "2022-01-13T14:20:24.800Z",
        updatedAt: "2022-01-13T14:20:24.800Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "E14 5HQ",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xyj330154vzty1ouxrf3",
    createdAt: "2022-01-13T14:20:24.811Z",
    updatedAt: "2022-01-13T14:20:24.812Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "ICBC (LONDON) PLC",
    shortName: "ICBC UK",
    registration: "04552753",
    private: false,
    addressId: "ckyd26xyj330164vztmjnmej2v",
    address: {
      id: "ckyd26xyj330164vztmjnmej2v",
      createdAt: "2022-01-13T14:20:24.811Z",
      updatedAt: "2022-01-13T14:20:24.811Z",
      deletedAt: null,
      line1: "81 King William Street",
      line2: null,
      line3: null,
      cityId: "ckyd26xyj330174vzte38oqq49",
      city: {
        id: "ckyd26xyj330174vzte38oqq49",
        createdAt: "2022-01-13T14:20:24.811Z",
        updatedAt: "2022-01-13T14:20:24.811Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC4N 7BG",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xxt328454vztnthn3t5f",
    createdAt: "2022-01-13T14:20:24.785Z",
    updatedAt: "2022-01-13T14:20:24.786Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "ING Bank N.V",
    shortName: "ING Bank NV",
    registration: "33031431",
    private: false,
    addressId: "ckyd26xxt328464vzt3ixeelu0",
    address: {
      id: "ckyd26xxt328464vzt3ixeelu0",
      createdAt: "2022-01-13T14:20:24.785Z",
      updatedAt: "2022-01-13T14:20:24.785Z",
      deletedAt: null,
      line1: "Bijlmerdreef 106 - 1102 CT",
      line2: null,
      line3: null,
      cityId: "ckyd26xxt328474vztjgkej1cl",
      city: {
        id: "ckyd26xxt328474vztjgkej1cl",
        createdAt: "2022-01-13T14:20:24.785Z",
        updatedAt: "2022-01-13T14:20:24.785Z",
        name: "AMSTERDAM ZUIDOOST",
        countryId: "ckvi6x9pp08034qye0xciozlj",
        postalCode: "",
        country: {
          name: "Netherlands",
        },
      },
    },
  },
  {
    id: "ckyd26xz1330964vztngfet8nc",
    createdAt: "2022-01-13T14:20:24.829Z",
    updatedAt: "2022-01-13T14:20:24.831Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "The Iyo Bank",
    shortName: "Iyo Bank",
    registration: "",
    private: false,
    addressId: "ckyd26xz1330974vztpzd1iu1h",
    address: {
      id: "ckyd26xz1330974vztpzd1iu1h",
      createdAt: "2022-01-13T14:20:24.829Z",
      updatedAt: "2022-01-13T14:20:24.829Z",
      deletedAt: null,
      line1: "1",
      line2: " Minami-Horibata-Cho",
      line3: " Matsuyama-Shi",
      cityId: "ckyd26xz2330984vzt0vd74iwp",
      city: {
        id: "ckyd26xz2330984vzt0vd74iwp",
        createdAt: "2022-01-13T14:20:24.830Z",
        updatedAt: "2022-01-13T14:20:24.830Z",
        name: "Ehima",
        countryId: "ckvi6x8x604944qyett7tclvr",
        postalCode: "790-8514",
        country: {
          name: "Japan",
        },
      },
    },
  },
  {
    id: "ckyd26xyr330514vztyk5cp1u5",
    createdAt: "2022-01-13T14:20:24.819Z",
    updatedAt: "2022-01-13T14:20:24.821Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Japan Bank for International Cooperation",
    shortName: "JBIC",
    registration: "10001145977",
    private: false,
    addressId: "ckyd26xyr330524vztadac840o",
    address: {
      id: "ckyd26xyr330524vztadac840o",
      createdAt: "2022-01-13T14:20:24.819Z",
      updatedAt: "2022-01-13T14:20:24.820Z",
      deletedAt: null,
      line1: "4-1 Ohtemachi 1-chome",
      line2: " Chiyoda-ku",
      line3: null,
      cityId: "ckyd26xys330534vzt4no4496f",
      city: {
        id: "ckyd26xys330534vzt4no4496f",
        createdAt: "2022-01-13T14:20:24.820Z",
        updatedAt: "2022-01-13T14:20:24.820Z",
        name: "TOKYO",
        countryId: "ckvi6x8x604944qyett7tclvr",
        postalCode: "100-8144",
        country: {
          name: "Japan",
        },
      },
    },
  },
  {
    id: "ckyd26xz4331044vztq2v0x91n",
    createdAt: "2022-01-13T14:20:24.832Z",
    updatedAt: "2022-01-13T14:20:24.833Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "JPMorgan Chase & Co",
    shortName: "JP Morgan",
    registration: "13-2624428",
    private: false,
    addressId: "ckyd26xz4331054vzt9ew9qr3z",
    address: {
      id: "ckyd26xz4331054vzt9ew9qr3z",
      createdAt: "2022-01-13T14:20:24.832Z",
      updatedAt: "2022-01-13T14:20:24.832Z",
      deletedAt: null,
      line1: "270 Park Avenue",
      line2: null,
      line3: null,
      cityId: "ckyd26xz4331064vztltej7q3h",
      city: {
        id: "ckyd26xz4331064vztltej7q3h",
        createdAt: "2022-01-13T14:20:24.832Z",
        updatedAt: "2022-01-13T14:20:24.832Z",
        name: "NEW YORK",
        countryId: "ckvi6xam911794qye0pfhrme8",
        postalCode: "NY 10017",
        country: {
          name: "United States",
        },
      },
    },
  },
  {
    id: "ckyd26xyg329924vztecdjsn7o",
    createdAt: "2022-01-13T14:20:24.807Z",
    updatedAt: "2022-01-13T14:20:24.809Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Kredit Anstalt für Wiederaufbau",
    shortName: "KFW",
    registration: "79744",
    private: false,
    addressId: "ckyd26xyg329934vztga3aeuzu",
    address: {
      id: "ckyd26xyg329934vztga3aeuzu",
      createdAt: "2022-01-13T14:20:24.808Z",
      updatedAt: "2022-01-13T14:20:24.808Z",
      deletedAt: null,
      line1: "Palmengartenstrasse 5-9",
      line2: null,
      line3: null,
      cityId: "ckyd26xyg329944vztfytmatu2",
      city: {
        id: "ckyd26xyg329944vztfytmatu2",
        createdAt: "2022-01-13T14:20:24.808Z",
        updatedAt: "2022-01-13T14:20:24.808Z",
        name: "FRANKFURT AM MAIN",
        countryId: "ckvi6x5zl01224qyebcy10zfp",
        postalCode: "60325",
        country: {
          name: "Germany",
        },
      },
    },
  },
  {
    id: "cm2og3php347265a0i20f185mq",
    createdAt: "2024-10-25T08:03:29.869Z",
    updatedAt: "2024-10-25T08:03:29.870Z",
    deletedAt: null,
    industry: "OTHER",
    type: "GROUP",
    name: "Matthieu_BORROWER_DEMO_ORG",
    shortName: "MB",
    registration: "Reg-MB",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "ckyd26xy2329064vztnuu4pp4e",
    createdAt: "2022-01-13T14:20:24.794Z",
    updatedAt: "2022-01-13T14:20:24.795Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Mizuho Bank Ltd",
    shortName: "Mizuho UK",
    registration: "FC004234",
    private: false,
    addressId: "ckyd26xy2329074vzt9zv3kh06",
    address: {
      id: "ckyd26xy2329074vzt9zv3kh06",
      createdAt: "2022-01-13T14:20:24.794Z",
      updatedAt: "2022-01-13T14:20:24.794Z",
      deletedAt: null,
      line1: "Mizuho Bank 30 Old Bailey",
      line2: null,
      line3: null,
      cityId: "ckyd26xy2329084vztlsg1tgij",
      city: {
        id: "ckyd26xy2329084vztlsg1tgij",
        createdAt: "2022-01-13T14:20:24.794Z",
        updatedAt: "2022-01-13T14:20:24.794Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC4M 7AU",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xye329814vztrf31xm56",
    createdAt: "2022-01-13T14:20:24.806Z",
    updatedAt: "2022-01-13T14:20:24.807Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Maritime & Merchant Bank ASA",
    shortName: "M&M",
    registration: "912277070",
    private: false,
    addressId: "ckyd26xye329824vzt63wmrrl9",
    address: {
      id: "ckyd26xye329824vzt63wmrrl9",
      createdAt: "2022-01-13T14:20:24.806Z",
      updatedAt: "2022-01-13T14:20:24.806Z",
      deletedAt: null,
      line1: "Haakon VIIs gate 1",
      line2: null,
      line3: null,
      cityId: "ckyd26xye329834vzt2d68l6xd",
      city: {
        id: "ckyd26xye329834vzt2d68l6xd",
        createdAt: "2022-01-13T14:20:24.806Z",
        updatedAt: "2022-01-13T14:20:24.806Z",
        name: "OSLO",
        countryId: "ckvi6x9qg08174qyes76887zu",
        postalCode: "0161",
        country: {
          name: "Norway",
        },
      },
    },
  },
  {
    id: "ckyd26xyw330704vztanxk2pgu",
    createdAt: "2022-01-13T14:20:24.824Z",
    updatedAt: "2022-01-13T14:20:24.825Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "National Bank of Greece",
    shortName: "National Bank of Greece",
    registration: "EYKCVFAW8ZLO",
    private: false,
    addressId: "ckyd26xyw330714vztzxipi07h",
    address: {
      id: "ckyd26xyw330714vztzxipi07h",
      createdAt: "2022-01-13T14:20:24.824Z",
      updatedAt: "2022-01-13T14:20:24.824Z",
      deletedAt: null,
      line1: "Eolou 86",
      line2: null,
      line3: null,
      cityId: "ckyd26xyw330724vztaigq5hw4",
      city: {
        id: "ckyd26xyw330724vztaigq5hw4",
        createdAt: "2022-01-13T14:20:24.824Z",
        updatedAt: "2022-01-13T14:20:24.824Z",
        name: "ATHENS",
        countryId: "ckvi6x5zs01824qyerfpm7o7u",
        postalCode: "102 32",
        country: {
          name: "Greece",
        },
      },
    },
  },
  {
    id: "cm5n19zwu486768y5gihp5jsv",
    createdAt: "2025-01-07T22:19:49.902Z",
    updatedAt: "2025-01-07T22:19:49.903Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Natixis Corporate & Investment Banking",
    shortName: "Natixis",
    registration: "542 044 524",
    private: false,
    addressId: "cm5n19zwu486868y5sdc7y5kn",
    address: {
      id: "cm5n19zwu486868y5sdc7y5kn",
      createdAt: "2025-01-07T22:19:49.902Z",
      updatedAt: "2025-01-07T22:19:49.902Z",
      deletedAt: null,
      line1: "7",
      line2: " promenade Germaine Sablon",
      line3: null,
      cityId: "cm5n19zwu486968y5rhnkfzyz",
      city: {
        id: "cm5n19zwu486968y5rhnkfzyz",
        createdAt: "2025-01-07T22:19:49.902Z",
        updatedAt: "2025-01-07T22:19:49.902Z",
        name: "Paris",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75013",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xxy328864vztv37ocpjk",
    createdAt: "2022-01-13T14:20:24.790Z",
    updatedAt: "2022-01-13T14:20:24.791Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Nordea Bank ASA",
    shortName: "Nordea NO",
    registration: "920058817",
    private: false,
    addressId: "ckyd26xxy328874vzty657hjvl",
    address: {
      id: "ckyd26xxy328874vzty657hjvl",
      createdAt: "2022-01-13T14:20:24.790Z",
      updatedAt: "2022-01-13T14:20:24.790Z",
      deletedAt: null,
      line1: "Essendrops gate 7",
      line2: " Postboks 1166 Sentrum",
      line3: null,
      cityId: "ckyd26xxy328884vzt7t5lc3sp",
      city: {
        id: "ckyd26xxy328884vzt7t5lc3sp",
        createdAt: "2022-01-13T14:20:24.790Z",
        updatedAt: "2022-01-13T14:20:24.790Z",
        name: "OSLO",
        countryId: "ckvi6x9qg08174qyes76887zu",
        postalCode: "0107",
        country: {
          name: "Norway",
        },
      },
    },
  },
  {
    id: "ckyd26xy0328964vztjkblo6xa",
    createdAt: "2022-01-13T14:20:24.792Z",
    updatedAt: "2022-01-13T14:20:24.793Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Nordea Bank Abp",
    shortName: "Nordea SWE",
    registration: "516411-1683",
    private: false,
    addressId: "ckyd26xy0328974vztwv2am17m",
    address: {
      id: "ckyd26xy0328974vztwv2am17m",
      createdAt: "2022-01-13T14:20:24.792Z",
      updatedAt: "2022-01-13T14:20:24.792Z",
      deletedAt: null,
      line1: "Smålandsgatan 17",
      line2: null,
      line3: null,
      cityId: "ckyd26xy0328984vztcszcvi33",
      city: {
        id: "ckyd26xy0328984vztcszcvi33",
        createdAt: "2022-01-13T14:20:24.792Z",
        updatedAt: "2022-01-13T14:20:24.792Z",
        name: "STOCKHOLM",
        countryId: "ckvi6x8fd03734qyeq8osqwrp",
        postalCode: "105 71",
        country: {
          name: "Sweden",
        },
      },
    },
  },
  {
    id: "cm5n19zw5471868y54uio0t9y",
    createdAt: "2025-01-07T22:19:49.877Z",
    updatedAt: "2025-01-07T22:19:49.878Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Palatine",
    shortName: "Palatine",
    registration: "542 104 245",
    private: false,
    addressId: "cm5n19zw5471968y5vvquzwck",
    address: {
      id: "cm5n19zw5471968y5vvquzwck",
      createdAt: "2025-01-07T22:19:49.877Z",
      updatedAt: "2025-01-07T22:19:49.877Z",
      deletedAt: null,
      line1: "86 Rue de Courcelles",
      line2: null,
      line3: null,
      cityId: "ckylc6yk11045984vxwymh4woub",
      city: {
        id: "ckylc6yk11045984vxwymh4woub",
        createdAt: "2022-01-19T09:22:31.153Z",
        updatedAt: "2022-01-19T09:22:31.153Z",
        name: "Paris",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75008",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xyu330614vztx27pxbn7",
    createdAt: "2022-01-13T14:20:24.822Z",
    updatedAt: "2022-01-13T14:20:24.823Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Piraeus Bank",
    shortName: "Piraeus Bank",
    registration: "1KW32H8THQ6F",
    private: false,
    addressId: "ckyd26xyu330624vztmwg9ie80",
    address: {
      id: "ckyd26xyu330624vztmwg9ie80",
      createdAt: "2022-01-13T14:20:24.822Z",
      updatedAt: "2022-01-13T14:20:24.822Z",
      deletedAt: null,
      line1: "Piraeus Bank 4 Amerikis St",
      line2: null,
      line3: null,
      cityId: "ckyd26xyu330634vztfuso9x1i",
      city: {
        id: "ckyd26xyu330634vztfuso9x1i",
        createdAt: "2022-01-13T14:20:24.822Z",
        updatedAt: "2022-01-13T14:20:24.822Z",
        name: "ATHENS",
        countryId: "ckvi6x5zs01824qyerfpm7o7u",
        postalCode: "105 64",
        country: {
          name: "Greece",
        },
      },
    },
  },
  {
    id: "ckyd26xyq330464vztyuym11a0",
    createdAt: "2022-01-13T14:20:24.818Z",
    updatedAt: "2022-01-13T14:20:24.819Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Banco Santander",
    shortName: "Santander",
    registration: "A-39000013",
    private: false,
    addressId: "ckyd26xyq330474vzt4lya2649",
    address: {
      id: "ckyd26xyq330474vzt4lya2649",
      createdAt: "2022-01-13T14:20:24.818Z",
      updatedAt: "2022-01-13T14:20:24.818Z",
      deletedAt: null,
      line1: "Av. de Cantabria s/n",
      line2: null,
      line3: null,
      cityId: "ckyd26xyq330484vztdp0xz8wi",
      city: {
        id: "ckyd26xyq330484vztdp0xz8wi",
        createdAt: "2022-01-13T14:20:24.818Z",
        updatedAt: "2022-01-13T14:20:24.818Z",
        name: "MADRID",
        countryId: "ckvi6x5zn01424qyetum74vm9",
        postalCode: "28660 Boadilla del Monte",
        country: {
          name: "Spain",
        },
      },
    },
  },
  {
    id: "ckyd26xz0330914vztzvmlofrl",
    createdAt: "2022-01-13T14:20:24.828Z",
    updatedAt: "2022-01-13T14:20:24.830Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Skandinaviska Enskida Banken",
    shortName: "SEB",
    registration: "502032-9081",
    private: false,
    addressId: "ckyd26xz0330924vztosi3z8pt",
    address: {
      id: "ckyd26xz0330924vztosi3z8pt",
      createdAt: "2022-01-13T14:20:24.828Z",
      updatedAt: "2022-01-13T14:20:24.828Z",
      deletedAt: null,
      line1: "Kungstradgardsgatan 8",
      line2: null,
      line3: null,
      cityId: "ckyd26xz1330934vzthqzzem0w",
      city: {
        id: "ckyd26xz1330934vzthqzzem0w",
        createdAt: "2022-01-13T14:20:24.829Z",
        updatedAt: "2022-01-13T14:20:24.829Z",
        name: "STOCKHOLM",
        countryId: "ckvi6x8fd03734qyeq8osqwrp",
        postalCode: "106 40",
        country: {
          name: "Sweden",
        },
      },
    },
  },
  {
    id: "ckyd26xxk328064vztzents4xh",
    createdAt: "2022-01-13T14:20:24.776Z",
    updatedAt: "2022-01-13T14:20:24.777Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Société Générale",
    shortName: "SG",
    registration: "552120222",
    private: false,
    addressId: "ckyd26xxk328074vztw6qq78kr",
    address: {
      id: "ckyd26xxk328074vztw6qq78kr",
      createdAt: "2022-01-13T14:20:24.776Z",
      updatedAt: "2022-01-13T14:20:24.776Z",
      deletedAt: null,
      line1: "29 boulevard Haussmann",
      line2: null,
      line3: null,
      cityId: "ckyd26xxk328084vzt5ni11oz9",
      city: {
        id: "ckyd26xxk328084vzt5ni11oz9",
        createdAt: "2022-01-13T14:20:24.776Z",
        updatedAt: "2022-01-13T14:20:24.776Z",
        name: "PARIS",
        countryId: "ckvi6x5zp01544qye4dnjappx",
        postalCode: "75009",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd26xxm328164vztvg1r31t8",
    createdAt: "2022-01-13T14:20:24.778Z",
    updatedAt: "2022-01-13T14:20:24.779Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Société Générale",
    shortName: "SG London Branch",
    registration: "FC000268",
    private: false,
    addressId: "ckyd26xxm328174vztjrt0h9lo",
    address: {
      id: "ckyd26xxm328174vztjrt0h9lo",
      createdAt: "2022-01-13T14:20:24.778Z",
      updatedAt: "2022-01-13T14:20:24.778Z",
      deletedAt: null,
      line1: "One Bank Street",
      line2: null,
      line3: null,
      cityId: "ckyd26xxm328184vztsw054plq",
      city: {
        id: "ckyd26xxm328184vztsw054plq",
        createdAt: "2022-01-13T14:20:24.778Z",
        updatedAt: "2022-01-13T14:20:24.778Z",
        name: "LONDON E14 4SG",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "Canary Wharf",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xxr328364vztef76d5mb",
    createdAt: "2022-01-13T14:20:24.783Z",
    updatedAt: "2022-01-13T14:20:24.784Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "SMBC Bank International plc",
    shortName: "SMBC BI UK",
    registration: "4684034",
    private: false,
    addressId: "ckyd26xxr328374vztvgyt00oe",
    address: {
      id: "ckyd26xxr328374vztvgyt00oe",
      createdAt: "2022-01-13T14:20:24.783Z",
      updatedAt: "2022-01-13T14:20:24.783Z",
      deletedAt: null,
      line1: "99 Queen Victoria Street",
      line2: null,
      line3: null,
      cityId: "ckyd26xxr328384vztcwbqzhpb",
      city: {
        id: "ckyd26xxr328384vztcwbqzhpb",
        createdAt: "2022-01-13T14:20:24.783Z",
        updatedAt: "2022-01-13T14:20:24.783Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC4V 4EH",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xy3329104vztmqto5n2k",
    createdAt: "2022-01-13T14:20:24.795Z",
    updatedAt: "2022-01-13T14:20:24.796Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Sumitomo Mitsui Trust Bank, Limited London Branch",
    shortName: "SMTB UK",
    registration: "FC008329",
    private: false,
    addressId: "ckyd26xy3329114vzt6mc5eldg",
    address: {
      id: "ckyd26xy3329114vzt6mc5eldg",
      createdAt: "2022-01-13T14:20:24.795Z",
      updatedAt: "2022-01-13T14:20:24.795Z",
      deletedAt: null,
      line1: "3rd Floor",
      line2: " 155 Bishopsgate",
      line3: null,
      cityId: "ckyd26xy3329124vztqmh5p2zh",
      city: {
        id: "ckyd26xy3329124vztqmh5p2zh",
        createdAt: "2022-01-13T14:20:24.795Z",
        updatedAt: "2022-01-13T14:20:24.795Z",
        name: "LONDON",
        countryId: "ckvi6x5zq01614qye3gr39p2m",
        postalCode: "EC2M 3XU",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd26xy7329384vzttcg3tl5r",
    createdAt: "2022-01-13T14:20:24.799Z",
    updatedAt: "2022-01-13T14:20:24.800Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Standard Chartered Bank",
    shortName: "Standard Chartered NYC",
    registration: "94-1595409",
    private: false,
    addressId: "ckyd26xy7329394vztv7d0643s",
    address: {
      id: "ckyd26xy7329394vztv7d0643s",
      createdAt: "2022-01-13T14:20:24.799Z",
      updatedAt: "2022-01-13T14:20:24.799Z",
      deletedAt: null,
      line1: "1095 Avenue of the Americas",
      line2: null,
      line3: null,
      cityId: "ckyd26xy7329404vzt3lh9rk8q",
      city: {
        id: "ckyd26xy7329404vzt3lh9rk8q",
        createdAt: "2022-01-13T14:20:24.799Z",
        updatedAt: "2022-01-13T14:20:24.799Z",
        name: "NEW YORK",
        countryId: "ckvi6xam911794qye0pfhrme8",
        postalCode: "NY 10036",
        country: {
          name: "United States",
        },
      },
    },
  },
  {
    id: "ckyd26xxu328544vztazmr7kmz",
    createdAt: "2022-01-13T14:20:24.786Z",
    updatedAt: "2022-01-13T14:20:24.787Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Swedbank AB",
    shortName: "Swedbank",
    registration: "502017-7753",
    private: false,
    addressId: "ckyd26xxu328554vztpitqcobe",
    address: {
      id: "ckyd26xxu328554vztpitqcobe",
      createdAt: "2022-01-13T14:20:24.786Z",
      updatedAt: "2022-01-13T14:20:24.786Z",
      deletedAt: null,
      line1: "Swedbank AB",
      line2: null,
      line3: null,
      cityId: "ckyd26xxu328564vztnga1z97b",
      city: {
        id: "ckyd26xxu328564vztnga1z97b",
        createdAt: "2022-01-13T14:20:24.786Z",
        updatedAt: "2022-01-13T14:20:24.786Z",
        name: "STOCKHOLM",
        countryId: "ckvi6x9pp08034qye0xciozlj",
        postalCode: "105 34",
        country: {
          name: "Netherlands",
        },
      },
    },
  },
  {
    id: "ckyd26xyy330814vztawjy8gbg",
    createdAt: "2022-01-13T14:20:24.826Z",
    updatedAt: "2022-01-13T14:20:24.827Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Unicredit",
    shortName: "Unicredit",
    registration: "348170101",
    private: false,
    addressId: "ckyd26xyy330824vzteclbvlim",
    address: {
      id: "ckyd26xyy330824vzteclbvlim",
      createdAt: "2022-01-13T14:20:24.826Z",
      updatedAt: "2022-01-13T14:20:24.826Z",
      deletedAt: null,
      line1: "Piazza Gae Aulenti 3 - Tower A",
      line2: null,
      line3: null,
      cityId: "ckyd26xyy330834vzt00bcptzb",
      city: {
        id: "ckyd26xyy330834vzt00bcptzb",
        createdAt: "2022-01-13T14:20:24.826Z",
        updatedAt: "2022-01-13T14:20:24.826Z",
        name: "MILANO",
        countryId: "ckvi6x8ey03674qyeaxc8pvoc",
        postalCode: "20154",
        country: {
          name: "Italy",
        },
      },
    },
  },
];

export const organisationsMock: Organization[] = [
  {
    id: "ckyd3bxzz144954vxli0d37mk5",
    createdAt: "2022-01-13T14:52:17.759Z",
    updatedAt: "2022-01-13T14:52:17.760Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "ABN AMRO Bank N.V. Oslo branch",
    shortName: "ABN AMRO NO",
    registration: "995550164",
    private: false,
    addressId: "ckyd3bxzz144964vxl9vizfsew",
    address: {
      id: "ckyd3bxzz144964vxl9vizfsew",
      createdAt: "2022-01-13T14:52:17.759Z",
      updatedAt: "2022-01-13T14:52:17.759Z",
      deletedAt: null,
      line1: "Olav V Gate 5",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt524474vxliflw2egm",
      city: {
        id: "ckyc7bqt524474vxliflw2egm",
        createdAt: "2022-01-12T23:56:20.729Z",
        updatedAt: "2022-01-12T23:56:20.729Z",
        name: "OSLO",
        countryId: "ckvrc3yr50791sxyenmy243pg",
        postalCode: "N-0161",
        country: {
          name: "Norway",
        },
      },
    },
  },
  {
    id: "ckyd3bxzx144884vxl9ar8dlfo",
    createdAt: "2022-01-13T14:52:17.757Z",
    updatedAt: "2022-01-13T14:52:17.758Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "ABN AMRO Bank N.V. ",
    shortName: "ABN AMRO NV",
    registration: "34334259",
    private: false,
    addressId: "ckyd3bxzx144894vxla9n2aurv",
    address: {
      id: "ckyd3bxzx144894vxla9n2aurv",
      createdAt: "2022-01-13T14:52:17.757Z",
      updatedAt: "2022-01-13T14:52:17.757Z",
      deletedAt: null,
      line1: "Gustav Mahlerlaan 10",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt324354vxlovm9aex4",
      city: {
        id: "ckyc7bqt324354vxlovm9aex4",
        createdAt: "2022-01-12T23:56:20.727Z",
        updatedAt: "2022-01-12T23:56:20.727Z",
        name: "AMSTERDAM",
        countryId: "ckvrc3yr00789sxye4a1bermd",
        postalCode: "1082 PP",
        country: {
          name: "Netherlands",
        },
      },
    },
  },
  {
    id: "ckyd3by0l145944vxlu9oojwey",
    createdAt: "2022-01-13T14:52:17.781Z",
    updatedAt: "2022-01-13T14:52:17.782Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Alpha Bank",
    shortName: "Alpha Bank",
    registration: "9N55YRQC69CN",
    private: false,
    addressId: "ckyd3by0l145954vxlmthr5aj6",
    address: {
      id: "ckyd3by0l145954vxlmthr5aj6",
      createdAt: "2022-01-13T14:52:17.781Z",
      updatedAt: "2022-01-13T14:52:17.781Z",
      deletedAt: null,
      line1: "40 Stadiou St",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtn25794vxlpufw4cbc",
      city: {
        id: "ckyc7bqtn25794vxlpufw4cbc",
        createdAt: "2022-01-12T23:56:20.747Z",
        updatedAt: "2022-01-12T23:56:20.747Z",
        name: "ATHENS",
        countryId: "ckvrc3wur0183sxyes9zo3x0y",
        postalCode: "102 52",
        country: {
          name: "Greece",
        },
      },
    },
  },
  {
    id: "ckyd3by0m146014vxlcfcgh4xc",
    createdAt: "2022-01-13T14:52:17.782Z",
    updatedAt: "2022-01-13T14:52:17.783Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Australia & New Zealand Bank",
    shortName: "ANZ",
    registration: "35976",
    private: false,
    addressId: "ckyd3by0m146024vxlptlvg371",
    address: {
      id: "ckyd3by0m146024vxlptlvg371",
      createdAt: "2022-01-13T14:52:17.782Z",
      updatedAt: "2022-01-13T14:52:17.782Z",
      deletedAt: null,
      line1: "ANZ Centre Melbourne",
      line2: " Level 9 833 collins Street",
      line3: " Docklands",
      cityId: "ckyc7bqtp25894vxlmq85n9ga",
      city: {
        id: "ckyc7bqtp25894vxlmq85n9ga",
        createdAt: "2022-01-12T23:56:20.749Z",
        updatedAt: "2022-01-12T23:56:20.749Z",
        name: "VICTORIA",
        countryId: "ckvrc3wub0033sxyemhi8ttjr",
        postalCode: "3008",
        country: {
          name: "Australia",
        },
      },
    },
  },
  {
    id: "bank1",
    createdAt: "2024-07-02T17:32:41.181Z",
    updatedAt: "2024-10-09T13:13:12.668Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 1",
    shortName: "Bank-1",
    registration: "Reg-Bank-1",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank2",
    createdAt: "2024-07-02T17:32:41.264Z",
    updatedAt: "2024-10-09T13:13:12.737Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 2",
    shortName: "Bank-2",
    registration: "Reg-Bank-2",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank3",
    createdAt: "2024-07-02T17:32:41.333Z",
    updatedAt: "2024-10-09T13:13:12.805Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 3",
    shortName: "Bank-3",
    registration: "Reg-Bank-3",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank4",
    createdAt: "2024-07-02T17:32:41.392Z",
    updatedAt: "2024-10-09T13:13:12.869Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 4",
    shortName: "Bank-4",
    registration: "Reg-Bank-4",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank5",
    createdAt: "2024-07-02T17:32:41.453Z",
    updatedAt: "2024-10-09T13:13:12.933Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 5",
    shortName: "Bank-5",
    registration: "Reg-Bank-5",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank6",
    createdAt: "2024-07-02T17:32:41.513Z",
    updatedAt: "2024-10-09T13:13:12.993Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 6",
    shortName: "Bank-6",
    registration: "Reg-Bank-6",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank7",
    createdAt: "2024-07-02T17:32:41.577Z",
    updatedAt: "2024-10-09T13:13:13.053Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 7",
    shortName: "Bank-7",
    registration: "Reg-Bank-7",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank8",
    createdAt: "2024-07-02T17:32:41.637Z",
    updatedAt: "2024-10-09T13:13:13.113Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 8",
    shortName: "Bank-8",
    registration: "Reg-Bank-8",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "bank9",
    createdAt: "2024-07-02T17:32:41.694Z",
    updatedAt: "2024-10-09T13:13:13.173Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank 9",
    shortName: "Bank-9",
    registration: "Reg-Bank-9",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "ckyd3by04145194vxl4z28e6ch",
    createdAt: "2022-01-13T14:52:17.764Z",
    updatedAt: "2022-01-13T14:52:17.766Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank Itau BBA SA",
    shortName: "Bank Itau",
    registration: "60.872.504/0001-23",
    private: false,
    addressId: "ckyd3by05145204vxly0n2tp9v",
    address: {
      id: "ckyd3by05145204vxly0n2tp9v",
      createdAt: "2022-01-13T14:52:17.765Z",
      updatedAt: "2022-01-13T14:52:17.765Z",
      deletedAt: null,
      line1: "Av. Brigadeiro Faria Lima 3.500 -1 - 2 - 3 (Parte)",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt924764vxlwb29j4yt",
      city: {
        id: "ckyc7bqt924764vxlwb29j4yt",
        createdAt: "2022-01-12T23:56:20.733Z",
        updatedAt: "2022-01-12T23:56:20.733Z",
        name: "SAO PAULO",
        countryId: "ckvrc3wuh0070sxyei2g80g88",
        postalCode: "4 E 5 Andares",
        country: {
          name: "Brazil",
        },
      },
    },
  },
  {
    id: "ckyd3by08145374vxlui7cqqqh",
    createdAt: "2022-01-13T14:52:17.768Z",
    updatedAt: "2022-01-13T14:52:17.769Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bank of China",
    shortName: "Bank of China",
    registration: "FC002851",
    private: false,
    addressId: "ckyd3by09145384vxl1klqpfre",
    address: {
      id: "ckyd3by09145384vxl1klqpfre",
      createdAt: "2022-01-13T14:52:17.769Z",
      updatedAt: "2022-01-13T14:52:17.768Z",
      deletedAt: null,
      line1: "1 Lothbury",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtd25004vxljqx60jvh",
      city: {
        id: "ckyc7bqtd25004vxljqx60jvh",
        createdAt: "2022-01-12T23:56:20.737Z",
        updatedAt: "2022-01-12T23:56:20.737Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC2R 7DB",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "cm5nmufne11080468zezryune75",
    createdAt: "2025-01-08T08:23:35.354Z",
    updatedAt: "2025-01-08T08:23:35.355Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque de Savoie",
    shortName: "Banque de Savoie",
    registration: "745 520 411",
    private: false,
    addressId: "cm5nmufne11080568zewaty0rst",
    address: {
      id: "cm5nmufne11080568zewaty0rst",
      createdAt: "2025-01-08T08:23:35.354Z",
      updatedAt: "2025-01-08T08:23:35.354Z",
      deletedAt: null,
      line1: "6 Boulevard du Théâtre",
      line2: null,
      line3: null,
      cityId: "cm5nmufne11080668zexlvqggpg",
      city: {
        id: "cm5nmufne11080668zexlvqggpg",
        createdAt: "2025-01-08T08:23:35.354Z",
        updatedAt: "2025-01-08T08:23:35.354Z",
        name: "Chambéry",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "73000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by05145234vxlw92xhdyi",
    createdAt: "2022-01-13T14:52:17.765Z",
    updatedAt: "2022-01-13T14:52:17.766Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Joh. Berenberg, Gossler & Co. KG",
    shortName: "Berenberg",
    registration: "42659",
    private: false,
    addressId: "ckyd3by05145244vxl8eh65586",
    address: {
      id: "ckyd3by05145244vxl8eh65586",
      createdAt: "2022-01-13T14:52:17.765Z",
      updatedAt: "2022-01-13T14:52:17.765Z",
      deletedAt: null,
      line1: "Neuer Jungfernstieg",
      line2: " Neustadt 20",
      line3: null,
      cityId: "ckyc7bqta24804vxllt55pr1l",
      city: {
        id: "ckyc7bqta24804vxllt55pr1l",
        createdAt: "2022-01-12T23:56:20.734Z",
        updatedAt: "2022-01-12T23:56:20.734Z",
        name: "HAMBURG",
        countryId: "ckvrc3wuo0131sxye2b1bk7z6",
        postalCode: "20354",
        country: {
          name: "Germany",
        },
      },
    },
  },
  {
    id: "ckyd3bxy9143874vxlub5xxyul",
    createdAt: "2022-01-13T14:52:17.697Z",
    updatedAt: "2025-01-07T08:58:57.797Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "BNP PARIBAS SA",
    shortName: "BNPP FR",
    registration: "662042449",
    private: false,
    addressId: "ckyd3bxy9143884vxljoguqw6q",
    address: {
      id: "ckyd3bxy9143884vxljoguqw6q",
      createdAt: "2022-01-13T14:52:17.697Z",
      updatedAt: "2022-01-13T14:52:17.697Z",
      deletedAt: null,
      line1: "16 Boulevard des Italiens",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsh22814vxlogldtfkp",
      city: {
        id: "ckyc7bqsh22814vxlogldtfkp",
        createdAt: "2022-01-12T23:56:20.705Z",
        updatedAt: "2022-01-12T23:56:20.705Z",
        name: "PARIS",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75009",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3bxy8143814vxlep5gdut2",
    createdAt: "2022-01-13T14:52:17.696Z",
    updatedAt: "2022-01-13T14:52:17.697Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "BNP PARIBAS (Suisse)",
    shortName: "BNPP SWITZ.",
    registration: "CHE-102.922.193",
    private: false,
    addressId: "ckyd3bxy8143824vxlrf7cw64h",
    address: {
      id: "ckyd3bxy8143824vxlrf7cw64h",
      createdAt: "2022-01-13T14:52:17.696Z",
      updatedAt: "2022-01-13T14:52:17.696Z",
      deletedAt: null,
      line1: "2 place de Hollande",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsi22854vxl5dth3n8d",
      city: {
        id: "ckyc7bqsi22854vxl5dth3n8d",
        createdAt: "2022-01-12T23:56:20.706Z",
        updatedAt: "2022-01-12T23:56:20.706Z",
        name: "GENEVE",
        countryId: "ckvrc3wuk0089sxyeksy6n0os",
        postalCode: "1204",
        country: {
          name: "Switzerland",
        },
      },
    },
  },
  {
    id: "ckyd3by0g145694vxlkl3wlmyf",
    createdAt: "2022-01-13T14:52:17.776Z",
    updatedAt: "2022-01-13T14:52:17.777Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "BANK OF AMERICA EUROPE DESIGNATED ACTIVITY COMPANY",
    shortName: "BoA ML",
    registration: "FC026157",
    private: false,
    addressId: "ckyd3by0g145704vxl0p2bli4q",
    address: {
      id: "ckyd3by0g145704vxl0p2bli4q",
      createdAt: "2022-01-13T14:52:17.776Z",
      updatedAt: "2022-01-13T14:52:17.776Z",
      deletedAt: null,
      line1: "2 King Edward Street",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtj25454vxluzvksr2w",
      city: {
        id: "ckyc7bqtj25454vxluzvksr2w",
        createdAt: "2022-01-12T23:56:20.743Z",
        updatedAt: "2022-01-12T23:56:20.743Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC1A 1HQ",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "cm5nmufng11081868zefbm5qen5",
    createdAt: "2025-01-08T08:23:35.356Z",
    updatedAt: "2025-01-08T08:23:35.357Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Aquitaine Centre Atlantique",
    shortName: "BPACA",
    registration: "347 607 939",
    private: false,
    addressId: "cm5nmufng11081968zeq8iplvci",
    address: {
      id: "cm5nmufng11081968zeq8iplvci",
      createdAt: "2025-01-08T08:23:35.356Z",
      updatedAt: "2025-01-08T08:23:35.356Z",
      deletedAt: null,
      line1: "10 Quai des Queyries",
      line2: null,
      line3: null,
      cityId: "cm5nmufng11082068zeowho6946",
      city: {
        id: "cm5nmufng11082068zeowho6946",
        createdAt: "2025-01-08T08:23:35.356Z",
        updatedAt: "2025-01-08T08:23:35.356Z",
        name: "Bordeaux",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "33072",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnf11081268ze92ram8kx",
    createdAt: "2025-01-08T08:23:35.355Z",
    updatedAt: "2025-01-08T08:23:35.356Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Alsace Lorraine Champagne",
    shortName: "BPALC",
    registration: "356 801 571",
    private: false,
    addressId: "cm5nmufnf11081368zee0bdnxqe",
    address: {
      id: "cm5nmufnf11081368zee0bdnxqe",
      createdAt: "2025-01-08T08:23:35.355Z",
      updatedAt: "2025-01-08T08:23:35.355Z",
      deletedAt: null,
      line1: "3 Rue François de Curel",
      line2: null,
      line3: null,
      cityId: "cm5nmufnf11081468zeve0uaxra",
      city: {
        id: "cm5nmufnf11081468zeve0uaxra",
        createdAt: "2025-01-08T08:23:35.355Z",
        updatedAt: "2025-01-08T08:23:35.355Z",
        name: "Metz",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "57000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnh11082368zejrbfz56y",
    createdAt: "2025-01-08T08:23:35.357Z",
    updatedAt: "2025-01-08T08:23:35.358Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Auvergne Rhône Alpes",
    shortName: "BPAURA",
    registration: "356 000 109",
    private: false,
    addressId: "cm5nmufnh11082468zecwm58k2q",
    address: {
      id: "cm5nmufnh11082468zecwm58k2q",
      createdAt: "2025-01-08T08:23:35.357Z",
      updatedAt: "2025-01-08T08:23:35.357Z",
      deletedAt: null,
      line1: "4 Boulevard Eugène Deruelle",
      line2: null,
      line3: null,
      cityId: "cm5nmufnh11082568zepk2casd5",
      city: {
        id: "cm5nmufnh11082568zepk2casd5",
        createdAt: "2025-01-08T08:23:35.357Z",
        updatedAt: "2025-01-08T08:23:35.357Z",
        name: "Lyon",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "69003",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnh11082968zeodsl075y",
    createdAt: "2025-01-08T08:23:35.357Z",
    updatedAt: "2025-01-08T08:23:35.358Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Bourgogne Franche-Comté",
    shortName: "BPBFC",
    registration: "542 820 352",
    private: false,
    addressId: "cm5nmufnh11083068zeik0vurz3",
    address: {
      id: "cm5nmufnh11083068zeik0vurz3",
      createdAt: "2025-01-08T08:23:35.357Z",
      updatedAt: "2025-01-08T08:23:35.357Z",
      deletedAt: null,
      line1: "14 Boulevard de la Trémouille",
      line2: null,
      line3: null,
      cityId: "cm5nmufnh11083168zeccyqjo05",
      city: {
        id: "cm5nmufnh11083168zeccyqjo05",
        createdAt: "2025-01-08T08:23:35.357Z",
        updatedAt: "2025-01-08T08:23:35.357Z",
        name: "Dijon",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "21000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnj11084468zeqfhuf9f6",
    createdAt: "2025-01-08T08:23:35.359Z",
    updatedAt: "2025-01-08T08:23:35.360Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Grand Ouest",
    shortName: "BPGO",
    registration: "857 500 227",
    private: false,
    addressId: "cm5nmufnj11084568ze0nx1rgn2",
    address: {
      id: "cm5nmufnj11084568ze0nx1rgn2",
      createdAt: "2025-01-08T08:23:35.359Z",
      updatedAt: "2025-01-08T08:23:35.359Z",
      deletedAt: null,
      line1: "15 Boulevard de la Boutière",
      line2: null,
      line3: null,
      cityId: "cm5nmufnj11084668zeh75lxiaj",
      city: {
        id: "cm5nmufnj11084668zeh75lxiaj",
        createdAt: "2025-01-08T08:23:35.359Z",
        updatedAt: "2025-01-08T08:23:35.359Z",
        name: "Saint-Grégoire",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "35768",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3bxy9143844vxloc8s4x1h",
    createdAt: "2022-01-13T14:52:17.697Z",
    updatedAt: "2022-01-13T14:52:17.698Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bpifrance Assurance Export",
    shortName: "BPI  Export",
    registration: "815276308",
    private: false,
    addressId: "ckyd3bxy9143854vxlqiuwlz39",
    address: {
      id: "ckyd3bxy9143854vxlqiuwlz39",
      createdAt: "2022-01-13T14:52:17.697Z",
      updatedAt: "2022-01-13T14:52:17.697Z",
      deletedAt: null,
      line1: "27-31 avenue du Général Leclerc",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsj22894vxl7vu7ou7b",
      city: {
        id: "ckyc7bqsj22894vxl7vu7ou7b",
        createdAt: "2022-01-12T23:56:20.707Z",
        updatedAt: "2022-01-12T23:56:20.707Z",
        name: "94700",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "MAISONS-ALFORT CEDEX",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cl1f6vma8000009l07txf3gsg",
    createdAt: "2022-03-31T18:00:00.000Z",
    updatedAt: "2022-03-31T18:00:00.000Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Bpifrance S.A.",
    shortName: "Bpifrance",
    registration: "320 252 489",
    private: false,
    addressId: "ckyd3bxy9143854vxlqiuwlz39",
    address: {
      id: "ckyd3bxy9143854vxlqiuwlz39",
      createdAt: "2022-01-13T14:52:17.697Z",
      updatedAt: "2022-01-13T14:52:17.697Z",
      deletedAt: null,
      line1: "27-31 avenue du Général Leclerc",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsj22894vxl7vu7ou7b",
      city: {
        id: "ckyc7bqsj22894vxl7vu7ou7b",
        createdAt: "2022-01-12T23:56:20.707Z",
        updatedAt: "2022-01-12T23:56:20.707Z",
        name: "94700",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "MAISONS-ALFORT CEDEX",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnk11084868zetirhb5hn",
    createdAt: "2025-01-08T08:23:35.360Z",
    updatedAt: "2025-01-08T08:23:35.361Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Méditerranée",
    shortName: "BPMED",
    registration: "058 801 481",
    private: false,
    addressId: "cm5nmufnk11084968zeocc2ijz1",
    address: {
      id: "cm5nmufnk11084968zeocc2ijz1",
      createdAt: "2025-01-08T08:23:35.360Z",
      updatedAt: "2025-01-08T08:23:35.360Z",
      deletedAt: null,
      line1: "457 Promenade des Anglais",
      line2: null,
      line3: null,
      cityId: "cm5nmufnk11085068ze6t1mim5g",
      city: {
        id: "cm5nmufnk11085068ze6t1mim5g",
        createdAt: "2025-01-08T08:23:35.360Z",
        updatedAt: "2025-01-08T08:23:35.360Z",
        name: "Nice",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "06200",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufni11083468ze5q3r5cg2",
    createdAt: "2025-01-08T08:23:35.358Z",
    updatedAt: "2025-01-08T08:23:35.359Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire du Nord",
    shortName: "BPN",
    registration: "457 506 566",
    private: false,
    addressId: "cm5nmufni11083568zetfq6q4fd",
    address: {
      id: "cm5nmufni11083568zetfq6q4fd",
      createdAt: "2025-01-08T08:23:35.358Z",
      updatedAt: "2025-01-08T08:23:35.358Z",
      deletedAt: null,
      line1: "847 Avenue de la République",
      line2: null,
      line3: null,
      cityId: "cm5nmufni11083668ze2ixkwu6m",
      city: {
        id: "cm5nmufni11083668ze2ixkwu6m",
        createdAt: "2025-01-08T08:23:35.358Z",
        updatedAt: "2025-01-08T08:23:35.358Z",
        name: "Marcq-en-Barœul",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "59700",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnk11085468zevy1ipad6",
    createdAt: "2025-01-08T08:23:35.360Z",
    updatedAt: "2025-01-08T08:23:35.361Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Occitane",
    shortName: "BPOC",
    registration: "560 801 300",
    private: false,
    addressId: "cm5nmufnk11085568ze1v6hzexg",
    address: {
      id: "cm5nmufnk11085568ze1v6hzexg",
      createdAt: "2025-01-08T08:23:35.360Z",
      updatedAt: "2025-01-08T08:23:35.360Z",
      deletedAt: null,
      line1: "33 Avenue Georges Pompidou",
      line2: null,
      line3: null,
      cityId: "cm5nmufnl11085668zezzex5zdf",
      city: {
        id: "cm5nmufnl11085668zezzex5zdf",
        createdAt: "2025-01-08T08:23:35.360Z",
        updatedAt: "2025-01-08T08:23:35.360Z",
        name: "Balma",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "31130",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnl11085968ze3u6b7z5s",
    createdAt: "2025-01-08T08:23:35.361Z",
    updatedAt: "2025-01-08T08:23:35.362Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Rives de Paris",
    shortName: "BPRI",
    registration: "552 002 313",
    private: false,
    addressId: "cm5nmufnl11086068zey6hlg5mf",
    address: {
      id: "cm5nmufnl11086068zey6hlg5mf",
      createdAt: "2025-01-08T08:23:35.361Z",
      updatedAt: "2025-01-08T08:23:35.361Z",
      deletedAt: null,
      line1: "80 Boulevard Auguste Blanqui",
      line2: null,
      line3: null,
      cityId: "cm5nmufnl11086168zer74b7rzd",
      city: {
        id: "cm5nmufnl11086168zer74b7rzd",
        createdAt: "2025-01-08T08:23:35.361Z",
        updatedAt: "2025-01-08T08:23:35.361Z",
        name: "Paris",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75013",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufni11084068zehx13wdv4",
    createdAt: "2025-01-08T08:23:35.358Z",
    updatedAt: "2025-01-08T08:23:35.359Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire du Sud",
    shortName: "BPS",
    registration: "554 200 808",
    private: false,
    addressId: "cm5nmufnj11084168zecrt67ugs",
    address: {
      id: "cm5nmufnj11084168zecrt67ugs",
      createdAt: "2025-01-08T08:23:35.359Z",
      updatedAt: "2025-01-08T08:23:35.359Z",
      deletedAt: null,
      line1: "38 Boulevard Georges Clémenceau",
      line2: null,
      line3: null,
      cityId: "cm5nmufnj11084268zedorhwpsy",
      city: {
        id: "cm5nmufnj11084268zedorhwpsy",
        createdAt: "2025-01-08T08:23:35.359Z",
        updatedAt: "2025-01-08T08:23:35.359Z",
        name: "Perpignan",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "66000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnm11086468ze34ccz9k7",
    createdAt: "2025-01-08T08:23:35.362Z",
    updatedAt: "2025-01-08T08:23:35.362Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Populaire Val de France",
    shortName: "BPVF",
    registration: "549 800 373",
    private: false,
    addressId: "cm5nmufnm11086568ze6ruky2gx",
    address: {
      id: "cm5nmufnm11086568ze6ruky2gx",
      createdAt: "2025-01-08T08:23:35.362Z",
      updatedAt: "2025-01-08T08:23:35.362Z",
      deletedAt: null,
      line1: "9 Avenue Newton",
      line2: null,
      line3: null,
      cityId: "cm5nmufnm11086668zeo3l6jjlv",
      city: {
        id: "cm5nmufnm11086668zeo3l6jjlv",
        createdAt: "2025-01-08T08:23:35.362Z",
        updatedAt: "2025-01-08T08:23:35.362Z",
        name: "Montigny-le-Bretonneux",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "78180",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnm11086968zec2lqfuqq",
    createdAt: "2025-01-08T08:23:35.362Z",
    updatedAt: "2025-01-08T08:23:35.363Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "BRED Banque Populaire",
    shortName: "BRED",
    registration: "552 091 795",
    private: false,
    addressId: "cm5nmufnm11087068zefa1z8ugy",
    address: {
      id: "cm5nmufnm11087068zefa1z8ugy",
      createdAt: "2025-01-08T08:23:35.362Z",
      updatedAt: "2025-01-08T08:23:35.362Z",
      deletedAt: null,
      line1: "18 Quai de la Rapée",
      line2: null,
      line3: null,
      cityId: "cm5nmufnm11087168zeoq3nou6s",
      city: {
        id: "cm5nmufnm11087168zeoq3nou6s",
        createdAt: "2025-01-08T08:23:35.362Z",
        updatedAt: "2025-01-08T08:23:35.362Z",
        name: "Paris",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75012",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by03145124vxl9eqfbh8t",
    createdAt: "2022-01-13T14:52:17.763Z",
    updatedAt: "2022-01-13T14:52:17.764Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "The Bank of Tokyo Mitsubishi UFJ, Ltd",
    shortName: "BTMU NYC",
    registration: "13-5611741",
    private: false,
    addressId: "ckyd3by03145134vxlb5tcvzoc",
    address: {
      id: "ckyd3by03145134vxlb5tcvzoc",
      createdAt: "2022-01-13T14:52:17.763Z",
      updatedAt: "2022-01-13T14:52:17.763Z",
      deletedAt: null,
      line1: "1251 Avenue of the Americas",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt724674vxlqby11zml",
      city: {
        id: "ckyc7bqt724674vxlqby11zml",
        createdAt: "2022-01-12T23:56:20.731Z",
        updatedAt: "2022-01-12T23:56:20.731Z",
        name: "NEW YORK",
        countryId: "ckvrc3z6o1162sxyekt2k45hw",
        postalCode: "NY 10020-1104",
        country: {
          name: "United States",
        },
      },
    },
  },
  {
    id: "ckyd3by03145154vxly5f6542q",
    createdAt: "2022-01-13T14:52:17.763Z",
    updatedAt: "2022-01-13T14:52:17.764Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "The Bank of Tokyo Mitsubishi UFJ, Ltd",
    shortName: "BTMU UK",
    registration: "FC007499",
    private: false,
    addressId: "ckyd3by03145164vxla7cizp7t",
    address: {
      id: "ckyd3by03145164vxla7cizp7t",
      createdAt: "2022-01-13T14:52:17.764Z",
      updatedAt: "2022-01-13T14:52:17.763Z",
      deletedAt: null,
      line1: "24 Lombard Street",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt824724vxl3z4l75zo",
      city: {
        id: "ckyc7bqt824724vxl3z4l75zo",
        createdAt: "2022-01-12T23:56:20.732Z",
        updatedAt: "2022-01-12T23:56:20.732Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC3V 9AJ",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3bxyo144194vxl6x9d0o63",
    createdAt: "2022-01-13T14:52:17.712Z",
    updatedAt: "2022-01-13T14:52:17.713Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Crédit Agricole CIB",
    shortName: "CACIB",
    registration: "304187701",
    private: false,
    addressId: "ckyd3bxyp144204vxlj54lbucf",
    address: {
      id: "ckyd3bxyp144204vxlj54lbucf",
      createdAt: "2022-01-13T14:52:17.713Z",
      updatedAt: "2022-01-13T14:52:17.713Z",
      deletedAt: null,
      line1: "12 place des Etats Unis -CS 70052",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsp23434vxl6f7rgzy9",
      city: {
        id: "ckyc7bqsp23434vxl6f7rgzy9",
        createdAt: "2022-01-12T23:56:20.714Z",
        updatedAt: "2022-01-12T23:56:20.714Z",
        name: "MONTROUGE CEDEX",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "92547",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cl9n0w1o2000209igbjj942hp",
    createdAt: "2022-10-24T19:00:00.000Z",
    updatedAt: "2022-10-24T19:00:00.000Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Crédit Agricole du Finistère",
    shortName: "CA FInistère",
    registration: "778134601",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "ckyd3bxyq144264vxl02pl7k3n",
    createdAt: "2022-01-13T14:52:17.714Z",
    updatedAt: "2022-01-13T14:52:17.715Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "CaixaBank SA",
    shortName: "CaixaBank",
    registration: "A08663619",
    private: false,
    addressId: "ckyd3bxyq144274vxlb87bdmo6",
    address: {
      id: "ckyd3bxyq144274vxlb87bdmo6",
      createdAt: "2022-01-13T14:52:17.714Z",
      updatedAt: "2022-01-13T14:52:17.714Z",
      deletedAt: null,
      line1: "2-4 calle Pintor Sorolla",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsr23544vxl6xpdseu5",
      city: {
        id: "ckyc7bqsr23544vxl6xpdseu5",
        createdAt: "2022-01-12T23:56:20.715Z",
        updatedAt: "2022-01-12T23:56:20.715Z",
        name: "VALENCE",
        countryId: "ckvrc3wuo0137sxyebh6b89a6",
        postalCode: "46002",
        country: {
          name: "Spain",
        },
      },
    },
  },
  {
    id: "cl9n0stay000109igcwuj4trw",
    createdAt: "2022-10-24T19:00:00.000Z",
    updatedAt: "2022-10-24T19:00:00.000Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Crédit Agricole Normandie Seine",
    shortName: "CA Normandie Seine",
    registration: "433786738",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "cm5nmufnv11094468zevkrckv9z",
    createdAt: "2025-01-08T08:23:35.371Z",
    updatedAt: "2025-01-08T08:23:35.372Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CASDEN Banque Populaire",
    shortName: "CASDEN",
    registration: "784 275 778",
    private: false,
    addressId: "cm5nmufnv11094568zenev51iuw",
    address: {
      id: "cm5nmufnv11094568zenev51iuw",
      createdAt: "2025-01-08T08:23:35.371Z",
      updatedAt: "2025-01-08T08:23:35.371Z",
      deletedAt: null,
      line1: "1bis rue Jean WIENER",
      line2: null,
      line3: null,
      cityId: "cm5nmufnv11094668ze87d9n6va",
      city: {
        id: "cm5nmufnv11094668ze87d9n6va",
        createdAt: "2025-01-08T08:23:35.371Z",
        updatedAt: "2025-01-08T08:23:35.371Z",
        name: "Champs-sur-Marne",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "77420",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by0j145884vxl0u9n3law",
    createdAt: "2022-01-13T14:52:17.779Z",
    updatedAt: "2022-01-13T14:52:17.780Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Commonwealth Bank of Australia, Ltd",
    shortName: "CBA UK",
    registration: "FC016009",
    private: false,
    addressId: "ckyd3by0j145894vxldmsskxdr",
    address: {
      id: "ckyd3by0j145894vxldmsskxdr",
      createdAt: "2022-01-13T14:52:17.779Z",
      updatedAt: "2022-01-13T14:52:17.779Z",
      deletedAt: null,
      line1: "1 New Ludgate 60 Ludgate Hill",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtm25694vxlqx8q84kx",
      city: {
        id: "ckyc7bqtm25694vxlqx8q84kx",
        createdAt: "2022-01-12T23:56:20.746Z",
        updatedAt: "2022-01-12T23:56:20.746Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC4M 7AW",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "cm5nmufnw11095268zeyt2lq8c8",
    createdAt: "2025-01-08T08:23:35.372Z",
    updatedAt: "2025-01-08T08:23:35.373Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Crédit Coopératif",
    shortName: "CCOOP",
    registration: "349 974 931",
    private: false,
    addressId: "cm5nmufnw11095368ze9dg5uu48",
    address: {
      id: "cm5nmufnw11095368ze9dg5uu48",
      createdAt: "2025-01-08T08:23:35.372Z",
      updatedAt: "2025-01-08T08:23:35.372Z",
      deletedAt: null,
      line1: "12 Boulevard de Pesaro",
      line2: " CS 10002",
      line3: null,
      cityId: "cm5nmufnw11095468zezkqva9m5",
      city: {
        id: "cm5nmufnw11095468zezkqva9m5",
        createdAt: "2025-01-08T08:23:35.372Z",
        updatedAt: "2025-01-08T08:23:35.372Z",
        name: "Nanterre",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "92000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by0p146184vxl2iy7mo6o",
    createdAt: "2022-01-13T14:52:17.785Z",
    updatedAt: "2022-01-13T14:52:17.786Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "China Development Bank",
    shortName: "CDB",
    registration: "",
    private: false,
    addressId: "ckyd3by0p146194vxlwy1emm6t",
    address: {
      id: "ckyd3by0p146194vxlwy1emm6t",
      createdAt: "2022-01-13T14:52:17.785Z",
      updatedAt: "2022-01-13T14:52:17.785Z",
      deletedAt: null,
      line1: "No.18 Fuxingmennei Street",
      line2: null,
      line3: null,
      cityId: "ckyc7bqts26144vxlp0knvbx1",
      city: {
        id: "ckyc7bqts26144vxlp0knvbx1",
        createdAt: "2022-01-12T23:56:20.752Z",
        updatedAt: "2022-01-12T23:56:20.752Z",
        name: "BEIJING",
        countryId: "ckvrc3wul0095sxyeqtowhdj5",
        postalCode: "Xicheng District",
        country: {
          name: "China",
        },
      },
    },
  },
  {
    id: "cm5nmufnn11087568zen8gg9uwc",
    createdAt: "2025-01-08T08:23:35.363Z",
    updatedAt: "2025-01-08T08:23:35.364Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Aquitaine Poitou-Charentes",
    shortName: "CEAPC",
    registration: "353 821 028",
    private: false,
    addressId: "cm5nmufnn11087668zeb6bve42d",
    address: {
      id: "cm5nmufnn11087668zeb6bve42d",
      createdAt: "2025-01-08T08:23:35.363Z",
      updatedAt: "2025-01-08T08:23:35.363Z",
      deletedAt: null,
      line1: "1 parvis Corto Maltese – CS 31271",
      line2: null,
      line3: null,
      cityId: "cm5nmufnn11087768zeb4lgmlgb",
      city: {
        id: "cm5nmufnn11087768zeb4lgmlgb",
        createdAt: "2025-01-08T08:23:35.363Z",
        updatedAt: "2025-01-08T08:23:35.363Z",
        name: "Bordeaux cedex",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "33076",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufno11088568ze3bhd2bxd",
    createdAt: "2025-01-08T08:23:35.364Z",
    updatedAt: "2025-01-08T08:23:35.365Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne de Bourgogne Franche-Comté",
    shortName: "CEBFC",
    registration: "352 483 341",
    private: false,
    addressId: "cm5nmufno11088668zevzr20qck",
    address: {
      id: "cm5nmufno11088668zevzr20qck",
      createdAt: "2025-01-08T08:23:35.364Z",
      updatedAt: "2025-01-08T08:23:35.364Z",
      deletedAt: null,
      line1: "18 Avenue Françoise Giroud",
      line2: null,
      line3: null,
      cityId: "cm5nmufno11088768ze4l7qoy2t",
      city: {
        id: "cm5nmufno11088768ze4l7qoy2t",
        createdAt: "2025-01-08T08:23:35.364Z",
        updatedAt: "2025-01-08T08:23:35.364Z",
        name: "Dijon",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "21000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnp11089068ze3djtpnd8",
    createdAt: "2025-01-08T08:23:35.365Z",
    updatedAt: "2025-01-08T08:23:35.366Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Bretagne Pays de Loire",
    shortName: "CEBPL",
    registration: "392 640 090",
    private: false,
    addressId: "cm5nmufnp11089168zehy4vutcg",
    address: {
      id: "cm5nmufnp11089168zehy4vutcg",
      createdAt: "2025-01-08T08:23:35.365Z",
      updatedAt: "2025-01-08T08:23:35.365Z",
      deletedAt: null,
      line1: "2",
      line2: " Place Graslin – CS 10305",
      line3: null,
      cityId: "cm5nmufnp11089268ze6vzil3mc",
      city: {
        id: "cm5nmufnp11089268ze6vzil3mc",
        createdAt: "2025-01-08T08:23:35.365Z",
        updatedAt: "2025-01-08T08:23:35.365Z",
        name: "NANTES Cedex 1.",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "44003",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cl9n0xztr000309ig8q096pxo",
    createdAt: "2022-10-24T19:00:00.000Z",
    updatedAt: "2022-10-24T19:00:00.000Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Caisse d'Epargne Côte d'azur",
    shortName: "CECAZ",
    registration: "384402871",
    private: false,
    addressId: null,
    address: null,
  },
  {
    id: "cm5nmufnp11089668zehkg3kiz3",
    createdAt: "2025-01-08T08:23:35.365Z",
    updatedAt: "2025-01-08T08:23:35.366Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Côte d’Azur",
    shortName: "CECAZ",
    registration: "384 402 871",
    private: false,
    addressId: "cm5nmufnp11089768zervqsni0f",
    address: {
      id: "cm5nmufnp11089768zervqsni0f",
      createdAt: "2025-01-08T08:23:35.365Z",
      updatedAt: "2025-01-08T08:23:35.365Z",
      deletedAt: null,
      line1: "455 Promenade des Anglais",
      line2: null,
      line3: null,
      cityId: "cm5nmufnp11089868zejxdhwlm7",
      city: {
        id: "cm5nmufnp11089868zejxdhwlm7",
        createdAt: "2025-01-08T08:23:35.365Z",
        updatedAt: "2025-01-08T08:23:35.365Z",
        name: "Nice",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "06200",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnq11090068zeemkc8e56",
    createdAt: "2025-01-08T08:23:35.366Z",
    updatedAt: "2025-01-08T08:23:35.367Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Grand Est Europe",
    shortName: "CEGEE",
    registration: "775 618 622",
    private: false,
    addressId: "cm5nmufnq11090168zenzf1xmo6",
    address: {
      id: "cm5nmufnq11090168zenzf1xmo6",
      createdAt: "2025-01-08T08:23:35.366Z",
      updatedAt: "2025-01-08T08:23:35.366Z",
      deletedAt: null,
      line1: "1 Avenue du Rhin",
      line2: null,
      line3: null,
      cityId: "cm5nmufnq11090268zetamdoeb1",
      city: {
        id: "cm5nmufnq11090268zetamdoeb1",
        createdAt: "2025-01-08T08:23:35.366Z",
        updatedAt: "2025-01-08T08:23:35.366Z",
        name: "Strasbourg",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "67000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnq11090568zegxfq7zy5",
    createdAt: "2025-01-08T08:23:35.366Z",
    updatedAt: "2025-01-08T08:23:35.367Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Hauts de France",
    shortName: "CEHDF",
    registration: "383 000 692",
    private: false,
    addressId: "cm5nmufnq11090668zeuwycf92k",
    address: {
      id: "cm5nmufnq11090668zeuwycf92k",
      createdAt: "2025-01-08T08:23:35.366Z",
      updatedAt: "2025-01-08T08:23:35.366Z",
      deletedAt: null,
      line1: "612 rue de la Chaude Rivière",
      line2: null,
      line3: null,
      cityId: "cm5nmufnq11090768ze76qr91di",
      city: {
        id: "cm5nmufnq11090768ze76qr91di",
        createdAt: "2025-01-08T08:23:35.366Z",
        updatedAt: "2025-01-08T08:23:35.366Z",
        name: "Lille",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "59800",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnr11090968zerl8bd5v3",
    createdAt: "2025-01-08T08:23:35.367Z",
    updatedAt: "2025-01-08T08:23:35.368Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Ile-de-France",
    shortName: "CEIDF",
    registration: "382 900 942",
    private: false,
    addressId: "cm5nmufnr11091068zer2y5r5aq",
    address: {
      id: "cm5nmufnr11091068zer2y5r5aq",
      createdAt: "2025-01-08T08:23:35.367Z",
      updatedAt: "2025-01-08T08:23:35.367Z",
      deletedAt: null,
      line1: "19 Rue du Louvre",
      line2: null,
      line3: null,
      cityId: "cm5nmufnr11091168zeqc0l6cpq",
      city: {
        id: "cm5nmufnr11091168zeqc0l6cpq",
        createdAt: "2025-01-08T08:23:35.367Z",
        updatedAt: "2025-01-08T08:23:35.367Z",
        name: "Paris",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75001",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnt11092568zeynct6nfw",
    createdAt: "2025-01-08T08:23:35.369Z",
    updatedAt: "2025-01-08T08:23:35.370Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Loire-Centre",
    shortName: "CELC",
    registration: "383 952 470",
    private: false,
    addressId: "cm5nmufnt11092668zemzlb6pet",
    address: {
      id: "cm5nmufnt11092668zemzlb6pet",
      createdAt: "2025-01-08T08:23:35.369Z",
      updatedAt: "2025-01-08T08:23:35.369Z",
      deletedAt: null,
      line1: "7 Rue d'Escures",
      line2: null,
      line3: null,
      cityId: "cm5nmufnt11092768zehi5ljb6k",
      city: {
        id: "cm5nmufnt11092768zehi5ljb6k",
        createdAt: "2025-01-08T08:23:35.369Z",
        updatedAt: "2025-01-08T08:23:35.369Z",
        name: "Orléans",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "45000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufns11092068zef0k3scig",
    createdAt: "2025-01-08T08:23:35.368Z",
    updatedAt: "2025-01-08T08:23:35.369Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Loire Drôme Ardèche",
    shortName: "CELDA",
    registration: "383 686 839",
    private: false,
    addressId: "cm5nmufns11092168zeugngn4fa",
    address: {
      id: "cm5nmufns11092168zeugngn4fa",
      createdAt: "2025-01-08T08:23:35.368Z",
      updatedAt: "2025-01-08T08:23:35.368Z",
      deletedAt: null,
      line1: "17 Rue Pierre et Dominique Ponchardier",
      line2: null,
      line3: null,
      cityId: "cm5nmufns11092268zex5wihghc",
      city: {
        id: "cm5nmufns11092268zex5wihghc",
        createdAt: "2025-01-08T08:23:35.368Z",
        updatedAt: "2025-01-08T08:23:35.368Z",
        name: "Saint-Étienne",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "42100",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnr11091568ze61yr2dtp",
    createdAt: "2025-01-08T08:23:35.367Z",
    updatedAt: "2025-01-08T08:23:35.368Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Languedoc-Roussillon",
    shortName: "CELR",
    registration: "383 451 267",
    private: false,
    addressId: "cm5nmufnr11091668ze6hwoegvb",
    address: {
      id: "cm5nmufnr11091668ze6hwoegvb",
      createdAt: "2025-01-08T08:23:35.367Z",
      updatedAt: "2025-01-08T08:23:35.367Z",
      deletedAt: null,
      line1: "254 Rue Michel Teule",
      line2: null,
      line3: null,
      cityId: "cm5nmufns11091768zeg0mes9pw",
      city: {
        id: "cm5nmufns11091768zeg0mes9pw",
        createdAt: "2025-01-08T08:23:35.368Z",
        updatedAt: "2025-01-08T08:23:35.368Z",
        name: "Montpellier",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "34000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnt11093068zeuo6plytn",
    createdAt: "2025-01-08T08:23:35.369Z",
    updatedAt: "2025-01-08T08:23:35.370Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne de Midi-Pyrénées",
    shortName: "CEMP",
    registration: "383 354 594",
    private: false,
    addressId: "cm5nmufnt11093168ze7dda1ffr",
    address: {
      id: "cm5nmufnt11093168ze7dda1ffr",
      createdAt: "2025-01-08T08:23:35.369Z",
      updatedAt: "2025-01-08T08:23:35.369Z",
      deletedAt: null,
      line1: "10 Avenue James Clerk Maxwell",
      line2: null,
      line3: null,
      cityId: "cm5nmufnt11093268zej6wt1lao",
      city: {
        id: "cm5nmufnt11093268zej6wt1lao",
        createdAt: "2025-01-08T08:23:35.369Z",
        updatedAt: "2025-01-08T08:23:35.369Z",
        name: "Toulouse",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "31100",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnu11093468zeg534y4hy",
    createdAt: "2025-01-08T08:23:35.370Z",
    updatedAt: "2025-01-08T08:23:35.371Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Normandie",
    shortName: "CEN",
    registration: "384 353 413",
    private: false,
    addressId: "cm5nmufnu11093568zevbrqtv98",
    address: {
      id: "cm5nmufnu11093568zevbrqtv98",
      createdAt: "2025-01-08T08:23:35.370Z",
      updatedAt: "2025-01-08T08:23:35.370Z",
      deletedAt: null,
      line1: "151 Rue d'Uelzen",
      line2: null,
      line3: null,
      cityId: "cm5nmufnu11093668ze32t5p2lu",
      city: {
        id: "cm5nmufnu11093668ze32t5p2lu",
        createdAt: "2025-01-08T08:23:35.370Z",
        updatedAt: "2025-01-08T08:23:35.370Z",
        name: "Bois-Guillaume",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "76230",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnw11094868ze3b2fp4kv",
    createdAt: "2025-01-08T08:23:35.372Z",
    updatedAt: "2025-01-08T08:23:35.373Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CE CEPAC",
    shortName: "CEPAC",
    registration: "775 559 404",
    private: false,
    addressId: "cm5nmufnw11094968zeewn5pdii",
    address: {
      id: "cm5nmufnw11094968zeewn5pdii",
      createdAt: "2025-01-08T08:23:35.372Z",
      updatedAt: "2025-01-08T08:23:35.372Z",
      deletedAt: null,
      line1: "Place Estrangin Pastré",
      line2: null,
      line3: null,
      cityId: "cm5nmufnw11095068zerz77exvr",
      city: {
        id: "cm5nmufnw11095068zerz77exvr",
        createdAt: "2025-01-08T08:23:35.372Z",
        updatedAt: "2025-01-08T08:23:35.372Z",
        name: "Marseille",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "13006",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnn11088068ze3iulv2r2",
    createdAt: "2025-01-08T08:23:35.363Z",
    updatedAt: "2025-01-08T08:23:35.364Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d'Epargne Auvergne Limousin",
    shortName: "CEPAL",
    registration: "382 742 013",
    private: false,
    addressId: "cm5nmufnn11088168zefiqiq08z",
    address: {
      id: "cm5nmufnn11088168zefiqiq08z",
      createdAt: "2025-01-08T08:23:35.363Z",
      updatedAt: "2025-01-08T08:23:35.363Z",
      deletedAt: null,
      line1: "63 Boulevard François Mitterrand",
      line2: null,
      line3: null,
      cityId: "cm5nmufnn11088268ze0rh3tvh7",
      city: {
        id: "cm5nmufnn11088268ze0rh3tvh7",
        createdAt: "2025-01-08T08:23:35.363Z",
        updatedAt: "2025-01-08T08:23:35.363Z",
        name: "Clermont-Ferrand",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "63000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cm5nmufnu11093968zel77kfiuy",
    createdAt: "2025-01-08T08:23:35.370Z",
    updatedAt: "2025-01-08T08:23:35.371Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Caisse d’Epargne Rhône Alpes",
    shortName: "CERA",
    registration: "384 006 029",
    private: false,
    addressId: "cm5nmufnu11094068ze27wsg8iq",
    address: {
      id: "cm5nmufnu11094068ze27wsg8iq",
      createdAt: "2025-01-08T08:23:35.370Z",
      updatedAt: "2025-01-08T08:23:35.370Z",
      deletedAt: null,
      line1: "116 Cours Lafayette",
      line2: null,
      line3: null,
      cityId: "cm5nmufnv11094168zek6si56fk",
      city: {
        id: "cm5nmufnv11094168zek6si56fk",
        createdAt: "2025-01-08T08:23:35.371Z",
        updatedAt: "2025-01-08T08:23:35.371Z",
        name: "Lyon",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "69003",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by0n146084vxlav4lpc5w",
    createdAt: "2022-01-13T14:52:17.783Z",
    updatedAt: "2022-01-13T14:52:17.784Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "China EXIM",
    shortName: "CEXIM",
    registration: "914403001017814000",
    private: false,
    addressId: "ckyd3by0n146094vxlm9zwxb73",
    address: {
      id: "ckyd3by0n146094vxlm9zwxb73",
      createdAt: "2022-01-13T14:52:17.783Z",
      updatedAt: "2022-01-13T14:52:17.783Z",
      deletedAt: null,
      line1: "No.30",
      line2: " FuXingMenNei Street",
      line3: " XiCheng District",
      cityId: "ckyc7bqtq26014vxlzq3rx89d",
      city: {
        id: "ckyc7bqtq26014vxlzq3rx89d",
        createdAt: "2022-01-12T23:56:20.750Z",
        updatedAt: "2022-01-12T23:56:20.750Z",
        name: "BEIJING",
        countryId: "ckvrc3wul0095sxyeqtowhdj5",
        postalCode: "100031",
        country: {
          name: "China",
        },
      },
    },
  },
  {
    id: "cmabfvvu662831887xgnfazgtgx",
    createdAt: "2025-05-05T18:54:03.246Z",
    updatedAt: "2025-05-05T18:54:03.247Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CIC Est",
    shortName: "CIC Est",
    registration: "75480071203230",
    private: false,
    addressId: "cmabfvvu662831987xgc6orblvk",
    address: {
      id: "cmabfvvu662831987xgc6orblvk",
      createdAt: "2025-05-05T18:54:03.246Z",
      updatedAt: "2025-05-05T18:54:03.246Z",
      deletedAt: null,
      line1: "31 RUE JEAN WENGER-VALENTIN",
      line2: null,
      line3: null,
      cityId: "cmabfvvu662832087xg4rlusnqx",
      city: {
        id: "cmabfvvu662832087xg4rlusnqx",
        createdAt: "2025-05-05T18:54:03.246Z",
        updatedAt: "2025-05-05T18:54:03.246Z",
        name: "STRASBOURG",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "67000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3bxya143914vxluxnkw6he",
    createdAt: "2022-01-13T14:52:17.698Z",
    updatedAt: "2025-01-07T08:58:57.797Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Crédit Industriel et Commercial",
    shortName: "CIC FR",
    registration: "542016381",
    private: false,
    addressId: "ckyd3bxya143924vxlmyiwvh5v",
    address: {
      id: "ckyd3bxya143924vxlmyiwvh5v",
      createdAt: "2022-01-13T14:52:17.698Z",
      updatedAt: "2022-01-13T14:52:17.698Z",
      deletedAt: null,
      line1: "6 avenue de Provence",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsh22814vxlogldtfkp",
      city: {
        id: "ckyc7bqsh22814vxlogldtfkp",
        createdAt: "2022-01-12T23:56:20.705Z",
        updatedAt: "2022-01-12T23:56:20.705Z",
        name: "PARIS",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75009",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cmabfvvu862832887xgnm4mtnqf",
    createdAt: "2025-05-05T18:54:03.248Z",
    updatedAt: "2025-05-05T18:54:03.249Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CIC Lyonnaise de Banque",
    shortName: "CIC Lyonnaise de Banque",
    registration: "95450797600015",
    private: false,
    addressId: "cmabfvvu862832987xgx3gzbi01",
    address: {
      id: "cmabfvvu862832987xgx3gzbi01",
      createdAt: "2025-05-05T18:54:03.248Z",
      updatedAt: "2025-05-05T18:54:03.248Z",
      deletedAt: null,
      line1: "8 RUE DE LA REPUBLIQUE",
      line2: null,
      line3: null,
      cityId: "cmabfvvu862833087xgt6mwfdpp",
      city: {
        id: "cmabfvvu862833087xgt6mwfdpp",
        createdAt: "2025-05-05T18:54:03.248Z",
        updatedAt: "2025-05-05T18:54:03.248Z",
        name: "LYON",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "69001",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cmabfvvu762832487xgslfn62zp",
    createdAt: "2025-05-05T18:54:03.247Z",
    updatedAt: "2025-05-05T18:54:03.248Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CIC Nord Ouest",
    shortName: "CIC Nord Ouest",
    registration: "45550209602250",
    private: false,
    addressId: "cmabfvvu762832587xgx2pugi33",
    address: {
      id: "cmabfvvu762832587xgx2pugi33",
      createdAt: "2025-05-05T18:54:03.247Z",
      updatedAt: "2025-05-05T18:54:03.247Z",
      deletedAt: null,
      line1: "33 AVENUE LE CORBUSIER",
      line2: null,
      line3: null,
      cityId: "cl5ilmote647614vwrr86lgqy0",
      city: {
        id: "cl5ilmote647614vwrr86lgqy0",
        createdAt: "2022-07-12T20:01:19.394Z",
        updatedAt: "2022-07-12T20:01:19.394Z",
        name: "LILLE",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "59800",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cl5ilmote647594vwr0z533zhl",
    createdAt: "2022-07-12T20:01:19.394Z",
    updatedAt: "2022-07-12T20:01:19.395Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "BANQUE CIC NORD OUEST",
    shortName: "CIC NORD OUEST",
    registration: "455502096",
    private: false,
    addressId: "cl5ilmote647604vwruugm9077",
    address: {
      id: "cl5ilmote647604vwruugm9077",
      createdAt: "2022-07-12T20:01:19.394Z",
      updatedAt: "2022-07-12T20:01:19.394Z",
      deletedAt: null,
      line1: "33 AV LE CORBUSIER",
      line2: null,
      line3: null,
      cityId: "cl5ilmote647614vwrr86lgqy0",
      city: {
        id: "cl5ilmote647614vwrr86lgqy0",
        createdAt: "2022-07-12T20:01:19.394Z",
        updatedAt: "2022-07-12T20:01:19.394Z",
        name: "LILLE",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "59800",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cmabfvvu962833787xgdl4z3c8a",
    createdAt: "2025-05-05T18:54:03.249Z",
    updatedAt: "2025-05-05T18:54:03.250Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CIC Ouest",
    shortName: "CIC Ouest",
    registration: "FR85580107202664",
    private: false,
    addressId: "cmabfvvu962833887xg6zwyzm6u",
    address: {
      id: "cmabfvvu962833887xg6zwyzm6u",
      createdAt: "2025-05-05T18:54:03.249Z",
      updatedAt: "2025-05-05T18:54:03.249Z",
      deletedAt: null,
      line1: "2 AVENUE JEAN CLAUDE BONDUELLE",
      line2: null,
      line3: null,
      cityId: "cmabfvvu962833987xgtia8l091",
      city: {
        id: "cmabfvvu962833987xgtia8l091",
        createdAt: "2025-05-05T18:54:03.249Z",
        updatedAt: "2025-05-05T18:54:03.249Z",
        name: "NANTES",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "44000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "cmabfvvu862833387xg3kyplrxd",
    createdAt: "2025-05-05T18:54:03.248Z",
    updatedAt: "2025-05-05T18:54:03.249Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CIC Sud Ouest",
    shortName: "CIC Sud Ouest",
    registration: "45620480901281",
    private: false,
    addressId: "cmabfvvu862833487xgv8ts3er5",
    address: {
      id: "cmabfvvu862833487xgv8ts3er5",
      createdAt: "2025-05-05T18:54:03.248Z",
      updatedAt: "2025-05-05T18:54:03.248Z",
      deletedAt: null,
      line1: "20 QUAI DES CHARTRONS",
      line2: null,
      line3: null,
      cityId: "cmabfvvu962833587xgku7t6tnf",
      city: {
        id: "cmabfvvu962833587xgku7t6tnf",
        createdAt: "2025-05-05T18:54:03.249Z",
        updatedAt: "2025-05-05T18:54:03.249Z",
        name: "BORDEAUX",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "33058",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by0b145514vxlcu7erppa",
    createdAt: "2022-01-13T14:52:17.771Z",
    updatedAt: "2022-01-13T14:52:17.772Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Citibank, N.a",
    shortName: "Citi NYC",
    registration: "13-5266470",
    private: false,
    addressId: "ckyd3by0b145524vxlg355dtw5",
    address: {
      id: "ckyd3by0b145524vxlg355dtw5",
      createdAt: "2022-01-13T14:52:17.771Z",
      updatedAt: "2022-01-13T14:52:17.771Z",
      deletedAt: null,
      line1: "388 Greenwich Street",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtf25204vxlpeoxgsig",
      city: {
        id: "ckyc7bqtf25204vxlpeoxgsig",
        createdAt: "2022-01-12T23:56:20.739Z",
        updatedAt: "2022-01-12T23:56:20.739Z",
        name: "NEW YORK",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "NY 10013",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3by0b145484vxlgjwrt22q",
    createdAt: "2022-01-13T14:52:17.770Z",
    updatedAt: "2022-01-13T14:52:17.771Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "CITIBANK EUROPE PLC, UK BRANCH",
    shortName: "Citi UK",
    registration: "FC032763",
    private: false,
    addressId: "ckyd3by0b145494vxlv2hjbabk",
    address: {
      id: "ckyd3by0b145494vxlv2hjbabk",
      createdAt: "2022-01-13T14:52:17.771Z",
      updatedAt: "2022-01-13T14:52:17.771Z",
      deletedAt: null,
      line1: "Citigroup Centre 33 Canada Square Canary Wharf",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtf25154vxl2byihty2",
      city: {
        id: "ckyc7bqtf25154vxl2byihty2",
        createdAt: "2022-01-12T23:56:20.739Z",
        updatedAt: "2022-01-12T23:56:20.739Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "E14 5LB",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "cm02blln70000xy3dp25c5hnl",
    createdAt: "2024-08-20T11:07:06.065Z",
    updatedAt: "2024-08-20T11:11:28.645Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "CAISSE REGIONALE DE CREDIT AGRICOLE MUTUEL DES COTES D’ARMOR",
    shortName: "Crédit Agricole Côtes d'Armor",
    registration: "77745617900020",
    private: false,
    addressId: "cm02bomif00001i3d4eaeg94i",
    address: {
      id: "cm02bomif00001i3d4eaeg94i",
      createdAt: "2024-08-20T11:09:27.159Z",
      updatedAt: "2024-08-20T11:11:28.269Z",
      deletedAt: null,
      line1: "LA CROIX TUAL",
      line2: null,
      line3: null,
      cityId: "cm02bnoju00000f3d2c517ga2",
      city: {
        id: "cm02bnoju00000f3d2c517ga2",
        createdAt: "2024-08-20T11:08:43.146Z",
        updatedAt: "2024-08-20T11:08:43.145Z",
        name: "PLOUFRAGAN ",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "22440",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "clbgjx0rd7022754uyd11buphcs",
    createdAt: "2022-12-09T13:36:03.865Z",
    updatedAt: "2022-12-09T13:36:03.868Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "CAISSE REGIONALE DE CREDIT AGRICOLE MUTUEL DE NORMANDIE-SEINE",
    shortName: "Crédit agricole de Normandie-Seine",
    registration: "433786738",
    private: false,
    addressId: "clbgjx0rd7022764uydoufdmee5",
    address: {
      id: "clbgjx0rd7022764uydoufdmee5",
      createdAt: "2022-12-09T13:36:03.865Z",
      updatedAt: "2022-12-09T13:36:03.865Z",
      deletedAt: null,
      line1: "CHE DE LA BRETEQUE",
      line2: null,
      line3: null,
      cityId: "clbgjx0rd7022774uydzx43ubxb",
      city: {
        id: "clbgjx0rd7022774uydzx43ubxb",
        createdAt: "2022-12-09T13:36:03.865Z",
        updatedAt: "2022-12-09T13:36:03.865Z",
        name: "BOIS-GUILLAUME",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "76230",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "clbgjx0rg7022804uydy7sqaz5z",
    createdAt: "2022-12-09T13:36:03.868Z",
    updatedAt: "2022-12-09T13:36:03.870Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "CAISSE REGIONALE DE CREDIT AGRICOLE MUTUEL DU FINISTERE",
    shortName: "Crédit agricole du Finistère",
    registration: "778134601",
    private: false,
    addressId: "clbgjx0rg7022814uydd2yl8zyu",
    address: {
      id: "clbgjx0rg7022814uydd2yl8zyu",
      createdAt: "2022-12-09T13:36:03.868Z",
      updatedAt: "2022-12-09T13:36:03.868Z",
      deletedAt: null,
      line1: "7 ROUTE DU LOCH",
      line2: null,
      line3: null,
      cityId: "clbgjx0rg7022824uyd9d84a53g",
      city: {
        id: "clbgjx0rg7022824uyd9d84a53g",
        createdAt: "2022-12-09T13:36:03.868Z",
        updatedAt: "2022-12-09T13:36:03.868Z",
        name: "QUIMPER",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "29000",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by0a145454vxlow4gz5a1",
    createdAt: "2022-01-13T14:52:17.770Z",
    updatedAt: "2022-01-13T14:52:17.771Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Crédit Suisse",
    shortName: "Crédit Suisse SWITZ.",
    registration: "CHE-106.831.974",
    private: false,
    addressId: "ckyd3by0a145464vxlianid589",
    address: {
      id: "ckyd3by0a145464vxlianid589",
      createdAt: "2022-01-13T14:52:17.770Z",
      updatedAt: "2022-01-13T14:52:17.770Z",
      deletedAt: null,
      line1: "Paradeplatz 8",
      line2: null,
      line3: null,
      cityId: "ckyc7bqte25114vxlc9xkr2g6",
      city: {
        id: "ckyc7bqte25114vxlc9xkr2g6",
        createdAt: "2022-01-12T23:56:20.738Z",
        updatedAt: "2022-01-12T23:56:20.738Z",
        name: "ZURICH",
        countryId: "ckvrc3wuk0089sxyeksy6n0os",
        postalCode: "8001",
        country: {
          name: "Switzerland",
        },
      },
    },
  },
  {
    id: "ckyd3by0e145634vxlhikjif18",
    createdAt: "2022-01-13T14:52:17.774Z",
    updatedAt: "2022-01-13T14:52:17.775Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Danske Bank",
    shortName: "Danske bank",
    registration: "61126228",
    private: false,
    addressId: "ckyd3by0e145644vxl7ccqoemf",
    address: {
      id: "ckyd3by0e145644vxl7ccqoemf",
      createdAt: "2022-01-13T14:52:17.774Z",
      updatedAt: "2022-01-13T14:52:17.774Z",
      deletedAt: null,
      line1: "Holmens Kanal 2-12",
      line2: null,
      line3: null,
      cityId: "ckyc7bqth25344vxlgbs56knu",
      city: {
        id: "ckyc7bqth25344vxlgbs56knu",
        createdAt: "2022-01-12T23:56:20.741Z",
        updatedAt: "2022-01-12T23:56:20.741Z",
        name: "COPENHAGUE",
        countryId: "ckvrc3wun0118sxyeimqarvvi",
        postalCode: "1092",
        country: {
          name: "Denmark",
        },
      },
    },
  },
  {
    id: "ckyd3by07145304vxl5b86j7p6",
    createdAt: "2022-01-13T14:52:17.767Z",
    updatedAt: "2022-01-13T14:52:17.768Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "DBS Bank",
    shortName: "DBS",
    registration: "196800306E",
    private: false,
    addressId: "ckyd3by07145314vxlxa14vdqz",
    address: {
      id: "ckyd3by07145314vxlxa14vdqz",
      createdAt: "2022-01-13T14:52:17.767Z",
      updatedAt: "2022-01-13T14:52:17.767Z",
      deletedAt: null,
      line1: "12 Marina Blvd",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtb24904vxlifxx7yew",
      city: {
        id: "ckyc7bqtb24904vxlifxx7yew",
        createdAt: "2022-01-12T23:56:20.735Z",
        updatedAt: "2022-01-12T23:56:20.735Z",
        name: "SINGAPOUR",
        countryId: "ckvrc3yzz1006sxyesnsxmxyc",
        postalCode: "018982",
        country: {
          name: "Singapore",
        },
      },
    },
  },
  {
    id: "ckyd3bxzh144504vxlgmob1cfi",
    createdAt: "2022-01-13T14:52:17.741Z",
    updatedAt: "2025-01-07T08:59:35.088Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "DNB Bank ASA",
    shortName: "DNB Bank",
    registration: "984851006",
    private: false,
    addressId: "ckyd3bxzh144514vxlmq3lucqc",
    address: {
      id: "ckyd3bxzh144514vxlmq3lucqc",
      createdAt: "2022-01-13T14:52:17.741Z",
      updatedAt: "2022-01-13T14:52:17.741Z",
      deletedAt: null,
      line1: "Dronning Eufemias gate 30",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsv23824vxl0nzmyd3q",
      city: {
        id: "ckyc7bqsv23824vxl0nzmyd3q",
        createdAt: "2022-01-12T23:56:20.719Z",
        updatedAt: "2022-01-12T23:56:20.719Z",
        name: "OSLO",
        countryId: "ckvrc3yr50791sxyenmy243pg",
        postalCode: "0191",
        country: {
          name: "Norway",
        },
      },
    },
  },
  {
    id: "ckyd3bxzj144644vxli66v5yw0",
    createdAt: "2022-01-13T14:52:17.743Z",
    updatedAt: "2022-01-13T14:52:17.744Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "DNB Bank ASA, New York Branch",
    shortName: "DNB Bank NYC",
    registration: "98-0408276",
    private: false,
    addressId: "ckyd3bxzj144654vxl3og9j5jl",
    address: {
      id: "ckyd3bxzj144654vxl3og9j5jl",
      createdAt: "2022-01-13T14:52:17.743Z",
      updatedAt: "2022-01-13T14:52:17.743Z",
      deletedAt: null,
      line1: "30 Hudson Yards",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsy24014vxlj47j7ye4",
      city: {
        id: "ckyc7bqsy24014vxlj47j7ye4",
        createdAt: "2022-01-12T23:56:20.722Z",
        updatedAt: "2022-01-12T23:56:20.722Z",
        name: "NEW YORK",
        countryId: "ckvrc3z6o1162sxyekt2k45hw",
        postalCode: "NEW YORK",
        country: {
          name: "United States",
        },
      },
    },
  },
  {
    id: "ckyd3bxzi144564vxloopr5d9m",
    createdAt: "2022-01-13T14:52:17.742Z",
    updatedAt: "2022-01-13T14:52:17.743Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "DNB Bank ASA, UK Branch",
    shortName: "DNB Bank UK",
    registration: "FC025119",
    private: false,
    addressId: "ckyd3bxzi144574vxlrdor0wlr",
    address: {
      id: "ckyd3bxzi144574vxlrdor0wlr",
      createdAt: "2022-01-13T14:52:17.742Z",
      updatedAt: "2022-01-13T14:52:17.742Z",
      deletedAt: null,
      line1: "8th Floor",
      line2: " The Walbrook Building 25 Walbrook",
      line3: null,
      cityId: "ckyc7bqsx23914vxlgghkltqw",
      city: {
        id: "ckyc7bqsx23914vxlgghkltqw",
        createdAt: "2022-01-12T23:56:20.721Z",
        updatedAt: "2022-01-12T23:56:20.721Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC4N 8AF",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3by0f145664vxlmnno9yrj",
    createdAt: "2022-01-13T14:52:17.775Z",
    updatedAt: "2022-01-13T14:52:17.776Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Danish Ship Finance",
    shortName: "DSF",
    registration: "27492649",
    private: false,
    addressId: "ckyd3by0f145674vxlppadcww8",
    address: {
      id: "ckyd3by0f145674vxlppadcww8",
      createdAt: "2022-01-13T14:52:17.775Z",
      updatedAt: "2022-01-13T14:52:17.775Z",
      deletedAt: null,
      line1: "Sankt Annæ Pl. 3",
      line2: null,
      line3: null,
      cityId: "ckyc7bqti25404vxlpzs2bv9b",
      city: {
        id: "ckyc7bqti25404vxlpzs2bv9b",
        createdAt: "2022-01-12T23:56:20.742Z",
        updatedAt: "2022-01-12T23:56:20.742Z",
        name: "COPENHAGUE",
        countryId: "ckvrc3wun0118sxyeimqarvvi",
        postalCode: "1250",
        country: {
          name: "Denmark",
        },
      },
    },
  },
  {
    id: "ckylc9pa097604uxdiuzxf9pv",
    createdAt: "2022-01-19T09:24:39.096Z",
    updatedAt: "2022-01-19T09:24:39.097Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Entrust Global ICAV - Blue Ocean Fund",
    shortName: "Entrust Blue Ocean Fund",
    registration: "LEI549300MFBL5PICTTOM90",
    private: false,
    addressId: "ckylc9pa097614uxdwxzi5h0d",
    address: {
      id: "ckylc9pa097614uxdwxzi5h0d",
      createdAt: "2022-01-19T09:24:39.096Z",
      updatedAt: "2022-01-19T09:24:39.096Z",
      deletedAt: null,
      line1: "32 Molesworth Street",
      line2: " Dublin 2",
      line3: null,
      cityId: "ckylc9pa097624uxdnw7ht3bg",
      city: {
        id: "ckylc9pa097624uxdnw7ht3bg",
        createdAt: "2022-01-19T09:24:39.096Z",
        updatedAt: "2022-01-19T09:24:39.096Z",
        name: "Dublin",
        countryId: "ckvrc3xsy0296sxyet3262nh0",
        postalCode: "IE-D IE D02 Y512",
        country: {
          name: "Ireland",
        },
      },
    },
  },
  {
    id: "clnizkkdx693875bzc0ektckcz",
    createdAt: "2023-10-09T14:26:17.013Z",
    updatedAt: "2023-10-09T14:26:17.015Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "E.SUN COMMERCIAL BANK, LTD. TOKYO BRANCH",
    shortName: "E.SUN COMMERCIAL BANK",
    registration: "4700150084974",
    private: false,
    addressId: "clnizkkdx693885bzcr6syxppy",
    address: {
      id: "clnizkkdx693885bzcr6syxppy",
      createdAt: "2023-10-09T14:26:17.013Z",
      updatedAt: "2023-10-09T14:26:17.014Z",
      deletedAt: null,
      line1: "34F",
      line2: " 2-4-1 Marunouchi",
      line3: null,
      cityId: "clnizkkdy693895bzcxb9lle09",
      city: {
        id: "clnizkkdy693895bzcxb9lle09",
        createdAt: "2023-10-09T14:26:17.014Z",
        updatedAt: "2023-10-09T14:26:17.014Z",
        name: "Tokyo",
        countryId: "ckvrc3yb50412sxye44lx8mqd",
        postalCode: "100-6334",
        country: {
          name: "Japan",
        },
      },
    },
  },
  {
    id: "ckyd3by0g145734vxlddnilit8",
    createdAt: "2022-01-13T14:52:17.776Z",
    updatedAt: "2022-01-13T14:52:17.777Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Hamburg Commercial Bank",
    shortName: "HCB",
    registration: "87366",
    private: false,
    addressId: "ckyd3by0g145744vxl5cxg0t7a",
    address: {
      id: "ckyd3by0g145744vxl5cxg0t7a",
      createdAt: "2022-01-13T14:52:17.776Z",
      updatedAt: "2022-01-13T14:52:17.776Z",
      deletedAt: null,
      line1: "Gerhart-Hauptmann-Platz 50",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtj25504vxlsanj5xyk",
      city: {
        id: "ckyc7bqtj25504vxlsanj5xyk",
        createdAt: "2022-01-12T23:56:20.743Z",
        updatedAt: "2022-01-12T23:56:20.743Z",
        name: "HAMBURG",
        countryId: "ckvrc3wuo0131sxye2b1bk7z6",
        postalCode: "20095",
        country: {
          name: "Germany",
        },
      },
    },
  },
  {
    id: "ckyd3by0h145764vxlea8a3pmn",
    createdAt: "2022-01-13T14:52:17.777Z",
    updatedAt: "2022-01-13T14:52:17.778Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Helaba (Landesbank Hessen-Thüringen Girozentrale)",
    shortName: "Helaba",
    registration: "29821",
    private: false,
    addressId: "ckyd3by0h145774vxls8z7ngxi",
    address: {
      id: "ckyd3by0h145774vxls8z7ngxi",
      createdAt: "2022-01-13T14:52:17.777Z",
      updatedAt: "2022-01-13T14:52:17.777Z",
      deletedAt: null,
      line1: "Neue Mainzer Strasse 52-58",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtk25564vxlq35nh0qs",
      city: {
        id: "ckyc7bqtk25564vxlq35nh0qs",
        createdAt: "2022-01-12T23:56:20.744Z",
        updatedAt: "2022-01-12T23:56:20.744Z",
        name: "FRANKFURT AM MAIN",
        countryId: "ckvrc3wuo0131sxye2b1bk7z6",
        postalCode: "60311",
        country: {
          name: "Germany",
        },
      },
    },
  },
  {
    id: "ckyd3by01145054vxl04xdourk",
    createdAt: "2022-01-13T14:52:17.761Z",
    updatedAt: "2022-01-13T14:52:17.762Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "HSBC BANK PLC",
    shortName: "HSBC London",
    registration: "00014259",
    private: false,
    addressId: "ckyd3by01145064vxlidyxy3ns",
    address: {
      id: "ckyd3by01145064vxlidyxy3ns",
      createdAt: "2022-01-13T14:52:17.761Z",
      updatedAt: "2022-01-13T14:52:17.761Z",
      deletedAt: null,
      line1: "8 Canada Square",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt724624vxlcqaqq2wd",
      city: {
        id: "ckyc7bqt724624vxlcqaqq2wd",
        createdAt: "2022-01-12T23:56:20.731Z",
        updatedAt: "2022-01-12T23:56:20.731Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "E14 5HQ",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3by0d145594vxlaup1j60r",
    createdAt: "2022-01-13T14:52:17.773Z",
    updatedAt: "2022-01-13T14:52:17.774Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "ICBC (LONDON) PLC",
    shortName: "ICBC UK",
    registration: "04552753",
    private: false,
    addressId: "ckyd3by0d145604vxlc1nnqkqe",
    address: {
      id: "ckyd3by0d145604vxlc1nnqkqe",
      createdAt: "2022-01-13T14:52:17.773Z",
      updatedAt: "2022-01-13T14:52:17.773Z",
      deletedAt: null,
      line1: "81 King William Street",
      line2: null,
      line3: null,
      cityId: "ckyc7bqth25304vxlf4l9ebqw",
      city: {
        id: "ckyc7bqth25304vxlf4l9ebqw",
        createdAt: "2022-01-12T23:56:20.741Z",
        updatedAt: "2022-01-12T23:56:20.741Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC4N 7BG",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3bxys144384vxl8couimkh",
    createdAt: "2022-01-13T14:52:17.716Z",
    updatedAt: "2022-01-13T14:52:17.733Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "ING Bank N.V",
    shortName: "ING Bank NV",
    registration: "33031431",
    private: false,
    addressId: "ckyd3bxys144394vxlcwf82mis",
    address: {
      id: "ckyd3bxys144394vxlcwf82mis",
      createdAt: "2022-01-13T14:52:17.716Z",
      updatedAt: "2022-01-13T14:52:17.716Z",
      deletedAt: null,
      line1: "Bijlmerdreef 106 - 1102 CT",
      line2: null,
      line3: null,
      cityId: "ckyc7bqst23684vxln7i9bvtq",
      city: {
        id: "ckyc7bqst23684vxln7i9bvtq",
        createdAt: "2022-01-12T23:56:20.717Z",
        updatedAt: "2022-01-12T23:56:20.717Z",
        name: "AMSTERDAM ZUIDOOST",
        countryId: "ckvrc3yr00789sxye4a1bermd",
        postalCode: "",
        country: {
          name: "Netherlands",
        },
      },
    },
  },
  {
    id: "clyylwo4u0000kx3dzlgk8due",
    createdAt: "2024-07-23T16:04:51.630Z",
    updatedAt: "2024-07-23T16:05:36.528Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Intesa Sanpaolo S.p.A, Paris Branch",
    shortName: "INTESA SANPAOLO SPA",
    registration: "478 952 955 00068",
    private: false,
    addressId: "clyylvvoc0000jp3dta7pov90",
    address: {
      id: "clyylvvoc0000jp3dta7pov90",
      createdAt: "2024-07-23T16:04:14.747Z",
      updatedAt: "2024-07-23T16:04:14.748Z",
      deletedAt: null,
      line1: "18 rue du 4 Septembre",
      line2: null,
      line3: null,
      cityId: "clyylvd0l0000il3d2w3cysto",
      city: {
        id: "clyylvd0l0000il3d2w3cysto",
        createdAt: "2024-07-23T16:03:50.564Z",
        updatedAt: "2024-07-23T16:03:50.565Z",
        name: "PARIS",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75002",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by0p146144vxlxlrhlj8z",
    createdAt: "2022-01-13T14:52:17.785Z",
    updatedAt: "2022-01-13T14:52:17.786Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "The Iyo Bank",
    shortName: "Iyo Bank",
    registration: "",
    private: false,
    addressId: "ckyd3by0p146154vxlkqpkijm2",
    address: {
      id: "ckyd3by0p146154vxlkqpkijm2",
      createdAt: "2022-01-13T14:52:17.785Z",
      updatedAt: "2022-01-13T14:52:17.785Z",
      deletedAt: null,
      line1: "1",
      line2: " Minami-Horibata-Cho",
      line3: " Matsuyama-Shi",
      cityId: "ckyc7bqts26104vxlaudyhyg6",
      city: {
        id: "ckyc7bqts26104vxlaudyhyg6",
        createdAt: "2022-01-12T23:56:20.752Z",
        updatedAt: "2022-01-12T23:56:20.752Z",
        name: "Ehima",
        countryId: "ckvrc3yb50412sxye44lx8mqd",
        postalCode: "790-8514",
        country: {
          name: "Japan",
        },
      },
    },
  },
  {
    id: "ckyd3by0j145844vxlvbw8v7ox",
    createdAt: "2022-01-13T14:52:17.779Z",
    updatedAt: "2022-01-13T14:52:17.779Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Japan Bank for International Cooperation",
    shortName: "JBIC",
    registration: "10001145977",
    private: false,
    addressId: "ckyd3by0j145854vxldvheg449",
    address: {
      id: "ckyd3by0j145854vxldvheg449",
      createdAt: "2022-01-13T14:52:17.779Z",
      updatedAt: "2022-01-13T14:52:17.779Z",
      deletedAt: null,
      line1: "4-1 Ohtemachi 1-chome",
      line2: " Chiyoda-ku",
      line3: null,
      cityId: "ckyc7bqtl25654vxlegnpg7ja",
      city: {
        id: "ckyc7bqtl25654vxlegnpg7ja",
        createdAt: "2022-01-12T23:56:20.745Z",
        updatedAt: "2022-01-12T23:56:20.745Z",
        name: "TOKYO",
        countryId: "ckvrc3yb50412sxye44lx8mqd",
        postalCode: "100-8144",
        country: {
          name: "Japan",
        },
      },
    },
  },
  {
    id: "ckyd3by0q146224vxlyqf6f6xl",
    createdAt: "2022-01-13T14:52:17.786Z",
    updatedAt: "2022-01-13T14:52:17.787Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "JPMorgan Chase & Co",
    shortName: "JP Morgan",
    registration: "13-2624428",
    private: false,
    addressId: "ckyd3by0q146234vxltr8rsc6r",
    address: {
      id: "ckyd3by0q146234vxltr8rsc6r",
      createdAt: "2022-01-13T14:52:17.786Z",
      updatedAt: "2022-01-13T14:52:17.786Z",
      deletedAt: null,
      line1: "270 Park Avenue",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtt26194vxljdrlw7f3",
      city: {
        id: "ckyc7bqtt26194vxljdrlw7f3",
        createdAt: "2022-01-12T23:56:20.753Z",
        updatedAt: "2022-01-12T23:56:20.753Z",
        name: "NEW YORK",
        countryId: "ckvrc3z6o1162sxyekt2k45hw",
        postalCode: "NY 10017",
        country: {
          name: "United States",
        },
      },
    },
  },
  {
    id: "ckyd3by09145414vxlrzznx01k",
    createdAt: "2022-01-13T14:52:17.769Z",
    updatedAt: "2022-01-13T14:52:17.770Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Kredit Anstalt für Wiederaufbau",
    shortName: "KFW",
    registration: "79744",
    private: false,
    addressId: "ckyd3by09145424vxle18ajfym",
    address: {
      id: "ckyd3by09145424vxle18ajfym",
      createdAt: "2022-01-13T14:52:17.769Z",
      updatedAt: "2022-01-13T14:52:17.769Z",
      deletedAt: null,
      line1: "Palmengartenstrasse 5-9",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtd25064vxl3wbinewr",
      city: {
        id: "ckyc7bqtd25064vxl3wbinewr",
        createdAt: "2022-01-12T23:56:20.737Z",
        updatedAt: "2022-01-12T23:56:20.737Z",
        name: "FRANKFURT AM MAIN",
        countryId: "ckvrc3wuo0131sxye2b1bk7z6",
        postalCode: "60325",
        country: {
          name: "Germany",
        },
      },
    },
  },
  {
    id: "ckyd3bxzs144824vxlwodozotz",
    createdAt: "2022-01-13T14:52:17.752Z",
    updatedAt: "2022-01-13T14:52:17.753Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Mizuho Bank Ltd",
    shortName: "Mizuho UK",
    registration: "FC004234",
    private: false,
    addressId: "ckyd3bxzs144834vxlremarnqa",
    address: {
      id: "ckyd3bxzs144834vxlremarnqa",
      createdAt: "2022-01-13T14:52:17.752Z",
      updatedAt: "2022-01-13T14:52:17.752Z",
      deletedAt: null,
      line1: "Mizuho Bank 30 Old Bailey",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt224244vxloybjnqg9",
      city: {
        id: "ckyc7bqt224244vxloybjnqg9",
        createdAt: "2022-01-12T23:56:20.726Z",
        updatedAt: "2022-01-12T23:56:20.726Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC4M 7AU",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3by08145344vxl5lxgowru",
    createdAt: "2022-01-13T14:52:17.768Z",
    updatedAt: "2022-01-13T14:52:17.769Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Maritime & Merchant Bank ASA",
    shortName: "M&M",
    registration: "912277070",
    private: false,
    addressId: "ckyd3by08145354vxlr3br2c0j",
    address: {
      id: "ckyd3by08145354vxlr3br2c0j",
      createdAt: "2022-01-13T14:52:17.768Z",
      updatedAt: "2022-01-13T14:52:17.768Z",
      deletedAt: null,
      line1: "Haakon VIIs gate 1",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtc24954vxltrar75hi",
      city: {
        id: "ckyc7bqtc24954vxltrar75hi",
        createdAt: "2022-01-12T23:56:20.736Z",
        updatedAt: "2022-01-12T23:56:20.736Z",
        name: "OSLO",
        countryId: "ckvrc3yr50791sxyenmy243pg",
        postalCode: "0161",
        country: {
          name: "Norway",
        },
      },
    },
  },
  {
    id: "clkjyif9x1593159yn33lybh8e",
    createdAt: "2023-07-26T16:45:16.629Z",
    updatedAt: "2023-07-26T16:45:16.631Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "MUFG BANK LTD",
    shortName: "MUFG London",
    registration: "FC004549 / BR002013",
    private: false,
    addressId: "clkjyif9x1593259ynz9yelxbq",
    address: {
      id: "clkjyif9x1593259ynz9yelxbq",
      createdAt: "2023-07-26T16:45:16.629Z",
      updatedAt: "2023-07-26T16:45:16.629Z",
      deletedAt: null,
      line1: "Ropenmaker Place",
      line2: " 25 Ropemaker Street",
      line3: null,
      cityId: "clkjyif9x1593359ynl8qnzcyi",
      city: {
        id: "clkjyif9x1593359ynl8qnzcyi",
        createdAt: "2023-07-26T16:45:16.629Z",
        updatedAt: "2023-07-26T16:45:16.629Z",
        name: "London",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC2Y 9AN",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3by0l145984vxltz28sues",
    createdAt: "2022-01-13T14:52:17.781Z",
    updatedAt: "2022-01-13T14:52:17.782Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "National Bank of Greece",
    shortName: "National Bank of Greece",
    registration: "EYKCVFAW8ZLO",
    private: false,
    addressId: "ckyd3by0l145994vxlw14p8ov2",
    address: {
      id: "ckyd3by0l145994vxlw14p8ov2",
      createdAt: "2022-01-13T14:52:17.782Z",
      updatedAt: "2022-01-13T14:52:17.782Z",
      deletedAt: null,
      line1: "Eolou 86",
      line2: null,
      line3: null,
      cityId: "ckyc7bqto25844vxl30ka7z1n",
      city: {
        id: "ckyc7bqto25844vxl30ka7z1n",
        createdAt: "2022-01-12T23:56:20.748Z",
        updatedAt: "2022-01-12T23:56:20.748Z",
        name: "ATHENS",
        countryId: "ckvrc3wur0183sxyes9zo3x0y",
        postalCode: "102 32",
        country: {
          name: "Greece",
        },
      },
    },
  },
  {
    id: "cm5nmufnx11095668zeyz00fvi6",
    createdAt: "2025-01-08T08:23:35.373Z",
    updatedAt: "2025-01-08T08:23:35.374Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Natixis Corporate & Investment Banking",
    shortName: "Natixis",
    registration: "542 044 524",
    private: false,
    addressId: "cm5nmufnx11095768zex8afqgom",
    address: {
      id: "cm5nmufnx11095768zex8afqgom",
      createdAt: "2025-01-08T08:23:35.373Z",
      updatedAt: "2025-01-08T08:23:35.373Z",
      deletedAt: null,
      line1: "7",
      line2: " promenade Germaine Sablon",
      line3: null,
      cityId: "cm5nmufnx11095868zepowp0mp7",
      city: {
        id: "cm5nmufnx11095868zepowp0mp7",
        createdAt: "2025-01-08T08:23:35.373Z",
        updatedAt: "2025-01-08T08:23:35.373Z",
        name: "Paris",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75013",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3bxzk144684vxlwotw7k8r",
    createdAt: "2022-01-13T14:52:17.744Z",
    updatedAt: "2022-01-13T14:52:17.750Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Nordea Bank ASA",
    shortName: "Nordea NO",
    registration: "920058817",
    private: false,
    addressId: "ckyd3bxzp144694vxlcly6j0t9",
    address: {
      id: "ckyd3bxzp144694vxlcly6j0t9",
      createdAt: "2022-01-13T14:52:17.744Z",
      updatedAt: "2022-01-13T14:52:17.749Z",
      deletedAt: null,
      line1: "Essendrops gate 7",
      line2: " Postboks 1166 Sentrum",
      line3: null,
      cityId: "ckyc7bqsz24054vxljamtrshe",
      city: {
        id: "ckyc7bqsz24054vxljamtrshe",
        createdAt: "2022-01-12T23:56:20.723Z",
        updatedAt: "2022-01-12T23:56:20.723Z",
        name: "OSLO",
        countryId: "ckvrc3yr50791sxyenmy243pg",
        postalCode: "0107",
        country: {
          name: "Norway",
        },
      },
    },
  },
  {
    id: "ckyd3bxzq144754vxl5gjru1a9",
    createdAt: "2022-01-13T14:52:17.750Z",
    updatedAt: "2022-01-13T14:52:17.751Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Nordea Bank Abp",
    shortName: "Nordea SWE",
    registration: "516411-1683",
    private: false,
    addressId: "ckyd3bxzq144764vxlngc971p9",
    address: {
      id: "ckyd3bxzq144764vxlngc971p9",
      createdAt: "2022-01-13T14:52:17.750Z",
      updatedAt: "2022-01-13T14:52:17.750Z",
      deletedAt: null,
      line1: "Smålandsgatan 17",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt024144vxlo025whtz",
      city: {
        id: "ckyc7bqt024144vxlo025whtz",
        createdAt: "2022-01-12T23:56:20.724Z",
        updatedAt: "2022-01-12T23:56:20.724Z",
        name: "STOCKHOLM",
        countryId: "ckvrc3y6m0361sxye48qr2cw9",
        postalCode: "105 71",
        country: {
          name: "Sweden",
        },
      },
    },
  },
  {
    id: "cm5nmufnf11080868ze82pt981o",
    createdAt: "2025-01-08T08:23:35.355Z",
    updatedAt: "2025-01-08T08:23:35.356Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Banque Palatine",
    shortName: "Palatine",
    registration: "542 104 245",
    private: false,
    addressId: "cm5nmufnf11080968zedqdifwfe",
    address: {
      id: "cm5nmufnf11080968zedqdifwfe",
      createdAt: "2025-01-08T08:23:35.355Z",
      updatedAt: "2025-01-08T08:23:35.355Z",
      deletedAt: null,
      line1: "86 Rue de Courcelles",
      line2: null,
      line3: null,
      cityId: "ckylc9p9x97414uxddyfu2vzf",
      city: {
        id: "ckylc9p9x97414uxddyfu2vzf",
        createdAt: "2022-01-19T09:24:39.093Z",
        updatedAt: "2022-01-19T09:24:39.093Z",
        name: "Paris",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75008",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by0k145914vxld1pb3li5",
    createdAt: "2022-01-13T14:52:17.780Z",
    updatedAt: "2022-01-13T14:52:17.781Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Piraeus Bank",
    shortName: "Piraeus Bank",
    registration: "1KW32H8THQ6F",
    private: false,
    addressId: "ckyd3by0k145924vxlunrrp7sz",
    address: {
      id: "ckyd3by0k145924vxlunrrp7sz",
      createdAt: "2022-01-13T14:52:17.780Z",
      updatedAt: "2022-01-13T14:52:17.780Z",
      deletedAt: null,
      line1: "Piraeus Bank 4 Amerikis St",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtn25754vxlfy6aetrx",
      city: {
        id: "ckyc7bqtn25754vxlfy6aetrx",
        createdAt: "2022-01-12T23:56:20.747Z",
        updatedAt: "2022-01-12T23:56:20.747Z",
        name: "ATHENS",
        countryId: "ckvrc3wur0183sxyes9zo3x0y",
        postalCode: "105 64",
        country: {
          name: "Greece",
        },
      },
    },
  },
  {
    id: "clyym587k0000s43djxxvf0ri",
    createdAt: "2024-07-23T16:11:30.897Z",
    updatedAt: "2024-07-23T16:09:47.853Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "GROUP",
    name: "Qatar national Bank, Paris Branch",
    shortName: "QATAR NATIONAL BANK",
    registration: "314 258 120 00042",
    private: false,
    addressId: "clyym2hwg0000qm3dl73fx4iy",
    address: {
      id: "clyym2hwg0000qm3dl73fx4iy",
      createdAt: "2024-07-23T16:09:23.488Z",
      updatedAt: "2024-07-23T16:09:23.487Z",
      deletedAt: null,
      line1: "65 avenue d'Iéna",
      line2: null,
      line3: null,
      cityId: "clyym0tvu0000pl3dpogn4hyn",
      city: {
        id: "clyym0tvu0000pl3dpogn4hyn",
        createdAt: "2024-07-23T16:08:05.706Z",
        updatedAt: "2024-07-23T16:08:05.705Z",
        name: "PARIS",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75116",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3by0i145804vxlyxbvzmps",
    createdAt: "2022-01-13T14:52:17.778Z",
    updatedAt: "2022-01-13T14:52:17.779Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Banco Santander",
    shortName: "Santander",
    registration: "A-39000013",
    private: false,
    addressId: "ckyd3by0i145814vxlrn12rpeh",
    address: {
      id: "ckyd3by0i145814vxlrn12rpeh",
      createdAt: "2022-01-13T14:52:17.778Z",
      updatedAt: "2022-01-13T14:52:17.778Z",
      deletedAt: null,
      line1: "Av. de Cantabria s/n",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtl25604vxlq1o2inbv",
      city: {
        id: "ckyc7bqtl25604vxlq1o2inbv",
        createdAt: "2022-01-12T23:56:20.745Z",
        updatedAt: "2022-01-12T23:56:20.745Z",
        name: "MADRID",
        countryId: "ckvrc3wuo0137sxyebh6b89a6",
        postalCode: "28660 Boadilla del Monte",
        country: {
          name: "Spain",
        },
      },
    },
  },
  {
    id: "ckyd3by0o146114vxly2yciloh",
    createdAt: "2022-01-13T14:52:17.784Z",
    updatedAt: "2022-01-13T14:52:17.785Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Skandinaviska Enskida Banken",
    shortName: "SEB",
    registration: "502032-9081",
    private: false,
    addressId: "ckyd3by0o146124vxlvr9btidp",
    address: {
      id: "ckyd3by0o146124vxlvr9btidp",
      createdAt: "2022-01-13T14:52:17.784Z",
      updatedAt: "2022-01-13T14:52:17.784Z",
      deletedAt: null,
      line1: "Kungstradgardsgatan 8",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtr26064vxlqw6r11s2",
      city: {
        id: "ckyc7bqtr26064vxlqw6r11s2",
        createdAt: "2022-01-12T23:56:20.751Z",
        updatedAt: "2022-01-12T23:56:20.751Z",
        name: "STOCKHOLM",
        countryId: "ckvrc3y6m0361sxye48qr2cw9",
        postalCode: "106 40",
        country: {
          name: "Sweden",
        },
      },
    },
  },
  {
    id: "ckyd3bxym144094vxlnf2a8buj",
    createdAt: "2022-01-13T14:52:17.710Z",
    updatedAt: "2025-01-07T08:59:17.977Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Société Générale",
    shortName: "SG",
    registration: "552120222",
    private: false,
    addressId: "ckyd3bxym144104vxlzuiqkn5k",
    address: {
      id: "ckyd3bxym144104vxlzuiqkn5k",
      createdAt: "2022-01-13T14:52:17.710Z",
      updatedAt: "2022-01-13T14:52:17.710Z",
      deletedAt: null,
      line1: "29 boulevard Haussmann",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsh22814vxlogldtfkp",
      city: {
        id: "ckyc7bqsh22814vxlogldtfkp",
        createdAt: "2022-01-12T23:56:20.705Z",
        updatedAt: "2022-01-12T23:56:20.705Z",
        name: "PARIS",
        countryId: "ckvrc3wup0158sxye98agsev8",
        postalCode: "75009",
        country: {
          name: "France",
        },
      },
    },
  },
  {
    id: "ckyd3bxyo144164vxlzs9jqyk9",
    createdAt: "2022-01-13T14:52:17.712Z",
    updatedAt: "2022-01-13T14:52:17.713Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Société Générale",
    shortName: "SG London Branch",
    registration: "FC000268",
    private: false,
    addressId: "ckyd3bxyo144174vxlmnnfk8t6",
    address: {
      id: "ckyd3bxyo144174vxlmnnfk8t6",
      createdAt: "2022-01-13T14:52:17.712Z",
      updatedAt: "2022-01-13T14:52:17.712Z",
      deletedAt: null,
      line1: "One Bank Street",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsp23394vxlx4niqlag",
      city: {
        id: "ckyc7bqsp23394vxlx4niqlag",
        createdAt: "2022-01-12T23:56:20.713Z",
        updatedAt: "2022-01-12T23:56:20.713Z",
        name: "LONDON E14 4SG",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "Canary Wharf",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "clnizkkdz693925bzc6ozdmhy6",
    createdAt: "2023-10-09T14:26:17.015Z",
    updatedAt: "2023-10-09T14:26:17.016Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "SHINSEI BANK, LIMITED",
    shortName: "SHINSEI BANK",
    registration: "0100-01-016855",
    private: false,
    addressId: "clnizkkdz693935bzcxeo6xqlh",
    address: {
      id: "clnizkkdz693935bzcxeo6xqlh",
      createdAt: "2023-10-09T14:26:17.015Z",
      updatedAt: "2023-10-09T14:26:17.015Z",
      deletedAt: null,
      line1: "4-3",
      line2: " Nihonbashi-muromachi 2-chrome",
      line3: null,
      cityId: "clnizkkdz693945bzceaxewzvj",
      city: {
        id: "clnizkkdz693945bzceaxewzvj",
        createdAt: "2023-10-09T14:26:17.015Z",
        updatedAt: "2023-10-09T14:26:17.015Z",
        name: "Tokyo",
        countryId: "ckvrc3yb50412sxye44lx8mqd",
        postalCode: "Chuo-ku",
        country: {
          name: "Japan",
        },
      },
    },
  },
  {
    id: "ckyd3bxyq144304vxlutwgjpd1",
    createdAt: "2022-01-13T14:52:17.714Z",
    updatedAt: "2022-01-13T14:52:17.715Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "SMBC Bank International plc",
    shortName: "SMBC BI UK",
    registration: "4684034",
    private: false,
    addressId: "ckyd3bxyq144314vxlkszdxtki",
    address: {
      id: "ckyd3bxyq144314vxlkszdxtki",
      createdAt: "2022-01-13T14:52:17.714Z",
      updatedAt: "2022-01-13T14:52:17.714Z",
      deletedAt: null,
      line1: "99 Queen Victoria Street",
      line2: null,
      line3: null,
      cityId: "ckyc7bqss23594vxlighkb8go",
      city: {
        id: "ckyc7bqss23594vxlighkb8go",
        createdAt: "2022-01-12T23:56:20.716Z",
        updatedAt: "2022-01-12T23:56:20.716Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC4V 4EH",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3bxzx144854vxlp1xbh11b",
    createdAt: "2022-01-13T14:52:17.757Z",
    updatedAt: "2022-01-13T14:52:17.758Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Sumitomo Mitsui Trust Bank, Limited London Branch",
    shortName: "SMTB UK",
    registration: "FC008329",
    private: false,
    addressId: "ckyd3bxzx144864vxl2trx50f4",
    address: {
      id: "ckyd3bxzx144864vxl2trx50f4",
      createdAt: "2022-01-13T14:52:17.757Z",
      updatedAt: "2022-01-13T14:52:17.757Z",
      deletedAt: null,
      line1: "3rd Floor",
      line2: " 155 Bishopsgate",
      line3: null,
      cityId: "ckyc7bqt224294vxl5l9fof9l",
      city: {
        id: "ckyc7bqt224294vxl5l9fof9l",
        createdAt: "2022-01-12T23:56:20.726Z",
        updatedAt: "2022-01-12T23:56:20.726Z",
        name: "LONDON",
        countryId: "ckvrc3wuq0161sxyey3rtybmr",
        postalCode: "EC2M 3XU",
        country: {
          name: "United Kingdom",
        },
      },
    },
  },
  {
    id: "ckyd3by00145014vxljk70he0c",
    createdAt: "2022-01-13T14:52:17.761Z",
    updatedAt: "2022-01-13T14:52:17.762Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Standard Chartered Bank",
    shortName: "Standard Chartered NYC",
    registration: "94-1595409",
    private: false,
    addressId: "ckyd3by01145024vxlhadl2cai",
    address: {
      id: "ckyd3by01145024vxlhadl2cai",
      createdAt: "2022-01-13T14:52:17.761Z",
      updatedAt: "2022-01-13T14:52:17.761Z",
      deletedAt: null,
      line1: "1095 Avenue of the Americas",
      line2: null,
      line3: null,
      cityId: "ckyc7bqt624574vxlxsldcorl",
      city: {
        id: "ckyc7bqt624574vxlxsldcorl",
        createdAt: "2022-01-12T23:56:20.730Z",
        updatedAt: "2022-01-12T23:56:20.730Z",
        name: "NEW YORK",
        countryId: "ckvrc3z6o1162sxyekt2k45hw",
        postalCode: "NY 10036",
        country: {
          name: "United States",
        },
      },
    },
  },
  {
    id: "ckyd3bxzh144464vxl3dwbfb8d",
    createdAt: "2022-01-13T14:52:17.741Z",
    updatedAt: "2022-01-13T14:52:17.742Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Swedbank AB",
    shortName: "Swedbank",
    registration: "502017-7753",
    private: false,
    addressId: "ckyd3bxzh144474vxlh4e3eqm0",
    address: {
      id: "ckyd3bxzh144474vxlh4e3eqm0",
      createdAt: "2022-01-13T14:52:17.741Z",
      updatedAt: "2022-01-13T14:52:17.741Z",
      deletedAt: null,
      line1: "Swedbank AB",
      line2: null,
      line3: null,
      cityId: "ckyc7bqsv23774vxlbracx76i",
      city: {
        id: "ckyc7bqsv23774vxlbracx76i",
        createdAt: "2022-01-12T23:56:20.719Z",
        updatedAt: "2022-01-12T23:56:20.719Z",
        name: "STOCKHOLM",
        countryId: "ckvrc3yr00789sxye4a1bermd",
        postalCode: "105 34",
        country: {
          name: "Netherlands",
        },
      },
    },
  },
  {
    id: "ckyd3by0m146044vxlky4vrh9b",
    createdAt: "2022-01-13T14:52:17.782Z",
    updatedAt: "2022-01-13T14:52:17.783Z",
    deletedAt: null,
    industry: "FINANCIAL",
    type: "LENDER",
    name: "Unicredit",
    shortName: "Unicredit",
    registration: "348170101",
    private: false,
    addressId: "ckyd3by0m146054vxllq95yj0c",
    address: {
      id: "ckyd3by0m146054vxllq95yj0c",
      createdAt: "2022-01-13T14:52:17.782Z",
      updatedAt: "2022-01-13T14:52:17.782Z",
      deletedAt: null,
      line1: "Piazza Gae Aulenti 3 - Tower A",
      line2: null,
      line3: null,
      cityId: "ckyc7bqtq25954vxl5p76f0wf",
      city: {
        id: "ckyc7bqtq25954vxl5p76f0wf",
        createdAt: "2022-01-12T23:56:20.750Z",
        updatedAt: "2022-01-12T23:56:20.750Z",
        name: "MILANO",
        countryId: "ckvrc3y520349sxyepmn2gweg",
        postalCode: "20154",
        country: {
          name: "Italy",
        },
      },
    },
  },
];
